import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import '../styles/LayoutEditor.css';
import { createPortal } from "react-dom";
import { Calendar, CalendarClock, Check, CheckSquare, ChevronDown, CloudCheck, CloudUpload, Dot, Download, Ellipsis, Eraser, FileIcon, FileSignature, Hash, Info, Link, Loader2, MessageSquare, Paperclip, Phone, Plus, PlusIcon, Printer, RefreshCw, Search, SendHorizonal, StickyNote, UserRoundPlus, X } from "lucide-react";
import { Calculate, CheckRounded } from "@mui/icons-material";
import { RiAiGenerate } from "react-icons/ri";
import { useNavigate } from 'react-router-dom';
import Modal from "./Modal";
import fuzzysort from 'fuzzysort';
import { getContactInitialsClassName } from '../utils/colorUtils';
import Alert from './Alert';
import { formatLastModified } from './NestedFeed';
import { fetchNavSearchResults } from '../utils/navSearch';
import {
    createCommunicationRequestId,
    isSmsConfirmationPending,
    SMS_CONFIRMATION_PENDING_MESSAGE
} from '../utils/communicationRequestId';
import { useGenerationReview } from './GenerationReview';
import { getGenerationTemplateType, supportsDocGenReview } from '../utils/generationTemplateTypes';
import {
    collectDroppedDocumentFiles,
    filterDocumentUploadFiles,
    getDocumentUploadFilePath,
    mergeDocumentUploadFiles
} from '../utils/documentUploadFiles';

const EMPTY_SEARCH_SELECT_OPTIONS = [];

export const dedupeSearchSelectOptions = (options = []) => {
    const byValue = new Map();
    (Array.isArray(options) ? options : []).forEach(option => {
        const value = String(option?.value ?? '');
        if (!value) return;
        byValue.set(value, { ...(byValue.get(value) || {}), ...option, value });
    });
    return Array.from(byValue.values());
};

export const formatDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return ["", "", "", ""];

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    try {
        let parsedDate = new Date(dateString);

        if (isNaN(parsedDate.getTime())) {
            parsedDate = new Date(dateString + "T00:00:00Z");
        }

        if (isNaN(parsedDate.getTime())) {
            return ["", "", "", ""];
        }

        const centralTime = new Date(parsedDate.toLocaleString("en-US", { timeZone: "America/Chicago" }));
        
        const iso = parsedDate.toISOString();
        // Use Central Time for display
        const longDate = `${months[centralTime.getMonth()]} ${centralTime.getDate()}, ${centralTime.getFullYear()}`;
        const shortDate = `${centralTime.getMonth() + 1}/${centralTime.getDate()}/${centralTime.getFullYear()}`;
        const shortFormatted = `${centralTime.getDate()} ${months[centralTime.getMonth()].slice(0, 3)} ${centralTime.getFullYear()}`;
        const hours = centralTime.getHours();
        const minutes = centralTime.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12 || 12;
        const minutePadded = String(minutes).padStart(2, '0');
        const timeFormatted = `${hour12}:${minutePadded} ${ampm}`;
        const dateTimeFormatted = `${centralTime.getMonth() + 1}/${centralTime.getDate()}/${centralTime.getFullYear()} ${timeFormatted}`;
        
        return [longDate, iso, timeFormatted, shortDate, shortFormatted, dateTimeFormatted];
    } catch (e) {
        console.error("Invalid date:", dateString, e);
        return ["", "", "", ""];
    }
};

export const formatValue = (val, type) => {
    if (type === 'currency') {
        const num = parseFloat(val);
        if (isNaN(num)) return '$0.00';
        return num.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        });
    } else if (type === 'percentage') {
        return `${parseFloat(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    }
    return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    // Take only the last 10 digits to remove country codes (1, +1, etc)
    const last10 = cleaned.slice(-10);
    const match = last10.match(/(\d{3})(\d{3})(\d{4})/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
};

const parseCalculationOptions = (options) => {
    if (!options) return null;
    if (typeof options === 'object') return options;

    try {
        return JSON.parse(options);
    } catch {
        return null;
    }
};

const isDateCalculationConfig = (config) => {
    if (!config || typeof config !== 'object') return false;

    const type = String(config.result_type || config.resultType || config.output_type || config.outputType || config.calculation_type || config.calculationType || config.type || '').toLowerCase();
    const operation = String(config.operation || config.operator || '').toLowerCase();

    return type === 'date'
        || operation === 'date'
        || operation === 'date_offset'
        || !!config.date
        || !!config.date_calculation
        || !!config.dateCalculation;
};

export const isDateCalculationField = (field) => {
    if (!field || Number(field.field_id) !== 17) return false;

    const config = parseCalculationOptions(field.options);
    if (isDateCalculationConfig(config)) return true;
    if (isDateCalculationConfig(config?.fallback)) return true;
    if (Array.isArray(config?.rules)) return config.rules.some(isDateCalculationConfig);

    return false;
};

const parseLocalDateValue = (value) => {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    if (typeof value === 'object') {
        const nestedValue = value.due
            ?? value.date
            ?? value.value
            ?? value.start_date
            ?? value.startDate
            ?? value.end_date
            ?? value.endDate;

        return nestedValue ? parseLocalDateValue(nestedValue) : null;
    }

    const raw = String(value).trim();

    if ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'))) {
        try {
            const parsed = JSON.parse(raw);
            const parsedValue = Array.isArray(parsed) ? parsed[0] : parsed;
            const parsedDate = parseLocalDateValue(parsedValue);
            if (parsedDate) return parsedDate;
        } catch {
        }
    }

    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
        const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const formatIsoLocalDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const addMonthsClamped = (date, months) => {
    const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(date.getDate(), lastDayOfTargetMonth));

    return target;
};

const formatCalculationDateForDisplay = (value, format = 'MM/DD/YYYY') => {
    const date = parseLocalDateValue(value);
    if (!date) return value || '';

    const normalizedFormat = String(format || 'MM/DD/YYYY').toLowerCase();
    if (normalizedFormat === 'iso' || normalizedFormat === 'yyyy-mm-dd') {
        return formatIsoLocalDate(date);
    }

    if (normalizedFormat === 'long') {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    if (normalizedFormat === 'short-text' || normalizedFormat === 'medium') {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

export const Text = ({ type, placeholder, value, onChange, disable, onBlur = () => {}, onKeyDown = () => {}, required = false }) => {
    return type === 'text' ? (
        <>
            <input
                type='text'
                disabled={disable}
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                className={required ? 'required-input' : ''}
            />
        </>
    ) : (type === 'link' ? (
        <div className='number-input'>
            <input
                type='text'
                disabled={disable}
                placeholder={placeholder}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                className={required ? 'required-input' : ''}
            />
            <span className='number-symbol subtext'><Link size={14} style={{ maxHeight: '14px', maxWidth: '14px' }} /></span>
        </div>
    ) : (
        <textarea
            placeholder={placeholder}
            disabled={disable}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            className={required ? 'required-input' : ''}
        />
    ));
};

export const NumberInput = ({ type, value, onChange, onBlur = () => {}, onKeyDown = () => {}, required = false, allowNegative = false }) => {
    return (
        <div className='number-input'>
            <input
                type='number'
                inputMode={allowNegative ? 'decimal' : 'numeric'}
                placeholder='0.00'
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                className={required ? 'required-input' : ''}
            />
            {type !== 'number' && <span className='number-symbol subtext'>{type === 'currency' ? "$" : '%'}</span>}
        </div>
    );
};

export const DateInput = ({
    value = '',
    onChange,
    onBlur = () => {},
    disable = false,
    checkbox = false,
    required = false
}) => {
    const [isChecked, setIsChecked] = useState(false);
    const [localValue, setLocalValue] = useState(value || '');
    const localValueRef = useRef(value || '');
    const inputRef = useRef(null);

    const today = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const handleCheckboxClick = () => {
        const newChecked = !isChecked;
        setIsChecked(newChecked);
        const newValue = newChecked ? today() : '';
        localValueRef.current = newValue;
        setLocalValue(newValue);
        onChange(newValue);
        onBlur();
    };

    const handleInputChange = (e) => {
        localValueRef.current = e.target.value;
        setLocalValue(e.target.value);
        setIsChecked(false);
    };

    const handleBlur = () => {
        onChange(localValueRef.current);
        onBlur(localValueRef.current);
    };

    const selectDateValue = (target = inputRef.current) => {
        if (!target) return;

        window.requestAnimationFrame(() => {
            try {
                target.select();
            } catch (error) {
                try {
                    target.setSelectionRange(0, String(target.value || '').length);
                } catch {
                }
            }
        });
    };

    const handleFocus = (e) => {
        selectDateValue(e.currentTarget);
    };

    const handleMouseUp = (e) => {
        e.preventDefault();
        selectDateValue(e.currentTarget);
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onChange(localValueRef.current);
            e.currentTarget.blur();
        }
    };

    useEffect(() => {
        setIsChecked(value === today());
        localValueRef.current = value || '';
        setLocalValue(value || '');
    }, [value]);

    return (
        <div className="date-input">
            <input
                ref={inputRef}
                type="date"
                placeholder="YYYY-MM-DD"
                inputMode="numeric"
                pattern="\d{4}-\d{2}-\d{2}"
                disabled={disable}
                value={localValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onClick={handleFocus}
                onMouseUp={handleMouseUp}
                onKeyDown={handleKeyDown}
                className={required ? 'required-input' : ''}
            />
            {checkbox && (
                <div className="form-box alt" onClick={handleCheckboxClick}>
                    <input type="checkbox" checked={isChecked} readOnly hidden />
                    {isChecked ? <X size={23} /> : <Check size={23} />}
                </div>
            )}
        </div>
    );
};

export const TimeInput = ({ value, onChange }) => {
    return <input type='time' value={value || ""} onChange={(e) => onChange(e.target.value)} />;
};

export const Dropdown = ({ placeholder = null, options = [], value = "", onChange = () => {}, marketing_list = false, onBlur = () => {}, onKeyDown = () => {}, lead_filter = null }) => {
    const [parsedOptions, setParsedOptions] = useState([]);

    useEffect(() => {
        if (marketing_list) {
            const fetchMarketingSources = async () => {
                try {
                    const response = await fetch(`https://api.casedb.co/marketing_sources.php?time=${Date.now()}`, {
                        credentials: 'include'
                    });
                    const data = await response.json();
                    if (lead_filter) {
                        const filteredSources = data.marketing_sources.filter(source => {
                            if (Array.isArray(lead_filter)) {
                                return lead_filter.includes(Number(source.marketing_type));
                            }
                            return Number(source.marketing_type) === Number(lead_filter);
                        });
                        const sources = filteredSources.map(source => [source.id, source.source_name]);
                        setParsedOptions(sources);
                    } else {
                        const sources = data.marketing_sources.map(source => [source.id, source.source_name]);
                        setParsedOptions(sources);
                    }
                } catch (error) {
                    console.error("Error fetching marketing sources:", error);
                    setParsedOptions([]);
                }
            };
    
            fetchMarketingSources();
        } else {
            try {
                let opts = [];
    
                if (Array.isArray(options)) {
                    opts = options;
                } else if (typeof options === 'string') {
                    opts = JSON.parse(options);
                }
    
                if (!Array.isArray(opts)) {
                    throw new Error('Parsed options is not an array');
                }
    
                setParsedOptions(opts);
            } catch (error) {
                console.error("Dropdown options parsing error:", error);
                setParsedOptions([]);
            }
        }
    }, [marketing_list, JSON.stringify(options)]);

    const coerceValue = (raw) => {
        if(raw === '' || raw === null || raw === undefined) return '';
        // Only coerce to number if raw looks like an integer (preserve leading zeros if strictly numeric string is acceptable to convert)
        if(/^[-]?\d+$/.test(raw)) return Number(raw);
        return raw; // preserve non-numeric codes (e.g., DONE, OVERDUE_OR_DUE_WITHIN_30)
    };
    
    return (
        <div className='filter-group'  style={{ width: '100%' }}>
            <div className='select-wrapper' style={{ width: '100%' }}>
                <select 
                    className='filter-select'
                    style={{ width: '100%' }}
                    value={String(value ?? "")}
                    onChange={(e) => onChange(coerceValue(e.target.value))}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                >
                    <option value="" disabled={!marketing_list}>{placeholder || 'Select...'}</option>
                    {parsedOptions.map((option, index) => {
                        const optVal = Array.isArray(option) ? option[0] : index;
                        const optLabel = Array.isArray(option) ? option[1] : option;
                        return <option key={index} value={String(optVal)}>{optLabel}</option>;
                    })}
                </select>
                <ChevronDown size={16} className="select-chevron" />
            </div>
        </div>
    );
};

export const MultiSelect = ({
    options = [],
    value = [],
    onChange = () => {},
    onBlur = () => {},
    onKeyDown = () => {}
}) => {
    let parsedOptions = [];
    let parsedValues = [];

    try {
        // Parse options - should be a JSON array string
        if (typeof options === 'string') {
            parsedOptions = JSON.parse(options);
        } else if (Array.isArray(options)) {
            parsedOptions = options;
        } else {
            parsedOptions = [];
        }

        // Parse values - handle different formats
        if (Array.isArray(value)) {
            parsedValues = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
            // Try parsing as JSON first
            try {
                parsedValues = JSON.parse(value);
            } catch {
                // If JSON parsing fails, it might be a comma-separated string of option names
                const optionNames = value.split(',').map(name => name.trim());
                // Convert option names to indices
                parsedValues = optionNames.map(name => {
                    const index = parsedOptions.findIndex(opt => opt === name);
                    return index !== -1 ? index : null;
                }).filter(index => index !== null);
            }
        } else {
            parsedValues = [];
        }
    } catch (error) {
        console.error("MultiSelect parsing error:", error);
        console.error("Options:", options);
        console.error("Value:", value);
        parsedOptions = [];
        parsedValues = [];
    }

    // Ensure parsedValues is always an array
    if (!Array.isArray(parsedValues)) {
        parsedValues = [];
    }

    const handleToggle = (index) => {
        const isSelected = parsedValues.includes(index);
        const updated = isSelected
            ? parsedValues.filter(v => v !== index)
            : [...parsedValues, index];
        onChange(updated);
    };

    return (
        <div
            className='multi-select-component'
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        >
            {parsedOptions?.map((optionText, index) => (
                <div
                    key={index}
                    className={`ms-option${parsedValues?.includes(index) ? ' active' : ''}`}
                    onClick={() => handleToggle(index)}
                >
                    {optionText}
                </div>
            ))}
        </div>
    );
};

export const SearchSelect = ({ value, onChange, options = EMPTY_SEARCH_SELECT_OPTIONS, placeholder = "Select an option", disabled = false, small = false, navOptions = false, expanded = null, minMatchScore = 0, loadOptions = null, loadOptionsDebounceMs = 250, loadOptionsMinChars = 0, returnOptionObject = false, portal = false, renderOption = null, ariaLabel = null, inputId = undefined, inputRef = undefined, ariaDescribedBy = undefined, ariaInvalid = undefined, ariaRequired = undefined }) => {
    const [search, setSearch] = useState(null);
    const [filtered, setFiltered] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState('below'); // 'below' or 'above'
    const [portalDropdownStyle, setPortalDropdownStyle] = useState(null);
    const [asyncOptions, setAsyncOptions] = useState([]);
    const [asyncLoading, setAsyncLoading] = useState(false);
    const [selectedAsyncOption, setSelectedAsyncOption] = useState(null);
    const dropdownRef = useRef(null);
    const portalDropdownRef = useRef(null);
    const [cases, setCases] = useState([]);
    const [leads, setLeads] = useState([]);
    const asyncRequestIdRef = useRef(0);
    const listboxId = React.useId();
    const navigate = useNavigate();
    const isDisabled = !!disabled || (navOptions && disabled !== null && !expanded);

    const resolvedOptions = useMemo(() => {
        if (!loadOptions) {
            return options;
        }

        if (Array.isArray(options)) {
            return [...options, ...asyncOptions];
        }

        return asyncOptions;
    }, [asyncOptions, loadOptions, options]);

    const validOptions = useMemo(() => {
        if (Array.isArray(resolvedOptions)) return true;
        if (typeof resolvedOptions === 'object' && resolvedOptions !== null) return true;
        if (typeof resolvedOptions === 'string') {
            try {
                const parsed = JSON.parse(resolvedOptions);
                return Array.isArray(parsed) || typeof parsed === 'object';
            } catch {
                return false;
            }
        }
        return false;
    }, [resolvedOptions]);
    
    const parsedOptions = useMemo(() => {
        if (!validOptions) return [];

        try {
            const opts = typeof resolvedOptions === 'string' ? JSON.parse(resolvedOptions) : resolvedOptions;

            let result = [];

            if (Array.isArray(opts)) {
                const isArrayOfStrings = opts.every(opt => typeof opt === 'string');
                const isArrayOfOptionObjects = opts.every(opt => typeof opt === 'object' && opt !== null && ('label' in opt || 'value' in opt));
                result = isArrayOfStrings
                    ? opts.map(val => ({ label: val, value: val }))
                    : isArrayOfOptionObjects
                        ? opts.map((opt, index) => ({
                            ...opt,
                            label: opt.label ?? String(opt.value ?? index),
                            value: String(opt.value ?? opt.label ?? index)
                        }))
                        : opts.map((val, index) => ({ label: String(val), value: index.toString() }));
            } else if (typeof opts === 'object') {
                result = Object.entries(opts).map(([key, label]) => ({ label, value: key }));
            }

            // Async results can overlap the caller's seed options. Keep one
            // stable option per value so labels and React keys remain reliable.
            return dedupeSearchSelectOptions(result)
                .sort((a, b) => String(a.label ?? '').toLowerCase().localeCompare(String(b.label ?? '').toLowerCase()));
        } catch (err) {
            console.error('Invalid options format for SearchSelect:', err);
            return [];
        }
    }, [resolvedOptions, validOptions]); // Add validOptions as dependency

    const selectedLabel = parsedOptions.find(o => o.value === value?.toString())?.label
        || (selectedAsyncOption && selectedAsyncOption.value === value?.toString() ? selectedAsyncOption.label : '')
        || '';

    const normalizeSearchText = useCallback((input) => {
        return String(input ?? '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }, []);

    const levenshteinDistance = useCallback((source, target) => {
        if (source === target) return 0;
        if (!source.length) return target.length;
        if (!target.length) return source.length;

        const previousRow = Array.from({ length: target.length + 1 }, (_, index) => index);

        for (let sourceIndex = 0; sourceIndex < source.length; sourceIndex++) {
            let previousDiagonal = previousRow[0];
            previousRow[0] = sourceIndex + 1;

            for (let targetIndex = 0; targetIndex < target.length; targetIndex++) {
                const temp = previousRow[targetIndex + 1];
                const cost = source[sourceIndex] === target[targetIndex] ? 0 : 1;

                previousRow[targetIndex + 1] = Math.min(
                    previousRow[targetIndex + 1] + 1,
                    previousRow[targetIndex] + 1,
                    previousDiagonal + cost
                );

                previousDiagonal = temp;
            }
        }

        return previousRow[target.length];
    }, []);

    const calculateMatchScore = useCallback((rawQuery, rawLabel) => {
        const query = normalizeSearchText(rawQuery);
        const label = normalizeSearchText(rawLabel);

        if (!query || !label) return 0;
        if (label === query) return 1;

        const queryTokens = query.split(/\s+/).filter(Boolean);
        const labelTokens = label.split(/\s+/).filter(Boolean);

        const tokenScores = queryTokens.map((queryToken) => {
            let bestScore = 0;

            for (const labelToken of labelTokens) {
                if (labelToken === queryToken) {
                    bestScore = 1;
                    break;
                }

                if (labelToken.startsWith(queryToken)) {
                    bestScore = Math.max(bestScore, Math.max(0.85, queryToken.length / labelToken.length));
                    continue;
                }

                if (labelToken.includes(queryToken)) {
                    bestScore = Math.max(bestScore, Math.max(0.7, queryToken.length / labelToken.length));
                    continue;
                }

                const similarity = 1 - (levenshteinDistance(queryToken, labelToken) / Math.max(queryToken.length, labelToken.length));
                bestScore = Math.max(bestScore, similarity);
            }

            return bestScore;
        });

        const tokenCoverage = tokenScores.length
            ? tokenScores.reduce((sum, score) => sum + score, 0) / tokenScores.length
            : 0;

        const phraseCoverage = label.includes(query)
            ? Math.max(0.75, Math.min(1, query.length / Math.max(query.length, label.length) + 0.35))
            : 0;

        const wholeStringSimilarity = 1 - (levenshteinDistance(query, label) / Math.max(query.length, label.length));

        return Math.max(tokenCoverage, phraseCoverage, wholeStringSimilarity);
    }, [levenshteinDistance, normalizeSearchText]);

    useEffect(() => {
        value !== null && setSearch(null);
    }, [value]);

    useEffect(() => {
        if (isDisabled) {
            setIsOpen(false);
        }
    }, [isDisabled]);

    useEffect(() => {
        const requestId = ++asyncRequestIdRef.current;
        // A previous search must not remain selectable while another query is
        // debouncing or after a selection closes and reopens the picker.
        setAsyncOptions([]);
        if (!loadOptions || !isOpen || isDisabled) {
            setAsyncLoading(false);
            return;
        }

        const query = String(search ?? '').trim();
        if (query !== '' && query.length < loadOptionsMinChars) {
            setAsyncLoading(false);
            return;
        }

        setAsyncLoading(true);
        const timer = setTimeout(async () => {
            try {
                const loadedOptions = await loadOptions(query);
                if (asyncRequestIdRef.current === requestId) {
                    setAsyncOptions(Array.isArray(loadedOptions) ? loadedOptions : []);
                }
            } catch (error) {
                if (asyncRequestIdRef.current === requestId) {
                    setAsyncOptions([]);
                    console.error('Error loading SearchSelect options:', error);
                }
            } finally {
                if (asyncRequestIdRef.current === requestId) {
                    setAsyncLoading(false);
                }
            }
        }, loadOptionsDebounceMs);

        return () => {
            clearTimeout(timer);
            // Ignore a response after the query changes, the picker closes or
            // becomes disabled, or its owner unmounts.
            ++asyncRequestIdRef.current;
        };
    }, [isDisabled, isOpen, loadOptions, loadOptionsDebounceMs, loadOptionsMinChars, search]);

    useEffect(() => {
        if (loadOptions) {
            setFiltered(parsedOptions);
            return;
        }

        if (!navOptions) {
            if (!search || search.trim() === '') {
                setFiltered(parsedOptions);
            } else {
                const query = search.trim().toLowerCase();

                if (minMatchScore > 0) {
                    const ranked = parsedOptions
                        .map(option => {
                            const labelText = String(option.label ?? '');
                            const normalizedLabel = labelText.toLowerCase();
                            let score = calculateMatchScore(query, labelText);
                            let tier = 3;

                            if (normalizedLabel === query) {
                                score = 1;
                                tier = 0;
                            } else if (normalizedLabel.startsWith(query)) {
                                score = Math.max(score, 0.95);
                                tier = 1;
                            } else if (normalizedLabel.includes(query)) {
                                score = Math.max(score, 0.8);
                                tier = 2;
                            }

                            return { option, score, tier };
                        })
                        .filter(item => item.score >= minMatchScore)
                        .sort((a, b) => {
                            if (a.tier !== b.tier) return a.tier - b.tier;
                            if (a.score !== b.score) return b.score - a.score;
                            return String(a.option.label ?? '').localeCompare(String(b.option.label ?? ''), undefined, { sensitivity: 'base' });
                        })
                        .slice(0, 25)
                        .map(item => item.option);

                    setFiltered(ranked);
                    return;
                }

                const rankOption = (option) => {
                    const label = String(option.label ?? '').toLowerCase();
                    if (!label) return { tier: 4, score: 0 };

                    if (label === query) return { tier: 0, score: 1000 };
                    if (label.startsWith(query)) return { tier: 1, score: 900 - label.indexOf(query) };
                    if (label.includes(query)) return { tier: 2, score: 800 - label.indexOf(query) };

                    const fuzzy = fuzzysort.single(query, label);
                    const similarityScore = calculateMatchScore(query, label);

                    if (!fuzzy || similarityScore < 0.55) {
                        return { tier: 4, score: -9999 };
                    }

                    return { tier: 3, score: fuzzy.score, similarityScore };
                };

                const rankedOptions = parsedOptions
                    .map(option => ({ option, ...rankOption(option) }))
                    .filter(item => item.tier < 4 && item.score > -10000)
                    .sort((a, b) => {
                        if (a.tier !== b.tier) return a.tier - b.tier;
                        if ((a.similarityScore ?? 0) !== (b.similarityScore ?? 0)) {
                            return (b.similarityScore ?? 0) - (a.similarityScore ?? 0);
                        }
                        if (a.score !== b.score) return b.score - a.score;
                        return String(a.option.label ?? '').localeCompare(String(b.option.label ?? ''), undefined, { sensitivity: 'base' });
                    });

                const hasDirectMatches = rankedOptions.some(item => item.tier <= 2);

                const ranked = rankedOptions
                    .filter(item => !hasDirectMatches || item.tier <= 2)
                    .slice(0, 25)
                    .map(item => item.option);

                setFiltered(ranked);
            }
        } else {
            setFiltered([...cases, ...leads]);
        }
    }, [calculateMatchScore, cases, leads, loadOptions, minMatchScore, navOptions, parsedOptions, search]);

    useEffect(() => {
        if (!isOpen || isDisabled) return;

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current
                && !dropdownRef.current.contains(event.target)
                && (!portalDropdownRef.current || !portalDropdownRef.current.contains(event.target))
            ) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (e) => {
            if ((e.key === 'Delete') && !search) {
                onChange(null);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isDisabled, isOpen, search, onChange]);

    // Calculate dropdown position when opening
    useEffect(() => {
        if (!isOpen || !dropdownRef.current) return;

        const calculatePosition = () => {
            const rect = dropdownRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const dropdownHeight = 300; // Estimated max dropdown height
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const portalWidth = Math.min(rect.width, Math.max(160, viewportWidth - 16));
            const portalLeft = Math.min(
                Math.max(8, rect.left),
                Math.max(8, viewportWidth - portalWidth - 8)
            );

            // Position above if there's insufficient space below but sufficient space above
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                setDropdownPosition('above');
                if (portal) {
                    setPortalDropdownStyle({
                        position: 'fixed',
                        left: `${portalLeft}px`,
                        bottom: `${Math.max(8, viewportHeight - rect.top + 4)}px`,
                        width: `${portalWidth}px`,
                        zIndex: 100000,
                    });
                }
            } else {
                setDropdownPosition('below');
                if (portal) {
                    setPortalDropdownStyle({
                        position: 'fixed',
                        left: `${portalLeft}px`,
                        top: `${rect.bottom + 4}px`,
                        width: `${portalWidth}px`,
                        zIndex: 100000,
                    });
                }
            }
        };

        // Calculate initially
        calculatePosition();

        // Recalculate on scroll or resize
        const handleResize = () => calculatePosition();
        const handleScroll = () => calculatePosition();

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, portal]);

    const handleSelect = (option) => {
        if (isDisabled) return;
        if (navOptions) {
            if (option.type === 'case') {
                navigate(`/case/${option.value}`);
            } else if (option.type === 'lead') {
                navigate(`/intake/${option.value}`);
            }
        } else {
            if (loadOptions) setSelectedAsyncOption(option);
            onChange(returnOptionObject ? option : (typeof option === 'object' ? option.value : option));
        }
    
        setIsOpen(false);
        setSearch('');
    };

    useEffect(() => {
        if (!navOptions || isDisabled) {
            return;
        }

        let cancelled = false;

        const fetchCases = async () => {
            try {
                const results = await fetchNavSearchResults(search ?? '', { type: 'both', limit: 10 });
                if (!cancelled) {
                    setCases(results.cases || []);
                    setLeads(results.leads || []);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Error fetching cases:", error);
                }
            }
        };

        fetchCases();

        return () => {
            cancelled = true;
        };
    }, [isDisabled, navOptions, search]);

    const handleFocus = () => {
        if (!isDisabled && validOptions) {
            if (portal) {
                setPortalDropdownStyle(null);
            }
            setIsOpen(true);
        }
    };

    return (
        <div
            className={`search-select always${isDisabled ? ' disabled' : ''}${small ? ' tiny' : ''}${expanded !== null && expanded === false ? " mini-contained" : ""}`}
            style={{ position: 'relative' }}
            ref={dropdownRef}
        >
            <Search className="search-select-icon" size={20} />
            <input
                id={inputId}
                ref={inputRef}
                type="text"
                className={`search-select-input ${expanded !== null && expanded === false ? "minimized-search" : ""}`}
                role='combobox'
                aria-label={ariaLabel || undefined}
                aria-describedby={ariaDescribedBy}
                aria-invalid={ariaInvalid}
                aria-required={ariaRequired}
                aria-autocomplete='list'
                aria-expanded={isOpen && !isDisabled}
                aria-controls={listboxId}
                value={search ?? (value ? selectedLabel : '')}
                onChange={e => {
                    if (isDisabled) return;
                    const newVal = e.target.value;
                    setSearch(newVal);

                    if (newVal.trim() === '') {
                        onChange(null);
                    }
                }}
                placeholder={expanded !== null && expanded === false ? '' : placeholder}
                onFocus={handleFocus}
                disabled={isDisabled}
                title='Press delete while focused to clear'
            />
            {isOpen && !isDisabled && validOptions && (() => {
                const dropdownMenu = (
                <div
                    id={listboxId}
                    ref={portal ? portalDropdownRef : null}
                    className={`search-select-dropdown ${portal ? 'search-select-dropdown-portal' : ''} ${dropdownPosition === 'above' ? 'position-above' : 'position-below'}`}
                    style={portal ? portalDropdownStyle : undefined}
                    role='listbox'
                >
                    {asyncLoading && filtered.length === 0 ? (
                        <div className="search-select-option disabled">Loading...</div>
                    ) : filtered.length > 0 ? (
                        !navOptions ? (
                            filtered.slice(0, 15).map(opt => {
                                const labelText = typeof opt.label === 'string' ? opt.label : String(opt.label ?? '');
                                return (
                                    <button
                                        type='button'
                                        key={opt.value}
                                        className="search-select-option"
                                        role='option'
                                        aria-selected={opt.value === value?.toString()}
                                        title={labelText}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        {renderOption ? renderOption(opt) : labelText}
                                    </button>
                                );
                            })
                        ) : (
                            <>
                                {/* Separate cases and leads from filtered results */}
                                {(() => {
                                    const filteredCases = filtered.filter(opt => cases.some(c => c.value === opt.value));
                                    const filteredLeads = filtered.filter(opt => leads.some(l => l.value === opt.value));
                                    
                                    return (
                                        <>
                                            {filteredCases.length > 0 && (
                                                <>
                                                    <div className="search-select-subheader">Cases</div>
                                                    {filteredCases.map((opt, idx) => {
                                                        const labelText = typeof opt.label === 'string' ? opt.label : String(opt.label ?? '');
                                                        return (
                                                        <button
                                                            type='button'
                                                            key={`case-${opt.value}-${idx}`}
                                                            className="search-select-option"
                                                            role='option'
                                                            aria-selected={opt.value === value?.toString()}
                                                            title={`${labelText} ${opt.phase ? ` (${opt.phase})` : ''}`}
                                                            onClick={() => handleSelect(opt)}
                                                        >
                                                            {labelText}{opt.phase && <span className="search-select-meta">({opt.phase})</span>}
                                                        </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                            {filteredLeads.length > 0 && (
                                                <>
                                                    {filteredCases.length > 0 && (
                                                        <hr className="search-select-divider" />
                                                    )}
                                                    <div className="search-select-subheader">Leads</div>
                                                    {filteredLeads.map((opt, idx) => {
                                                        const labelText = typeof opt.label === 'string' ? opt.label : String(opt.label ?? '');
                                                        return (
                                                        <button
                                                            type='button'
                                                            key={`lead-${opt.value}-${idx}`}
                                                            className="search-select-option"
                                                            role='option'
                                                            aria-selected={opt.value === value?.toString()}
                                                            title={`${labelText}${opt.status ? ` (${opt.status})` : ''}`}
                                                            onClick={() => handleSelect(opt)}
                                                        >
                                                            {labelText}{opt.status && <span className="search-select-meta"> ({opt.status})</span>}
                                                        </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </>
                        )
                    ) : (
                        <div style={{ padding: '8px', color: 'var(--text-color)' }}>
                            No results found
                        </div>
                    )}
                </div>
                );
                if (portal) {
                    return portalDropdownStyle
                        ? createPortal(dropdownMenu, document.body)
                        : null;
                }
                return dropdownMenu;
            })()}
        </div>
    );
};

const parseActionFieldOptions = (options) => {
    if (options && typeof options === 'object') {
        return options;
    }

    if (typeof options !== 'string') {
        return {};
    }

    const trimmed = options.trim();
    if (!trimmed) {
        return {};
    }

    try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
        }
    } catch (error) {
    }

    return { url: trimmed };
};

const resolveActionFieldUrl = (template, variables = {}) => {
    if (!template || typeof template !== 'string') {
        return '';
    }

    let resolved = template;

    Object.entries(variables).forEach(([key, rawValue]) => {
        const value = rawValue == null ? '' : String(rawValue);
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        resolved = resolved
            .replace(new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g'), value)
            .replace(new RegExp(`\\{${escapedKey}\\}`, 'g'), value)
            .replace(new RegExp(`${escapedKey}=\\?`, 'g'), `${key}=${encodeURIComponent(value)}`);
    });

    return resolved;
};

const resolveActionFieldText = (template, variables = {}) => {
    if (!template || typeof template !== 'string') {
        return '';
    }

    let resolved = template;

    Object.entries(variables).forEach(([key, rawValue]) => {
        const value = rawValue == null ? '' : String(rawValue);
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        resolved = resolved
            .replace(new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g'), value)
            .replace(new RegExp(`\\{${escapedKey}\\}`, 'g'), value);
    });

    return resolved.trim();
};

const getActionFieldOption = (config = {}, keys = [], fallback = '') => {
    for (const key of keys) {
        const value = config?.[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return fallback;
};

const parseActionFieldValue = (value) => {
    if (!value) {
        return null;
    }

    if (typeof value === 'object') {
        return value;
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
        } catch (error) {
            return {
                triggered_at: null,
                message: value,
                status: 'success'
            };
        }
    }

    return null;
};

const formatActionFieldTimestamp = (timestamp) => {
    if (!timestamp) {
        return '';
    }

    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) {
        return String(timestamp);
    }

    return parsedDate.toLocaleString();
};

const buildActionFieldDisplayContext = ({
    field,
    buttonLabel,
    triggeredAt,
    actionUrl,
    actionMessage,
    saveMessage,
    status
}) => {
    const formattedTime = formatActionFieldTimestamp(triggeredAt);

    return {
        field_name: field?.name || '',
        fieldName: field?.name || '',
        button_label: buttonLabel || '',
        buttonLabel: buttonLabel || '',
        triggered_at: triggeredAt || '',
        triggeredAt: triggeredAt || '',
        time: formattedTime,
        datetime: formattedTime,
        action_url: actionUrl || '',
        actionUrl: actionUrl || '',
        action_message: actionMessage || '',
        actionMessage: actionMessage || '',
        save_message: saveMessage || '',
        saveMessage: saveMessage || '',
        status: status || ''
    };
};

export const ActionTriggerField = ({
    field,
    value,
    leadId,
    caseId,
    groupId = null,
    disabled = false,
    onTriggered = null
}) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const [isRunning, setIsRunning] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [activityStatus, setActivityStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    const actionConfig = useMemo(() => parseActionFieldOptions(field?.options), [field?.options]);
    const method = String(actionConfig.method || 'GET').toUpperCase();
    const actionUrl = useMemo(() => resolveActionFieldUrl(actionConfig.url || '', {
        case_id: caseId,
        caseId,
        lead_id: leadId,
        leadId,
        field_id: field?.id,
        fieldId: field?.id
    }), [actionConfig.url, caseId, leadId, field?.id]);
    const storedState = useMemo(() => parseActionFieldValue(localValue), [localValue]);
    const buttonLabel = getActionFieldOption(actionConfig, ['buttonLabel', 'button_label', 'label', 'ctaLabel', 'cta_label'], 'Run Action');
    const storedStatusLabel = useMemo(() => {
        if (!storedState) {
            return '';
        }

        const explicitLabel = storedState.status_label || storedState.statusLabel || storedState.saved_label || storedState.savedLabel;
        if (explicitLabel) {
            return explicitLabel;
        }

        if (!storedState.triggered_at) {
            return '';
        }

        return `Last triggered ${formatActionFieldTimestamp(storedState.triggered_at)}`;
    }, [storedState]);
    const requiresCaseId = /\{\{?case_?id\}?\}|case_id=\?/i.test(String(actionConfig.url || ''));
    const canTrigger = !disabled && !!leadId && !!actionUrl && (!requiresCaseId || !!caseId);

    const handleTrigger = async () => {
        if (!canTrigger || isRunning) {
            return;
        }

        setIsRunning(true);
        setErrorMessage('');

        try {
            const requestOptions = {
                method,
                credentials: 'include',
                headers: {}
            };

            if (method !== 'GET') {
                requestOptions.headers['Content-Type'] = 'application/json';
                requestOptions.body = JSON.stringify({
                    case_id: caseId,
                    lead_id: leadId,
                    field_id: field?.id
                });
            }

            const actionResponse = await fetch(actionUrl, requestOptions);
            const actionContentType = actionResponse.headers.get('content-type') || '';
            const actionPayload = actionContentType.includes('application/json')
                ? await actionResponse.json()
                : { message: await actionResponse.text() };

            if (!actionResponse.ok || actionPayload?.success === false || actionPayload?.error) {
                throw new Error(actionPayload?.message || actionPayload?.error || `Request failed with status ${actionResponse.status}`);
            }

            const triggeredAt = new Date().toISOString();
            const actionMessage = actionPayload?.message || actionPayload?.data?.message || '';
            const saveSuccessMessage = getActionFieldOption(actionConfig, ['savedMessage', 'saved_message', 'successMessage', 'success_message'], '');
            const saveStatusTemplate = getActionFieldOption(actionConfig, ['savedLabel', 'saved_label', 'statusLabel', 'status_label', 'successLabel', 'success_label'], 'Last triggered {time}');
            const displayContext = buildActionFieldDisplayContext({
                field,
                buttonLabel,
                triggeredAt,
                actionUrl,
                actionMessage,
                saveMessage: saveSuccessMessage,
                status: 'success'
            });

            const storedPayload = {
                triggered_at: triggeredAt,
                status: 'success',
                status_label: resolveActionFieldText(saveStatusTemplate, displayContext),
                message: saveSuccessMessage
                    ? resolveActionFieldText(saveSuccessMessage, displayContext)
                    : (actionMessage || `${buttonLabel} triggered successfully`),
                action_url: actionUrl
            };
            const storedValue = JSON.stringify(storedPayload);

            const saveResponse = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    lead_id: leadId,
                    ...(caseId ? { case_id: caseId } : {}),
                    ...(groupId != null ? { group_id: groupId } : {}),
                    field_values: {
                        [field.id]: storedValue
                    }
                })
            });

            const savePayload = await saveResponse.json();
            if (!saveResponse.ok || !savePayload?.success) {
                throw new Error(savePayload?.message || 'Failed to save action trigger state');
            }

            setLocalValue(storedValue);
            if (typeof onTriggered === 'function') {
                await onTriggered(storedPayload, actionPayload, savePayload);
            }
        } catch (error) {
            console.error('Action trigger field error:', error);
            setErrorMessage(error.message || 'Failed to trigger action');
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className='form-group nm'>
            <label className='subtext'>{field?.name}</label>
            <button
                type='button'
                className='action small quaternary'
                onClick={handleTrigger}
                disabled={!canTrigger || isRunning}
                title={!caseId && requiresCaseId ? 'This action requires a case ID' : buttonLabel}
            >
                {isRunning ? 'Running...' : buttonLabel}
            </button>
            {storedStatusLabel && (
                <div className='subtext' style={{ marginTop: '6px' }}>
                    {storedStatusLabel}
                </div>
            )}
            {storedState?.message && storedState.message !== storedStatusLabel && (
                <div className='subtext' style={{ marginTop: '4px' }}>
                    {storedState.message}
                </div>
            )}
            {errorMessage && (
                <div className='subtext' style={{ marginTop: '4px', color: 'var(--priority-color)' }}>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export const FormActivityTriggerField = ({
    field,
    value,
    leadId,
    caseId,
    groupId = null,
    disabled = false
}) => {
    const [localValue, setLocalValue] = useState(value ?? '');
    const [isRunning, setIsRunning] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    const formConfig = useMemo(() => parseActionFieldOptions(field?.options), [field?.options]);
    const storedState = useMemo(() => parseActionFieldValue(localValue), [localValue]);
    const activityStatus = storedState?.form_activity_status || null;
    const buttonLabel = getActionFieldOption(formConfig, ['buttonLabel', 'button_label', 'label', 'ctaLabel', 'cta_label'], 'Create Form Request');
    const canTrigger = !disabled && !!leadId && !!caseId;

    const saveFormTriggerState = async (storedValue) => {
        const saveResponse = await fetch('https://api.casedb.co/custom_fields.php', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                lead_id: leadId,
                ...(caseId ? { case_id: caseId } : {}),
                ...(groupId != null ? { group_id: groupId } : {}),
                field_values: {
                    [field.id]: storedValue
                }
            })
        });

        const savePayload = await saveResponse.json();
        if (!saveResponse.ok || !savePayload?.success) {
            throw new Error(savePayload?.message || 'Failed to save form request state');
        }

        return savePayload;
    };

    const handleReset = async () => {
        if (isResetting) {
            return;
        }

        setIsResetting(true);
        setErrorMessage('');
        try {
            await saveFormTriggerState('');
            setLocalValue('');
        } catch (error) {
            console.error('Form activity reset error:', error);
            setErrorMessage(error.message || 'Failed to reset form request');
        } finally {
            setIsResetting(false);
        }
    };

    const handleTrigger = async () => {
        if (!canTrigger || isRunning) {
            return;
        }

        setIsRunning(true);
        setErrorMessage('');

        try {
            const response = await fetch('https://api.casedb.co/activity_feed.php', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create_form_activity',
                    case_id: caseId,
                    lead_id: leadId,
                    field_id: field?.id,
                    field_name: field?.name,
                    group_id: groupId,
                    config: formConfig
                })
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to create form request');
            }

            const triggeredAt = new Date().toISOString();
            const storedPayload = {
                triggered_at: triggeredAt,
                status: 'success',
                status_label: `Last requested ${formatActionFieldTimestamp(triggeredAt)}`,
                message: result.message || 'Form request created',
                activity_id: result.inserted_id || null
            };
            const storedValue = JSON.stringify(storedPayload);

            await saveFormTriggerState(storedValue);

            setLocalValue(storedValue);
        } catch (error) {
            console.error('Form activity trigger field error:', error);
            setErrorMessage(error.message || 'Failed to create form request');
        } finally {
            setIsRunning(false);
        }
    };

    const renderStatusCard = () => {
        if (!storedState?.activity_id) {
            return null;
        }

        const response = activityStatus?.response;
        const request = activityStatus?.activity || {};
        const mentionUsers = Array.isArray(request.mention_users) ? request.mention_users : [];
        const waitingFor = mentionUsers.length > 0
            ? mentionUsers.map(user => `@${user.user || user.name || user.id}`).join(', ')
            : 'mentioned user';
        const isSubmitted = activityStatus?.status === 'submitted' && response;
        const title = isSubmitted ? 'Form Response Submitted' : 'Awaiting Form Response';
        const primaryLine = isSubmitted
            ? `${response.name || response.user || 'A user'} responded ${response.label || response.value}`
            : `Waiting for ${waitingFor}`;
        const secondaryLine = isSubmitted
            ? `Responded ${formatActionFieldTimestamp(response.answered_at || response.creation_date)}`
            : `Requested ${formatActionFieldTimestamp(request.creation_date || storedState.triggered_at)}`;

        return (
            <div className='file-upload expanded form-activity-status-card'>
                <div className='file-upload-wrapper' style={{ border: '1px solid var(--border-color)' }}>
                    <div className='docgen-icon' style={{ marginTop: '2px' }}>
                        <CheckSquare size={18} style={{ flexShrink: 0 }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                        <div className='small-text' style={{ fontWeight: '500', color: 'var(--fill)' }}>
                            {title}
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--text-color)', lineHeight: '1.2' }}>
                            {primaryLine}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--subtext-color)', lineHeight: '1.1' }}>
                            {secondaryLine}
                        </span>
                    </div>
                    <button
                        className='action alt micro'
                        onClick={handleReset}
                        disabled={isResetting}
                        title='Disregard this request'
                        style={{ flexShrink: 0 }}
                    >
                        {isResetting ? <Loader2 size={16} className='spinner' /> : <Eraser size={16} />}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div id={`field-${field?.id}`} data-field-id={field?.id} className='form-group nm'>
            <label className='subtext'>{field?.name}</label>
            {storedState?.activity_id ? renderStatusCard() : (
                <button
                    type='button'
                    className='action small quaternary'
                    onClick={handleTrigger}
                    disabled={!canTrigger || isRunning}
                    title={!caseId ? 'This form request requires a case ID' : buttonLabel}
                >
                    {isRunning ? 'Creating...' : buttonLabel}
                </button>
            )}
            {errorMessage && (
                <div className='subtext' style={{ marginTop: '4px', color: 'var(--priority-color)' }}>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export const Boolean = ({ options = [], value, onChange, required, onBlur = () => {}, onKeyDown = () => {} }) => {
    let parsedOptions = [];

    try {
        parsedOptions = Array.isArray(options) 
            ? options 
            : JSON.parse(options);
    } catch (error) {
        console.error("Error parsing Boolean options:", options, error);
        parsedOptions = [];
    }

    return (
        <div className={`boolean${required ? ' required-input' : ''}`} onBlur={onBlur} onKeyDown={onKeyDown}>
            {parsedOptions.map((option, index) => (
                <div 
                    key={index} 
                    className={`option${value === index ? ' active' : ''}`} 
                    onClick={() => onChange(index)}
                >
                    {option}
                </div>
            ))}
        </div>
    );
};

export const Toggle = ({ value, onChange, label = null, labels = null }) => {
    const hasStateLabels = Array.isArray(labels) && labels.length === 2;
    const toggleValue = () => onChange(!value);

    return (
        <div className='toggle-container'>
            {hasStateLabels && (
                <span className={`subtext toggle-state-label${!value ? ' active' : ''}`}>
                    {labels[0]}
                </span>
            )}
            <div
                className={`toggle${value ? ' active' : ''}`}
                role="switch"
                aria-checked={!!value}
                tabIndex={0}
                onClick={toggleValue}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleValue();
                    }
                }}
            >
                <div className={`toggle-circle ${value ? 'set' : ''}`} />
            </div>
            {hasStateLabels ? (
                <span className={`subtext toggle-state-label${value ? ' active' : ''}`}>
                    {labels[1]}
                </span>
            ) : (
                label && <span className='subtext'>{label}</span>
            )}
        </div>
    );
};

export const NeutralToggle = ({ value, onChange, options, hint }) => {
    const isTwoState = Array.isArray(options) && options.length === 2 && options.every(o => o && o.value !== undefined);

    // Two-state toggle: cycle between option[0] and option[1]
    if (isTwoState) {
        const optionA = options[0];
        const optionB = options[1];
        const isA = value === optionA.value;
        const isB = value === optionB.value;
        const isNeutral = !isA && !isB;

        const handleToggle = () => {
            const next = isA ? optionB.value : optionA.value;
            onChange(next);
        };

        return (
            <div className='neutral-toggle-container'>
                <span className='subtext'>{optionA.label}</span>
                <div
                    className={`neutral-toggle${isNeutral ? ' neutral' : ''}${isB ? ' active' : ''}`}
                    onClick={handleToggle}
                >
                    <div className={`neutral-toggle-circle ${isNeutral ? ' neutral' : isB ? ' set' : ''}`} />
                </div>
                <span className='subtext'>{optionB.label}</span>
                {hint && <span className='subtext hint-text'>{hint}</span>}
            </div>
        );
    }

    // Legacy three-state behavior (null/true/false)
    return (
        <div className='neutral-toggle-container'>
            <span className='subtext'>{options[0].label}</span>
            <div className={`neutral-toggle${value === null ? ' neutral' : ''}${value ? ' active' : ''}`} onClick={() => {
                if (value === null) {
                    onChange(true);
                } else if (value === true) {
                    onChange(false);
                } else {
                    onChange(null);
                }
            }}>
                <div className={`neutral-toggle-circle ${value === null ? ' neutral' : value ? ' set' : ''}`} />
            </div>
            <span className='subtext'>{options[1].label}</span>
            {hint && <span className='subtext hint-text'>{hint}</span>}
        </div>
    );
};

export const Checkbox = ({
    value,
    onChange,
    mini = null,
    label = null,
    hint = null,
    space = null,
    top,
    disabled = false,
    ariaLabel = null,
    ariaDescribedBy = null,
}) => {
    const [hintVisible, setHintVisible] = useState(false);
    const hintRef = useRef(null);
    const hintBubbleRef = useRef(null);
    const [hintStyle, setHintStyle] = useState({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (hintRef.current && !hintRef.current.contains(event.target)) {
                setHintVisible(false);
            }
        };

        if (hintVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [hintVisible]);

    useEffect(() => {
        if (!hintVisible || !hintRef.current) {
            setHintStyle({});
            return;
        }

        const padding = 8;
        const anchorRect = hintRef.current.getBoundingClientRect();
        const centerX = anchorRect.left + anchorRect.width / 2;
        const spaceLeft = Math.max(0, centerX - padding);
        const spaceRight = Math.max(0, window.innerWidth - centerX - padding);
        const maxWidth = Math.max(120, Math.min(260, 2 * Math.min(spaceLeft, spaceRight)));

        setHintStyle({
            maxWidth: `${Math.floor(maxWidth)}px`,
            width: `${Math.floor(maxWidth)}px`,
            minWidth: '0'
        });
    }, [hintVisible]);

    const handleChange = (event) => {
        if (disabled) {
            event?.preventDefault();
            return;
        }
        onChange?.(event);
    };

    const handleKeyDown = (event) => {
        if (disabled || (event.key !== ' ' && event.key !== 'Enter')) return;
        event.preventDefault();
        onChange?.(event);
    };

    const accessibilityProps = ariaLabel ? {
        role: 'checkbox',
        'aria-checked': value ? 'true' : 'false',
        'aria-disabled': disabled,
        'aria-label': ariaLabel,
        'aria-describedby': ariaDescribedBy || undefined,
        tabIndex: disabled ? -1 : 0,
        onKeyDown: handleKeyDown,
    } : {};

    return (
        <div 
            className={`checkbox-group${space ? ' space' : ''}${disabled ? ' disabled' : ''}`}
            style={{ alignItems: top ? 'flex-start' : 'center' }}
            onClick={handleChange}
            {...accessibilityProps}
        >
            <div className={`checkbox${mini ? '-mini' : ''} ${value ? ' checked' : ''}`}>
                {value && !mini ? <Check size={24} /> : value && mini ? <Check size={16} /> : ''}
            </div>
            {label && <label className='subtext'>{label}</label>}
            <input type='checkbox' checked={!!value} readOnly disabled={disabled} hidden aria-hidden='true' />
            {hint && <div 
                className='hint-container' 
                ref={hintRef}
                onMouseEnter={() => setHintVisible(true)}
                onMouseLeave={() => setHintVisible(false)}
            >
                <Info className='info-box' size={18} />
                {hintVisible && (
                    <div className='hint' ref={hintBubbleRef} style={hintStyle}>
                        {hint}
                    </div>
                )}
            </div>}
        </div>
    );
};

export const Subheader = ({ title, small = false, action = null }) => {
    // Create URL-safe ID from title
    const titleId = title.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
    
    if (action) {
        return (
            <div className={`subheader ${small ? 'small' : ''}`} id={titleId}>
                <h2>{title}</h2>
                {action}
            </div>
        );
    }

    return <h2 className='subheader-solo' id={titleId}>{title}</h2>;
};

export const SubtextTitle = ({ title }) => {
    return <p className='subtext-title'>{title}</p>;
}

export const DataDisplay = ({title, value, type }) => {
    return (
        <div className='data-display'>
            <span className='subtext'>{title}</span>
            <span className='subtext-data'>{formatValue(value, type)}</span>
        </div>
    );
};

// Shared renderer for simple field inputs (mirrors Section.js switch for supported types)
export const renderFieldInputForPreset = ({ field, value, onChange, required = false }) => {
    if (!field) return null;

    switch (field?.field_id) {
        case 1:
            return <Text type="text" placeholder={field.name} value={value} onChange={onChange} required={required} />;
        case 2:
            return <Text type="textarea" placeholder={field.name} value={value} onChange={onChange} required={required} />;
        case 3:
            return <NumberInput type="currency" value={value} onChange={onChange} required={required} />;
        case 4:
            return <NumberInput type="percent" value={value} onChange={onChange} required={required} />;
        case 5:
            return <NumberInput type="number" value={value} onChange={onChange} required={required} />;
        case 6:
            return <DateInput value={String(value || '')} onChange={onChange} required={required} />;
        case 7:
            return <TimeInput value={value} onChange={onChange} />;
        case 10:
            return (
                <Dropdown
                    options={field?.options || "[]"}
                    value={value}
                    onChange={onChange}
                    marketing_list={field?.id === 225}
                />
            );
        case 11:
            return <MultiSelect options={field?.options || "[]"} value={value} onChange={onChange} />;
        case 12: {
            const booleanValue = (value === undefined || value === null || value === '') ? 2 : Number(value);
            return <Boolean options={field?.options || []} value={booleanValue} required={required} onChange={onChange} />;
        }
        case 22:
            return (
                <SearchSelect
                    value={value}
                    onChange={onChange}
                    options={field?.options || []}
                    placeholder={field?.name}
                />
            );
        default:
            return null;
    }
};

export const Instructions = ({ instructions, limited = false }) => {
    return <p className='instructions' style={{maxWidth: limited ? '50%' : ''}}>{instructions}</p>;
};

export const FileUpload = ({ value, onChange, upload = null, uploadWaiting = false }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [startWait, setStartWait] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const fileInputRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (uploadWaiting) return;

        const hasUploadedMeta = value && typeof value === 'object' && (value.id || value.file_id || value.file_url || value.url);
        const hasUploadedId = typeof value === 'number' || /^\d+$/.test(String(value || ''));

        if (file && (hasUploadedMeta || hasUploadedId)) {
            setFile(null);
            setUploading(false);
            setStartWait(false);
        }
    }, [value, uploadWaiting, file]);

    useEffect(() => {
        if (!menuOpen) return;

        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            onChange?.(selectedFile);
            setUploading(true);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleClear = () => {
        setFile(null);
        onChange?.(null);
        setUploading(false);
        setStartWait(false);
    };

    const handleReplaceClick = (e) => {
        e.stopPropagation();
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === null || bytes === undefined || isNaN(bytes)) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const formatted = formatLastModified(dateString);
        return formatted?.[5] || '';
    };

    const resolveDisplayMeta = () => {
        if (file) {
            return {
                id: null,
                name: file.name,
                size: file.size,
                uploaded_date: null,
                file_url: null
            };
        }

        if (value && typeof value === 'object') {
            return {
                id: value.id || value.file_id || null,
                name: value.file_name || value.filename || value.name || 'Uploaded file',
                size: value.size || value.file_size || null,
                uploaded_date: value.uploaded_date || value.creation_date || value.updated_at || null,
                file_url: value.file_url || value.url || null,
                file_path: value.file_path || value.key || null
            };
        }

        if (typeof value === 'number' || /^\d+$/.test(String(value || ''))) {
            return {
                id: Number(value),
                name: 'Uploaded file',
                size: null,
                uploaded_date: null,
                file_url: null
            };
        }

        if (typeof value === 'string' && value.trim() !== '') {
            return {
                id: null,
                name: value,
                size: null,
                uploaded_date: null,
                file_url: null
            };
        }

        return null;
    };

    const downloadFile = async (fileUrl, filename) => {
        if (!fileUrl) return;
        try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
        }
    };

    const displayMeta = resolveDisplayMeta();
    const fileId = displayMeta?.id || null;
    const filePath = displayMeta?.file_path || '';
    const normalizedPath = filePath.replace(/^cases\/\d+\//, '');
    const withoutName = normalizedPath.replace(/^{{Name}}\//, '');
    const folderFromPath = withoutName.includes('/')
        ? withoutName.split('/').slice(0, -1).join('/')
        : '';
    const canGoToLocation = !!folderFromPath;
    const openFullView = () => {
        if (!fileId) return;
        window.open(`/file/${fileId}`, '_blank', 'noopener');
    };
    const goToFileLocation = () => {
        if (!canGoToLocation) return;
        const targetUrl = `${window.location.pathname}?section=3&folder=${encodeURIComponent(folderFromPath)}`;
        navigate(targetUrl);
    };
    const detailParts = [];
    if (displayMeta && displayMeta.size !== null && displayMeta.size !== undefined && Number(displayMeta.size) > 0) {
        const sizeText = formatBytes(displayMeta.size);
        if (sizeText) detailParts.push(sizeText);
    }
    if (displayMeta) {
        const dateText = formatDateTime(displayMeta.uploaded_date);
        if (dateText) detailParts.push(dateText);
    }
    const detailText = detailParts.join(' • ');

    return (
        <div className={`file-upload ${uploading || value ? 'expanded' : ''}`} onClick={() => fileInputRef.current?.click()}>
            <input
                type='file'
                id='file-upload'
                ref={fileInputRef}
                onChange={handleFileChange}
                hidden
            />

            {!uploading && !value && (
                <label htmlFor='file-upload' className='small-text' onClick={(e) => { e.preventDefault(); }}>
                    Upload a file
                </label>
            )}

            {displayMeta && (
                <>
                    <div className="file-upload-wrapper" style={{ border: '1px solid var(--border-color)' }}>
                        <div className='docgen-icon' style={{ marginTop: '2px' }}>
                            <FileIcon size={18} style={{ flexShrink: 0 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center', alignItems: 'flex-start', flex: 1, minWidth: 0, height: '36px' }}>
                            <div
                                className="small-text"
                                style={{ fontWeight: '500', textWrap: 'wrap', color: 'var(--fill)', cursor: displayMeta.id ? 'pointer' : 'default', textDecoration: displayMeta.id ? 'underline' : 'none' }}
                                title={displayMeta.name}
                                onClick={() => displayMeta.id && window.open(`/file/${displayMeta.id}`, '_blank')}
                            >
                                {displayMeta.name}
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--subtext-color)', lineHeight: '1' }}>
                                {detailText}
                            </span>
                        </div>
                        <div ref={menuRef} style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                            <button
                                className='action alt micro'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                                title="Clear file"
                                style={{ flexShrink: 0 }}
                            >
                                <X size={16} />
                            </button>
                            <button
                                className='action alt micro'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen(prev => !prev);
                                }}
                                title="More options"
                                style={{ flexShrink: 0 }}
                            >
                                <Ellipsis size={16} />
                            </button>
                            {menuOpen && (
                                <div className='file-actions-menu-dropdown'>
                                    <div
                                        className={`file-actions-menu-dropdown-item${displayMeta.file_url ? '' : ' disabled'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!displayMeta.file_url) return;
                                            setMenuOpen(false);
                                            downloadFile(displayMeta.file_url, displayMeta.name);
                                        }}
                                    >
                                        Download
                                    </div>
                                    <div
                                        className={`file-actions-menu-dropdown-item${canGoToLocation ? '' : ' disabled'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!canGoToLocation) return;
                                            setMenuOpen(false);
                                            goToFileLocation();
                                        }}
                                    >
                                        Go to file location
                                    </div>
                                    <div
                                        className={`file-actions-menu-dropdown-item${fileId ? '' : ' disabled'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!fileId) return;
                                            setMenuOpen(false);
                                            openFullView();
                                        }}
                                    >
                                        View file
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {!value && <div className='file-actions'>
                        {upload != null && (
                            <>
                                <button
                                    className='upload'
                                    disabled={startWait}
                                    onClick={() => {
                                        upload?.();
                                        setStartWait(true);
                                    }}
                                >
                                    {!startWait ? (
                                        <CloudUpload size={18} />
                                    ) : startWait === 'done' ? (
                                        <Check />
                                    ) : (
                                        <Loader2 className='spinner' />
                                    )}
                                </button>
                                <button className='upload' disabled={startWait} onClick={handleClear}>
                                    <Eraser size={18} />
                                </button>
                            </>
                        )}
                    </div>}
                </>
            )}
        </div>
    );
};

export const UploadModal = ({ onClose, onUpload, uploadWaiting }) => {
    const [files, setFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [startWait, setStartWait] = useState(false);
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    // Track last drop signature to avoid duplicate processing when the browser
    // fires multiple drag/drop related events for the same payload.
    const lastDropSigRef = useRef({ sig: null, ts: 0 });
    // Counter for nested dragenter/dragleave events so active class doesn't flicker
    const dragCounterRef = useRef(0);

    const handleFileChange = (e) => {
        // FileList is live in real browsers: clearing the input can empty it
        // before React applies the queued state updater. Snapshot it first so
        // every file selected in this chooser interaction reaches the queue.
        const selectedFiles = Array.from(e.currentTarget.files || []);
        if (folderInputRef.current) folderInputRef.current.value = '';
        setFiles(currentFiles => mergeDocumentUploadFiles(currentFiles, selectedFiles));
        // Resetting the input lets selecting the same file again fire change;
        // state above remains the authoritative accumulated selection.
        e.currentTarget.value = '';
    };

    const handleFolderChange = (e) => {
        const selectedFiles = Array.from(e.currentTarget.files || []);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setFiles(currentFiles => mergeDocumentUploadFiles(currentFiles, selectedFiles));
        e.currentTarget.value = '';
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setDragActive(false);

        console.log('[DROP] Starting drag and drop processing');
        const dataTransfer = e.dataTransfer;
        console.log('[DROP] DataTransfer files length:', dataTransfer.files.length);

        try {
            const allFiles = await collectDroppedDocumentFiles(dataTransfer);

            console.log('[DROP] Total files collected from items:', allFiles.length);
            console.log('[DROP] File names:', allFiles.map(f => f.name));

            if (allFiles.length > 0) {
                const sig = allFiles.map(f => `${getDocumentUploadFilePath(f)}:${f.size}`).join('|');
                const now = Date.now();
                if (lastDropSigRef.current.sig === sig && (now - lastDropSigRef.current.ts) < 1000) {
                    console.log('⏭️ [DROP] Duplicate drop ignored');
                    return;
                }
                lastDropSigRef.current = { sig, ts: now };
                setFiles(currentFiles => mergeDocumentUploadFiles(currentFiles, allFiles));
            }
        } catch (error) {
            console.error('Error reading dropped files/folders:', error);
            // Final fallback to original method
            if (dataTransfer.files.length > 0) {
                const fallbackFiles = filterDocumentUploadFiles(dataTransfer.files);
                console.log('[DROP] Using final fallback method, files:', fallbackFiles.length);
                setFiles(currentFiles => mergeDocumentUploadFiles(currentFiles, fallbackFiles));
            }
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (!dragActive) setDragActive(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) {
            setDragActive(false);
        }
    };

    const handleUpload = () => {
        if (!files.length) return;
        
        console.log('[UPLOAD] Starting upload process');
        console.log('[UPLOAD] Number of files to upload:', files.length);
        console.log('[UPLOAD] File names:', files.map(f => f.name));
        
        setStartWait(true);
        onUpload(files);
        // Note: The parent component should close this modal after calling onUpload
    };

    useEffect(() => {
        if (uploadWaiting === false && startWait) {
            setStartWait(false);
            setFiles([]);
        }
    }, [uploadWaiting, startWait]);

    return (
        <div className="file-upload-container">
            <div 
                className='drop-zone-bg' 
                onClick={onClose}
            ></div>

            <div
                className={`drop-zone ${dragActive ? 'active' : ''}`}
                role="region"
                aria-label="File upload drop zone"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="drop-zone-content">
                    <CloudUpload size={40} />
                    {files.length > 0 ? (
                    <div className="inner-file-list subtext" style={{ marginTop: '1rem' }}>
                        {files.length > 5 ? (
                        <>
                            {files.slice(0, 5).map((f, i) => (
                            <span key={i}>{f.webkitRelativePath || f.name}</span>
                            ))}
                            <span>+{files.length - 5} more file(s)</span>
                        </>
                        ) : (
                        files.map((f, i) => (
                            <span key={i}>{f.webkitRelativePath || f.name}</span>
                        ))
                        )}
                    </div>
                    ) : (
                    <>
                        <p>Drag and drop files or folders here</p>
                        <p className="drop-zone-sub">or use the buttons below</p>
                    </>
                    )}

                    {files.length > 0 && (
                        <button 
                            className="upload action" 
                            onClick={handleUpload}
                            disabled={startWait}
                            style={{ marginTop: '1rem' }}
                        >
                            {startWait ? (uploadWaiting ? 'Uploading...' : 'Done') : 'Upload'}
                        </button>
                    )}

                    <div className="upload-actions library-actions">
                        <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        aria-label="Choose files"
                        hidden
                        onChange={handleFileChange}
                        />
                        <button onClick={() => fileInputRef.current?.click()} className="action quaternary">
                            Choose Files
                        </button>

                        <input
                        type="file"
                        ref={folderInputRef}
                        multiple
                        webkitdirectory="true"
                        aria-label="Choose folders"
                        hidden
                        onChange={handleFolderChange}
                        />
                        <button onClick={() => folderInputRef.current?.click()} className="action quaternary">
                            Choose Folders
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const MultiFile = ({ value, onChange, upload = null, uploadWaiting, disable }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [startWait, setStartWait] = useState(false);
    const fileInputRef = useRef(null);
    const wrapperRef = useRef(null);

    console.log(value);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
        onChange?.(selectedFiles);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (!uploadWaiting && startWait) {
            setFiles([]);
            setStartWait(false);
        }
    }, [uploadWaiting]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setUploading(false);
            }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (Array.isArray(value) && value.length > 0 && files.length === 0) {
            // Handle both File objects and file name strings
            setFiles(value.map(file => {
                if (typeof file === 'string') {
                    // It's already a file name, use as is
                    return file;
                }
                return file?.name || file;
            }));
        }
    }, [value]);

    return (
        <div className={`file-upload${uploading || value?.length > 0 ? ' expanded' : ''}`} ref={wrapperRef} onClick={() => {setUploading(true); fileInputRef.current?.click();}}>
            <input
                type='file'
                id='multi-file'
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
                hidden
                disabled={disable}
            />
            {files.length === 0 ? (
                <span className='small-text'>
                    {uploading ? 'Upload Files...' : 'Upload'}
                </span>
            ) : (
                upload != null && (
                    <div className='file-actions'>
                        <button className='upload' disabled={startWait} onClick={() => { upload(); setStartWait(true); }}>
                            {!startWait ? <CloudUpload size={18} /> : startWait === 'done' ? <Check /> : <Loader2 className="spinner" />}
                        </button>
                        <button className='upload' disabled={startWait} onClick={() => setFiles([])}>
                            <Eraser size={18} />
                        </button>
                    </div>
                )
            )}
            {files.length > 0 && (
                <>
                    <label className='file-upload-label small-text'>
                        Selected File(s)...
                    </label>
                    <div className={`file-list subtext ${disable ? 'disabled' : ''}`}>
                        {files.map((file, index) => {
                            let fileName;
                            if (typeof file === 'string') {
                                fileName = file;
                            } else {
                                fileName = file.name || 'Unknown file';
                            }
                            
                            const nameParts = String(fileName).split(".");
                            const extension = nameParts.pop();
                            const baseName = nameParts.join(".");
                            return (
                                <span key={index} className="file-name-ellipsis">
                                    {baseName}.{extension}
                                </span>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export const FolderUpload = ({ value, onChange, upload = null, uploadWaiting }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [startWait, setStartWait] = useState(false);
    const fileInputRef = useRef(null);
    const wrapperRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
        onChange?.(selectedFiles);
        fileInputRef.current.value = "";
    };

    useEffect(() => {
        if (!uploadWaiting && startWait) {
            setFiles([]);
            setStartWait(false);
        }
    }, [uploadWaiting]);

    const handleClick = (e) => {
        if (e.target === e.currentTarget) {
            setUploading(true);
            fileInputRef.current?.click();
        }
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setUploading(false);
            }
        };
    
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className={`file-upload${uploading ? ' expanded' : ''}`} onClick={handleClick} ref={wrapperRef}>
            <input
                type='file'
                multiple
                webkitdirectory="true"
                onChange={handleFileChange}
                ref={fileInputRef}
                hidden
            />
            {files.length === 0 ? (
                <span className='subtext' style={{ color: !uploading ? "var(--secondary-color)" : 'var(--text-color)' }}>
                    {uploading ? 'Select Folder...' : 'Upload Folder'}
                </span>
            ) : (
                upload != null && (
                    <div className='file-actions'>
                        <button className='upload' disabled={startWait} onClick={() => { upload(); setStartWait(true); }}>
                            {!startWait ? <CloudUpload size={18} /> : startWait === 'done' ? <Check /> : <Loader2 className="spinner" />}
                        </button>
                        <button className='upload' disabled={startWait} onClick={() => setFiles([])}>
                            <Eraser size={18} />
                        </button>
                    </div>
                )
            )}
            {files.length > 0 && (
                <div className='file-list subtext'>
                    {files.slice(0, 5).map((file, index) => (
                        <span key={index}>{file.webkitRelativePath}</span>
                    ))}
                    {files.length > 5 && <span>+ {files.length - 5} more</span>}
                </div>
            )}
        </div>
    );
};

export const Calculation = ({
    options,
    fieldUpdates = {},
    formData = {},
    fields = [],
    fieldId,
    onChange,
    groupId = null,
    groupedData = [],
    addItemMode = false,
    handleFieldChange = null
}) => {
    const [result, setResult] = useState(0);
    const [tooltipContent, setTooltipContent] = useState({ display: '', formula: '', fieldMappings: [] });
    const [showTooltip, setShowTooltip] = useState(false);
    const [isTooltipSticky, setIsTooltipSticky] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const tooltipRef = useRef(null);
    const lastResultRef = useRef(null);

    const getCalculationDateConfig = useCallback((config) => {
        if (!isDateCalculationConfig(config)) return null;

        const dateConfig = config.date || config.date_calculation || config.dateCalculation || config;
        const sourceFieldId = dateConfig.source_field_id
            ?? dateConfig.sourceFieldId
            ?? dateConfig.date_field_id
            ?? dateConfig.dateFieldId
            ?? dateConfig.field_id
            ?? dateConfig.fieldId
            ?? (Array.isArray(config.field_ids) ? config.field_ids[0] : null);

        if (!sourceFieldId) return null;

        const direction = String(dateConfig.direction || dateConfig.operation || dateConfig.operator || 'before').toLowerCase();
        const rawAmount = dateConfig.amount ?? dateConfig.value ?? dateConfig.offset ?? dateConfig.months ?? 0;
        const parsedAmount = Number(rawAmount);
        const signedAmount = Number.isNaN(parsedAmount)
            ? 0
            : (['before', 'subtract', 'minus', '-'].includes(direction) ? -Math.abs(parsedAmount) : parsedAmount);

        return {
            sourceFieldId: Number(sourceFieldId),
            amount: signedAmount,
            unit: String(dateConfig.unit || dateConfig.units || (dateConfig.months !== undefined ? 'months' : 'months')).toLowerCase(),
            displayFormat: dateConfig.display_format || dateConfig.displayFormat || dateConfig.format || 'MM/DD/YYYY',
        };
    }, []);

    const normalizeValueToIndex = useCallback((field, value) => {
        if (!field || !field.options) return value;

        let optionsList = [];
        try {
            optionsList = Array.isArray(field.options)
                ? field.options
                : JSON.parse(field.options);
        } catch {
            return value;
        }

        if (typeof value === "string") {
            const index = optionsList.findIndex(opt => opt.toLowerCase?.() === value.toLowerCase?.());
            return index !== -1 ? index : value;
        }

        return value;
    }, []);

    const getRawFieldValue = useCallback((targetFieldId) => {
        // For new items (addItemMode), only use current formData
        if (addItemMode) {
            if (Object.prototype.hasOwnProperty.call(formData || {}, targetFieldId)) {
                return formData[targetFieldId];
            }
            return null;
        }

        // For existing items, only use data from the same group - NEVER fall back to fieldUpdates from other groups
        if (groupId !== null && groupId !== undefined) {
            if (Object.prototype.hasOwnProperty.call(formData || {}, targetFieldId)) {
                return formData[targetFieldId];
            }

            const currentGroup = groupedData.find(group => group.group_id === groupId);
            if (currentGroup && Object.prototype.hasOwnProperty.call(currentGroup, targetFieldId)) {
                return currentGroup[targetFieldId];
            }

            return null;
        }

        // Standard mode for non-grouped fields
        if (Object.prototype.hasOwnProperty.call(formData || {}, targetFieldId)) {
            return formData[targetFieldId];
        }

        if (Object.prototype.hasOwnProperty.call(fieldUpdates || {}, targetFieldId)) {
            return fieldUpdates[targetFieldId];
        }

        return null;
    }, [formData, fieldUpdates, groupId, groupedData, addItemMode]);

    const calculateDateResult = useCallback((dateConfig) => {
        if (!dateConfig) return '';

        const sourceValue = getRawFieldValue(dateConfig.sourceFieldId);
        const sourceDate = parseLocalDateValue(sourceValue);
        if (!sourceDate) return '';

        const amount = dateConfig.amount;
        const calculatedDate = new Date(sourceDate.getFullYear(), sourceDate.getMonth(), sourceDate.getDate());

        if (dateConfig.unit.startsWith('year')) {
            return formatIsoLocalDate(addMonthsClamped(calculatedDate, amount * 12));
        }

        if (dateConfig.unit.startsWith('month')) {
            return formatIsoLocalDate(addMonthsClamped(calculatedDate, amount));
        }

        const days = dateConfig.unit.startsWith('week') ? amount * 7 : amount;
        calculatedDate.setDate(calculatedDate.getDate() + days);
        return formatIsoLocalDate(calculatedDate);
    }, [getRawFieldValue]);

    const parseDisplayCriteria = useCallback((field) => {
        const displayWhen = field.display_multi || field.display_when;
        let isAnswered = field.is_answered;

        if (!displayWhen) return null;

        if (typeof isAnswered === 'string') {
            try {
                isAnswered = JSON.parse(isAnswered);
            } catch {
                // Keep as string if parse fails
            }
        }

        let parsed = null;
        try {
            parsed = JSON.parse(String(displayWhen));
        } catch {
            parsed = displayWhen;
        }

        if (Array.isArray(parsed)) {
            const isAnsweredArray = Array.isArray(isAnswered) ? isAnswered : [isAnswered];

            return parsed.map((fieldId, index) => ({
                fieldId: fieldId,
                condition: (isAnsweredArray[index] === 0 || isAnsweredArray[index] === '0')
                    ? '0'
                    : (isAnsweredArray[index] ?? '')
            }));
        }

        if (typeof parsed === 'number' || (typeof parsed === 'string' && !isNaN(parsed))) {
            const fieldId = typeof parsed === 'number' ? parsed : parseInt(parsed, 10);
            const isAnsweredVal = Array.isArray(isAnswered)
                ? JSON.stringify(isAnswered)
                : ((isAnswered === 0 || isAnswered === '0') ? '0' : (isAnswered ?? ''));
            return [{
                fieldId,
                condition: isAnsweredVal
            }];
        }

        return null;
    }, []);

    const normalizeComparableValue = useCallback((val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'object') {
            if (val.id !== undefined) return String(val.id);
            if (val.value !== undefined) return String(val.value);
            if (val.name !== undefined) return String(val.name);
        }
        return String(val);
    }, []);

    const getFieldOptions = useCallback((field) => {
        if (!field?.options) return [];
        try {
            return Array.isArray(field.options) ? field.options : JSON.parse(field.options);
        } catch {
            return [];
        }
    }, []);

    const valueMatchesCondition = useCallback((controllingField, controllingValue, cleanAnswers) => {
        if (!cleanAnswers.length) return false;

        const normalizedValue = normalizeComparableValue(controllingValue);
        const valueSet = Array.isArray(controllingValue)
            ? controllingValue.map(normalizeComparableValue)
            : [normalizedValue];

        const options = getFieldOptions(controllingField);
        const optionsByIndex = options.map((opt) => {
            if (opt && typeof opt === 'object') {
                const id = opt.id ?? opt.value ?? opt.name;
                const name = opt.name ?? opt.label ?? opt.value ?? opt.id;
                const value = opt.value ?? opt.id ?? opt.name;
                return {
                    id: id !== undefined ? String(id) : '',
                    name: name !== undefined ? String(name) : '',
                    value: value !== undefined ? String(value) : ''
                };
            }
            const str = String(opt);
            return { id: str, name: str, value: str };
        });

        return cleanAnswers.some((answer) => {
            const answerStr = String(answer);

            if (valueSet.includes(answerStr)) return true;

            const index = parseInt(answerStr, 10);
            if (!Number.isNaN(index) && optionsByIndex[index]) {
                const option = optionsByIndex[index];
                if (valueSet.includes(option.id) || valueSet.includes(option.value) || valueSet.includes(option.name)) {
                    return true;
                }
            }

            if (optionsByIndex.length > 0) {
                return optionsByIndex.some(option =>
                    answerStr === option.id || answerStr === option.value || answerStr === option.name
                ) && valueSet.some(val =>
                    optionsByIndex.some(option => val === option.id || val === option.value || val === option.name)
                );
            }

            return false;
        });
    }, [getFieldOptions, normalizeComparableValue]);

    const evaluateCriterion = useCallback((fieldId, condition, controllingField) => {
        if (!controllingField) return false;

        let controllingValue = getRawFieldValue(fieldId);

        if (condition === '[!]') {
            return controllingValue === null || controllingValue === undefined || controllingValue === "";
        }

        if (condition === "" || condition === null) {
            return controllingValue !== null && controllingValue !== undefined && controllingValue !== "";
        }

        if (controllingField.field_id === 11) {
            const selectedValues = Array.isArray(controllingValue) ? controllingValue : [];

            let multiselectOptions = [];
            try {
                const rawOptions = Array.isArray(controllingField.options)
                    ? controllingField.options
                    : controllingField.options
                        ? JSON.parse(controllingField.options)
                        : [];

                multiselectOptions = rawOptions.map(opt =>
                    typeof opt === 'object' && opt !== null
                        ? opt
                        : { id: opt, name: String(opt) }
                );
            } catch {
                multiselectOptions = [];
            }

            let parsedAnswers = [];
            try {
                const parsed = JSON.parse(condition);
                if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
                else parsedAnswers = [String(parsed)];
            } catch {
                parsedAnswers = String(condition).split(',').map(v => v.trim());
            }

            const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
            const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

            const targetOptionIds = cleanAnswers.map(indexStr => {
                const index = parseInt(indexStr, 10);
                return multiselectOptions[index]?.id;
            }).filter(id => id !== undefined);

            if (isNegated) {
                return !targetOptionIds.some(optionId => selectedValues.includes(optionId));
            }

            return targetOptionIds.some(optionId => selectedValues.includes(optionId));
        }

        controllingValue = normalizeValueToIndex(controllingField, controllingValue);

        let parsedAnswers = [];
        try {
            const parsed = JSON.parse(condition);
            if (Array.isArray(parsed)) parsedAnswers = parsed.map(v => String(v));
            else parsedAnswers = [String(parsed)];
        } catch {
            parsedAnswers = String(condition).split(',').map(v => v.trim());
        }

        const isNegated = parsedAnswers.every(answer => String(answer).startsWith('!'));
        const cleanAnswers = parsedAnswers.map((v) => String(v).replace(/^!/, ''));

        const isListField = [10, 12].includes(controllingField.field_id);
        if (isListField) {
            const options = getFieldOptions(controllingField);
            let controllingIndex = null;

            if (typeof controllingValue === 'number' || (typeof controllingValue === 'string' && controllingValue.trim() !== '' && !isNaN(controllingValue))) {
                controllingIndex = parseInt(controllingValue, 10);
            } else if (typeof controllingValue === 'string') {
                const matchIndex = options.findIndex(opt => String(opt).toLowerCase?.() === controllingValue.toLowerCase?.());
                controllingIndex = matchIndex >= 0 ? matchIndex : null;
            }

            const answerIndices = cleanAnswers
                .map((answer) => parseInt(answer, 10))
                .filter((num) => !Number.isNaN(num));

            if (controllingIndex !== null && answerIndices.length > 0) {
                const matchesByIndex = answerIndices.includes(controllingIndex);
                return isNegated ? !matchesByIndex : matchesByIndex;
            }
        }

        const matches = valueMatchesCondition(controllingField, controllingValue, cleanAnswers);
        return isNegated ? !matches : matches;
    }, [getRawFieldValue, getFieldOptions, normalizeValueToIndex, valueMatchesCondition]);

    const shouldDisplayField = useCallback((field) => {
        if (!field) return false;

        if (field.add_item === 1 && !addItemMode) return false;

        if (!field.display_when && !field.display_multi) return true;

        const arrayParsed = parseDisplayCriteria(field);
        if (arrayParsed && arrayParsed.length > 0) {
            const results = arrayParsed.map(criterion => {
                const controllingField = fields.find(f => Number(f.id) === Number(criterion.fieldId));
                return evaluateCriterion(criterion.fieldId, criterion.condition, controllingField);
            });

            return results.every(result => result === true);
        }

        const criteriaStr = field.multi_criteria ? String(field.multi_criteria) : String(field.display_when || field.display_multi || '');
        const hasAndOperator = criteriaStr.includes('&&');
        const hasOrOperator = criteriaStr.includes('||');

        if (hasAndOperator || hasOrOperator) {
            const operator = hasAndOperator ? '&&' : '||';
            const fieldIds = criteriaStr.split(operator).map(id => parseInt(id.trim(), 10));
            const hasAnswered = field.is_answered !== null && field.is_answered !== undefined;
            const conditions = hasAnswered
                ? (Array.isArray(field.is_answered) ? field.is_answered : String(field.is_answered).split(operator))
                : [];

            const results = fieldIds.map((fieldId, index) => {
                const controllingField = fields.find(f => Number(f.id) === Number(fieldId));
                const condition = Array.isArray(conditions) ? (conditions[index] ? String(conditions[index]).trim() : '') : '';
                return evaluateCriterion(fieldId, condition, controllingField);
            });

            return operator === '&&'
                ? results.every(result => result === true)
                : results.some(result => result === true);
        }

        let fieldIdToCheck = null;
        try {
            const parsed = JSON.parse(criteriaStr);
            fieldIdToCheck = parseInt(parsed, 10);
        } catch {
            fieldIdToCheck = parseInt(field.display_when || field.display_multi, 10);
        }

        const controllingField = fields.find(f => Number(f.id) === Number(fieldIdToCheck));
        if (!controllingField) return false;

        const condition = Array.isArray(field.is_answered)
            ? ((field.is_answered[0] === 0 || field.is_answered[0] === '0') ? '0' : (field.is_answered[0] ?? ''))
            : ((field.is_answered === 0 || field.is_answered === '0') ? '0' : (field.is_answered ?? ''));

        return evaluateCriterion(fieldIdToCheck, condition, controllingField);
    }, [addItemMode, evaluateCriterion, fields, parseDisplayCriteria]);

    const resolveRuleConfig = useCallback(() => {
        if (!options) return null;

        const parsed = typeof options === 'string' ? JSON.parse(options) : options;
        if (!parsed) return null;

        if (Array.isArray(parsed.rules)) {
            for (const ruleConfig of parsed.rules) {
                const when = ruleConfig?.when;

                if (when?.displayFieldId) {
                    const displayField = fields.find(f => f.id === Number(when.displayFieldId));
                    if (displayField && shouldDisplayField(displayField)) {
                        return ruleConfig;
                    }
                    continue;
                }

                if (when?.fieldId) {
                    const controllingField = fields.find(f => f.id === Number(when.fieldId));
                    if (!controllingField) continue;

                    const controllingValue = getRawFieldValue(when.fieldId);
                    const condition = when.condition ?? '';
                    if (evaluateCriterion(when.fieldId, condition, controllingField)) {
                        return ruleConfig;
                    }
                    continue;
                }

                if (!when || when.default === true) {
                    return ruleConfig;
                }
            }

            if (parsed.fallback) {
                return parsed.fallback;
            }
        }

        return parsed;
    }, [options, fields, shouldDisplayField, getRawFieldValue, evaluateCriterion]);

    const handleMouseMove = useCallback((e) => {
        // Don't update position if tooltip is sticky
        if (isTooltipSticky) return;

        const offsetY = 10;
        const tooltipEl = tooltipRef.current;
        const tooltipWidth = tooltipEl?.offsetWidth || 200;
        const tooltipHeight = tooltipEl?.offsetHeight || 60;

        let x = e.pageX - (tooltipWidth / 2);
        let y = e.pageY - tooltipHeight - offsetY;

        const pageWidth = window.innerWidth;
        const margin = 10; // 10px margin from screen edges

        // Keep tooltip 10px away from left edge
        if (x < margin) x = margin;
        // Keep tooltip 10px away from right edge
        if (x + tooltipWidth > pageWidth - margin) x = pageWidth - tooltipWidth - margin;
        // Keep tooltip 10px away from top edge
        if (y < margin) y = margin;

        setMousePosition({ x, y });
    }, [isTooltipSticky]);

    const getFieldValue = useCallback((targetFieldId) => {
        // Debug logging to understand the context
        if (fieldId === 134) {
            console.log(`getFieldValue called for field ${targetFieldId}`);
            console.log(`Current context: groupId=${groupId}, addItemMode=${addItemMode}`);
            console.log(`FormData:`, formData);
            console.log(`FieldUpdates:`, fieldUpdates);
            console.log(`GroupedData:`, groupedData);
        }

        // For new items (addItemMode), only use current formData
        if (addItemMode) {
            console.log(`ADD_ITEM_MODE: Looking for field ${targetFieldId} in formData`);
            if (formData[targetFieldId] !== undefined && formData[targetFieldId] !== null) {
                let val = formData[targetFieldId];
                
                // Handle empty string as 0
                if (val === '') {
                    console.log(`ADD_ITEM_MODE: Found empty string for field ${targetFieldId}, returning 0`);
                    return 0;
                }
                
                // Only prevent self-reference (when a field references itself)
                if (val === targetFieldId || val === String(targetFieldId)) {
                    console.log(`Self-reference detected for field ${targetFieldId}, returning 0`);
                    val = 0;
                }
                console.log(`ADD_ITEM_MODE: Returning ${val} for field ${targetFieldId}`);
                return parseFloat(val) || 0;
            }
            console.log(`ADD_ITEM_MODE: No value found for field ${targetFieldId}, returning 0`);
            return 0;
        }

        // For existing items, only use data from the same group - NEVER fall back to fieldUpdates from other groups
        if (groupId !== null && groupId !== undefined) {
            console.log(`GROUPED_MODE: Looking for field ${targetFieldId} in group ${groupId}`);
            
            // First check formData for unsaved changes in the current group (prioritize new data)
            if (formData[targetFieldId] !== undefined && formData[targetFieldId] !== null) {
                let val = formData[targetFieldId];
                
                // Handle empty string as 0 but still prioritize it over saved group data
                if (val === '') {
                    console.log(`GROUPED_MODE: Found empty string in formData for field ${targetFieldId}, returning 0`);
                    return 0;
                }
                
                // Only prevent self-reference (when a field references itself)
                if (val === targetFieldId || val === String(targetFieldId)) {
                    console.log(`Self-reference detected in formData for field ${targetFieldId}, returning 0`);
                    val = 0;
                }
                console.log(`GROUPED_MODE: Found in formData: ${val} for field ${targetFieldId}`);
                return parseFloat(val) || 0;
            }

            // Then check saved data for the specific group
            const currentGroup = groupedData.find(group => group.group_id === groupId);
            console.log(`Current group data:`, currentGroup);
            
            if (currentGroup && currentGroup[targetFieldId] !== undefined && currentGroup[targetFieldId] !== null) {
                let val = currentGroup[targetFieldId];
                // Handle empty string as 0
                if (val === '') {
                    console.log(`GROUPED_MODE: Found empty string in group data for field ${targetFieldId}, returning 0`);
                    return 0;
                }
                // Only prevent self-reference (when a field references itself)
                if (val === targetFieldId || val === String(targetFieldId)) {
                    console.log(`Self-reference detected in group data for field ${targetFieldId}, returning 0`);
                    val = 0;
                }
                console.log(`GROUPED_MODE: Found in group data: ${val} for field ${targetFieldId}`);
                return parseFloat(val) || 0;
            }

            console.log(`GROUPED_MODE: No value found for field ${targetFieldId} in group ${groupId}, returning 0`);
            // CRITICAL: When in grouped mode, NEVER fall back to fieldUpdates from other groups
            return 0;
        }

        // Standard mode for non-grouped fields (regular calculation fields not in add_item sections)
        console.log(`STANDARD_MODE: Looking for field ${targetFieldId} in standard data sources`);
        
        // First check formData for any unsaved changes (prioritize new data even if empty string)
        if (formData[targetFieldId] !== undefined && formData[targetFieldId] !== null) {
            let val = formData[targetFieldId];
            
            // Handle empty string as 0 but still prioritize it over saved data
            if (val === '') {
                console.log(`STANDARD_MODE: Found empty string in formData for field ${targetFieldId}, returning 0`);
                return 0;
            }
            
            // Only prevent self-reference (when a field references itself)
            if (val === targetFieldId || val === String(targetFieldId)) {
                console.log(`Self-reference detected in standard formData for field ${targetFieldId}, returning 0`);
                val = 0;
            }
            console.log(`STANDARD_MODE: Found in formData: ${val} for field ${targetFieldId}`);
            return parseFloat(val) || 0;
        }

        // Then check fieldUpdates for saved values
        if (fieldUpdates[targetFieldId] !== undefined && fieldUpdates[targetFieldId] !== null && fieldUpdates[targetFieldId] !== '') {
            let val = fieldUpdates[targetFieldId];
            // Only prevent self-reference (when a field references itself)
            if (val === targetFieldId || val === String(targetFieldId)) {
                console.log(`Self-reference detected in standard fieldUpdates for field ${targetFieldId}, returning 0`);
                val = 0;
            }
            console.log(`STANDARD_MODE: Found in fieldUpdates: ${val} for field ${targetFieldId}`);
            return parseFloat(val) || 0;
        }

        console.log(`STANDARD_MODE: No value found for field ${targetFieldId}, returning 0`);
        return 0;
    }, [formData, fieldUpdates, groupId, groupedData, addItemMode, fieldId]);

    const handleHover = useCallback(() => {
        if (!options || !fieldId) return;

        const resolvedConfig = resolveRuleConfig();
        const dateConfig = getCalculationDateConfig(resolvedConfig);
        if (dateConfig) {
            const sourceValue = getRawFieldValue(dateConfig.sourceFieldId);
            const resultValue = calculateDateResult(dateConfig);
            const sourceField = fields.find(f => Number(f.id) === Number(dateConfig.sourceFieldId));
            const amountLabel = `${Math.abs(dateConfig.amount)} ${dateConfig.unit}`;
            const directionLabel = dateConfig.amount < 0 ? 'before' : 'after';

            setTooltipContent({
                display: resultValue ? formatCalculationDateForDisplay(resultValue, dateConfig.displayFormat) : '',
                formula: `${amountLabel} ${directionLabel} ${sourceField?.name || `Field ${dateConfig.sourceFieldId}`}`,
                fieldMappings: [{
                    letter: 'Date',
                    name: sourceField?.name || `Field ${dateConfig.sourceFieldId}`,
                    value: formatCalculationDateForDisplay(sourceValue, dateConfig.displayFormat),
                    fieldId: dateConfig.sourceFieldId
                }]
            });
            setShowTooltip(true);
            document.addEventListener('mousemove', handleMouseMove);
            return;
        }

        const rule = resolvedConfig?.rule || '';
        const fieldIds = Array.isArray(resolvedConfig?.field_ids)
            ? resolvedConfig.field_ids
            : JSON.parse(resolvedConfig?.field_ids || '[]');

        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let displayExpr = rule;
        let formula = rule;
        let fieldMappings = [];

        fieldIds.forEach((id, idx) => {
            const letter = letters[idx];
            const val = getFieldValue(id);
            
            const fieldDef = fields.find(f => f.id === id);
            const displayVal = fieldDef?.field_id === 4 ? val / 100 : val;
            
            displayExpr = displayExpr.replaceAll(letter, displayVal);
            
            // Add field mapping for tooltip
            fieldMappings.push({
                letter: letter,
                name: fieldDef?.name || `Field ${id}`,
                value: displayVal,
                fieldId: id
            });
        });

        setTooltipContent({ 
            display: displayExpr, 
            formula,
            fieldMappings 
        });
        setShowTooltip(true);
        document.addEventListener('mousemove', handleMouseMove);
    }, [options, fieldId, getCalculationDateConfig, getRawFieldValue, calculateDateResult, fields, handleMouseMove, resolveRuleConfig, getFieldValue]);

    const handleExit = useCallback(() => {
        if (!isTooltipSticky) {
            setShowTooltip(false);
            document.removeEventListener('mousemove', handleMouseMove);
        }
    }, [handleMouseMove, isTooltipSticky]);

    const ensureTooltipInBounds = useCallback(() => {
        const tooltipEl = tooltipRef.current;
        if (!tooltipEl) return;

        const tooltipRect = tooltipEl.getBoundingClientRect();
        const pageWidth = window.innerWidth;
        const pageHeight = window.innerHeight;
        const margin = 10; // 10px margin from screen edges

        let x = mousePosition.x;
        let y = mousePosition.y;

        // Keep tooltip 10px away from left edge
        if (x < margin) x = margin;
        // Keep tooltip 10px away from right edge
        if (x + tooltipRect.width > pageWidth - margin) x = pageWidth - tooltipRect.width - margin;
        // Keep tooltip 10px away from top edge
        if (y < margin) y = margin;
        // Keep tooltip 10px away from bottom edge
        if (y + tooltipRect.height > pageHeight - margin) y = pageHeight - tooltipRect.height - margin;

        // Only update if position changed
        if (x !== mousePosition.x || y !== mousePosition.y) {
            setMousePosition({ x, y });
        }
    }, [mousePosition]);

    const handleIconClick = useCallback((e) => {
        e.stopPropagation();
        if (isTooltipSticky) {
            // Close sticky tooltip
            setIsTooltipSticky(false);
            setShowTooltip(false);
            document.removeEventListener('mousemove', handleMouseMove);
        } else {
            // Make tooltip sticky
            handleHover();
            
            // Position tooltip relative to the icon instead of mouse
            const iconRect = e.currentTarget.getBoundingClientRect();
            const tooltipWidth = 250; // estimated width
            const tooltipHeight = 80; // estimated height
            const margin = 10;
            
            let x = iconRect.left + (iconRect.width / 2) - (tooltipWidth / 2);
            let y = iconRect.top - tooltipHeight - margin;
            
            // Keep within screen bounds
            const pageWidth = window.innerWidth;
            const pageHeight = window.innerHeight;
            
            if (x < margin) x = margin;
            if (x + tooltipWidth > pageWidth - margin) x = pageWidth - tooltipWidth - margin;
            if (y < margin) y = margin;
            if (y + tooltipHeight > pageHeight - margin) y = pageHeight - tooltipHeight - margin;
            
            setMousePosition({ x, y });
            setIsTooltipSticky(true);
            
            // Remove mouse move listener when sticky
            document.removeEventListener('mousemove', handleMouseMove);
            
            // Ensure final positioning after render
            setTimeout(ensureTooltipInBounds, 10);
        }
    }, [isTooltipSticky, handleHover, handleMouseMove, ensureTooltipInBounds]);

    const handleFieldValueClick = useCallback(async (fieldMapping, e) => {
        e.stopPropagation();
        
        try {
            // Clear the specific field that was clicked
            if (handleFieldChange && typeof handleFieldChange === 'function') {
                handleFieldChange(fieldMapping.fieldId, '');
                
                // Close the tooltip after successful action
                setIsTooltipSticky(false);
                setShowTooltip(false);
                document.removeEventListener('mousemove', handleMouseMove);
            } else {
                console.error('handleFieldChange not available for field clearing');
            }
        } catch (error) {
            console.error('Error clearing field:', error);
        }
    }, [handleFieldChange, handleMouseMove]);

    const calculatedResult = useMemo(() => {
        if (!options || !fieldId) {
            return 0;
        }

        try {
            const resolvedConfig = resolveRuleConfig();
            const dateConfig = getCalculationDateConfig(resolvedConfig);
            if (dateConfig) {
                return calculateDateResult(dateConfig);
            }

            const rule = resolvedConfig?.rule;
            const fieldIds = Array.isArray(resolvedConfig?.field_ids)
                ? resolvedConfig.field_ids
                : JSON.parse(resolvedConfig?.field_ids || '[]');

            if (!rule || !Array.isArray(fieldIds)) {
                return 0;
            }

            const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
            let expression = rule;

            fieldIds.forEach((id, idx) => {
                const letter = letters[idx];
                let val = getFieldValue(id);

                // Debug logging for the calculation issue
                if (fieldId === 134) {
                    console.log(`Calculation field 134 - Field ${id} (${letter}): value =`, val);
                    if (groupId !== null) {
                        const currentGroup = groupedData.find(group => group.group_id === groupId);
                        console.log(`Group ${groupId} data for field ${id}:`, currentGroup?.[id]);
                        console.log(`FormData for field ${id}:`, formData[id]);
                    }
                }

                const fieldDef = fields.find(f => f.id === id);
                if (fieldDef?.field_id === 4) {
                    val = val / 100;
                }

                expression = expression.replaceAll(letter, val);
            });

            if (/[^0-9+\-*/(). ]/.test(expression) || /[+\-*/]$/.test(expression)) {
                console.warn(`Invalid expression: "${expression}"`);
                return 0;
            }

            const calculated = new Function(`return ${expression}`)();
            return isNaN(calculated) ? 0 : parseFloat(calculated.toFixed(2));
        } catch (e) {
            console.error('Calculation error:', e);
            return 0;
        }
    }, [options, fieldId, getCalculationDateConfig, calculateDateResult, getFieldValue, fields, resolveRuleConfig]);

    const displayResult = useMemo(() => {
        const resolvedConfig = resolveRuleConfig();
        const dateConfig = getCalculationDateConfig(resolvedConfig);

        return dateConfig
            ? formatCalculationDateForDisplay(result, dateConfig.displayFormat)
            : result;
    }, [result, resolveRuleConfig, getCalculationDateConfig]);

    useEffect(() => {
        if (calculatedResult !== lastResultRef.current) {
            setResult(calculatedResult);
            lastResultRef.current = calculatedResult;
            
            if (onChange && typeof onChange === 'function') {
                onChange(calculatedResult);
            }
        }
    }, [calculatedResult, onChange]);

    // Ensure sticky tooltip stays within bounds when dimensions change
    useEffect(() => {
        if (isTooltipSticky && showTooltip && tooltipRef.current) {
            // Use a small delay to ensure tooltip is rendered with final dimensions
            const timeoutId = setTimeout(ensureTooltipInBounds, 10);
            return () => clearTimeout(timeoutId);
        }
    }, [isTooltipSticky, showTooltip, tooltipContent, ensureTooltipInBounds]);

    // Handle click outside to close sticky tooltip
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isTooltipSticky && tooltipRef.current && !tooltipRef.current.contains(e.target)) {
                // Check if the click is on the calculate icon
                const calcIcon = e.target.closest('.number-symbol');
                if (!calcIcon) {
                    setIsTooltipSticky(false);
                    setShowTooltip(false);
                    document.removeEventListener('mousemove', handleMouseMove);
                }
            }
        };

        if (isTooltipSticky) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [handleMouseMove, isTooltipSticky]);

    return (
        <>
            <div className='number-input'>
                <input type='text' disabled className='calculation' value={displayResult}/>
                <span
                    className='number-symbol subtext'
                    onMouseEnter={handleHover}
                    onMouseLeave={handleExit}
                    onClick={handleIconClick}
                    style={{ cursor: 'pointer' }}
                    title="Hover to preview calculation, click to pin tooltip"
                >
                    <Calculate size={18}/>
                </span>
            </div>

            {showTooltip && (
                <div
                    ref={tooltipRef}
                    className='tooltip-calculation'
                    style={{
                        position: 'fixed',
                        left: `${mousePosition.x}px`,
                        top: `${mousePosition.y}px`,
                        background: 'rgba(0, 0, 0, 0.9)',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        pointerEvents: isTooltipSticky ? 'auto' : 'none',
                        zIndex: 9999,
                        width: 'max-content',
                        maxWidth: '300px',
                        lineHeight: '1.4',
                        whiteSpace: 'normal',
                    }}
                >
                    <strong>Result: {tooltipContent.display}</strong>
                    <div style={{ marginTop: 4, fontStyle: 'italic', opacity: 0.8 }}>
                        Formula: {tooltipContent.formula}
                    </div>
                    {tooltipContent.fieldMappings && tooltipContent.fieldMappings.length > 0 && (
                        <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: 6 }}>
                            <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: 4 }}>
                                Field Values {isTooltipSticky ? '(click to clear)' : ''}:
                            </div>
                            {tooltipContent.fieldMappings.map((mapping, index) => (
                                <div 
                                    key={index} 
                                    style={{ 
                                        fontSize: '11px', 
                                        marginBottom: 2,
                                        cursor: isTooltipSticky ? 'pointer' : 'default',
                                        padding: isTooltipSticky ? '2px 4px' : '0',
                                        borderRadius: isTooltipSticky ? '3px' : '0',
                                        backgroundColor: isTooltipSticky ? 'rgba(255,255,255,0.1)' : 'transparent',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    onClick={isTooltipSticky ? (e) => handleFieldValueClick(mapping, e) : undefined}
                                    onMouseEnter={isTooltipSticky ? (e) => {
                                        e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                                    } : undefined}
                                    onMouseLeave={isTooltipSticky ? (e) => {
                                        e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                                    } : undefined}
                                >
                                    <span style={{ fontWeight: 'bold', color: '#fbbf24' }}>{mapping.letter}</span> - {mapping.name}: <span style={{ color: '#86efac' }}>{mapping.value}</span>
                                    {isTooltipSticky && (
                                        <span style={{ color: '#ef4444', marginLeft: '6px', fontSize: '10px' }}>✕</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export const Contact = ({ selectedContact = '', onCreateNewContact, setSelectedContact = () => {}, onCreateNewLead, lead = false, users = null, disabled = null, onEditContact = null, showDropdownMenu = false, onRemoveContact = null, noNewContacts = null }) => {    
    const [searchTerm, setSearchTerm] = useState(() => {
        if (typeof selectedContact === 'object' && selectedContact) {
            return selectedContact.full_name || selectedContact.contact_name || selectedContact.name || "";
        }
        return "";
    });
    const [searchResults, setSearchResults] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [displayContact, setDisplayContact] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0 });

    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);
    const actionMenuRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const currentSearchRef = useRef(null); // Track the current search query

    useEffect(() => {
        // Clear any pending search timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (users && searchTerm.trim().length >= 2) {
            const searchLower = searchTerm.toLowerCase();
            const filtered = users?.filter(u => u.name.toLowerCase().includes(searchLower));
            setSearchResults(filtered.slice(0, 25));
            setIsDropdownOpen(true);
            positionDropdown();
        } else if (!users && searchTerm.trim().length >= 2) {
            // Set a 300ms debounce timeout for API searches
            searchTimeoutRef.current = setTimeout(() => {
                currentSearchRef.current = searchTerm; // Track what we're searching for
                lead ? fetchLeads(searchTerm) : fetchContacts(searchTerm);
            }, 300);
        } else {
            setSearchResults([]);
            setIsDropdownOpen(false);
        }

        // Cleanup timeout on unmount or when searchTerm changes
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, lead, users]);

    const fetchContacts = async (query) => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?search=${encodeURIComponent(query)}&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            
            // Only update results if this search is still the current one
            if (currentSearchRef.current === query) {
                if (data.success) {
                    setSearchResults(data.contacts.slice(0, 25));
                    setIsDropdownOpen(true);
                    positionDropdown();
                } else {
                    setSearchResults([]);
                    setIsDropdownOpen(false);
                }
            }
        } catch (error) {
            console.error("Error fetching contacts:", error);
            // Only clear results if this was the current search
            if (currentSearchRef.current === query) {
                setSearchResults([]);
                setIsDropdownOpen(false);
            }
        }
    };

    const fetchLeads = async (query) => {
        try {
            const response = await fetch(`https://api.casedb.co/leads.php?search=${encodeURIComponent(query)}&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            
            // Only update results if this search is still the current one
            if (currentSearchRef.current === query) {
                if (data.success) {
                    setSearchResults(data.leads.slice(0, 25));
                    setIsDropdownOpen(true);
                    positionDropdown();
                } else {
                    setSearchResults([]);
                    setIsDropdownOpen(false);
                }
            }
        } catch (error) {
            console.error("Error fetching leads:", error);
            // Only clear results if this was the current search
            if (currentSearchRef.current === query) {
                setSearchResults([]);
                setIsDropdownOpen(false);
            }
        }
    };

    const fetchLead = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/leads.php?id=${selectedContact?.id || selectedContact}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setDisplayContact(data.lead.contact);
            } else {
                setDisplayContact(null);
            }
        } catch (error) {
            console.error("Error fetching lead:", error);
            setDisplayContact(null);
        }
    }

    const handleSelectItem = (item) => {
        const name = item.full_name || item.contact_name || item.first_name || item.name || "";
        setSearchTerm(name);
        setSearchResults([]);
        setIsDropdownOpen(false);
        
        if (typeof setSelectedContact === 'function') {
            setSelectedContact(item);
        }
        setDisplayContact(item);
    };

    const positionDropdown = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const position = {
                top: rect.bottom + window.scrollY + 2,
                left: rect.left + window.scrollX,
                width: rect.width
            };
            setDropdownPosition(position);
        } else {
            console.log('inputRef.current is null, cannot position dropdown'); // Debug log
        }
    };

    useEffect(() => {
        if (selectedContact && !users && !lead){
            if (typeof selectedContact === 'object' && selectedContact.id) {
                // If it's a complete contact object, use it directly
                setDisplayContact(selectedContact);
                const name = selectedContact.full_name || selectedContact.contact_name || selectedContact.name || "";
                setSearchTerm(name);
            } else {
                // If it's just an ID, fetch the contact
                console.log(selectedContact, "test");
                fetchContact(selectedContact);
            }
        } else if (selectedContact && !users && lead){
            fetchLead();
        } else if (selectedContact && users?.length > 0) {
            if (typeof selectedContact === 'object') {
                setDisplayContact(selectedContact);
                const name = selectedContact.full_name || selectedContact.contact_name || selectedContact.name || "";
                setSearchTerm(name);
            } else {
                const user = users?.find(u => Number(u?.id) === Number(selectedContact));
                setDisplayContact(user);
                if (user) {
                    setSearchTerm(user?.name || "");
                }
            }
        } else if (!selectedContact) {
            // Clear everything if selectedContact is null/undefined
            setDisplayContact(null);
            setSearchTerm("");
        }
    }, [selectedContact, users, lead]);

    const fetchContact = async (id) => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?id=${id?.id ? id.id : id}`, { credentials: 'include' });
            const data = await response.json();
            console.log('fetchContact received id:', id);
            if (data.success && data.contact) {
                setDisplayContact(data.contact);
            } else if (data.contacts && data.contacts.id) {
                setDisplayContact(data.contacts);
            } else {
                // Fallback to user API if contact not found
                const userResponse = await fetch(`https://api.casedb.co/user.php?id=${id?.id ? id.id : id}`, { credentials: 'include' });
                const userData = await userResponse.json();
                if (userResponse.ok && userData.contacts) {
                    setDisplayContact(userData.contacts);
                }
            }
        } catch (error) {
            console.error("Error fetching contact:", error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            console.log('Click outside detected:', { containerRef: containerRef.current, dropdownRef: dropdownRef.current, target: event.target }); // Debug log
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                console.log('Closing dropdown due to click outside'); // Debug log
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleClear = () => {
        if (typeof setSelectedContact === 'function') {
            setSelectedContact('');
        }
        setSearchTerm('');
        setDisplayContact(null);
        setSearchResults([]);
        setIsDropdownOpen(false);
    };

    const positionActionMenu = (element) => {
        if (element) {
            const rect = element.getBoundingClientRect();
            setActionMenuPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
        }
    };

    const handleXClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (showDropdownMenu && onRemoveContact) {
            positionActionMenu(e.currentTarget);
            setShowActionMenu(true);
        } else {
            handleClear();
        }
    };

    const handleSelectNewContact = () => {
        setShowActionMenu(false);
        setDisplayContact(null);
        setSearchTerm('');
    };

    const handleRemoveContact = () => {
        setShowActionMenu(false);
        if (onRemoveContact) {
            onRemoveContact();
        }
    };

    // Close action menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
            }
        };

        if (showActionMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showActionMenu]);

    return (
        <>
            {displayContact === null || typeof displayContact !== 'object' || !displayContact.id ? (
                <div className='contact-input' style={{ position: "relative" }} ref={containerRef}>
                    <input
                        ref={inputRef}
                        type='text'
                        placeholder={users ? "Name of user" : lead ? "Search for lead..." : "Search or create contact..."}
                        value={searchTerm}
                        onFocus={() => searchResults.length > 0 && setIsDropdownOpen(true)}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        name='contact-input'
                        autoComplete="off"
                    />
                    {!users && !noNewContacts && <div
                        className='form-box alt'
                        title="Create new contact"
                        onClick={() => {
                            if (!lead && onCreateNewContact && typeof onCreateNewContact === 'function') {
                                onCreateNewContact();
                            } else if (lead && onCreateNewLead && typeof onCreateNewLead === 'function') {
                                onCreateNewLead();
                            }
                        }}
                    >
                        <UserRoundPlus size={20} />
                    </div>}
                    {(() => {
                        return isDropdownOpen && searchResults.length > 0 && createPortal(
                        <div
                            ref={dropdownRef}
                            className="search-select-dropdown contact-search-dropdown"
                            style={{
                                position: 'absolute',
                                top: dropdownPosition.top,
                                left: dropdownPosition.left,
                                width: dropdownPosition.width,
                                minWidth: '350px',
                            }}
                        >
                            {searchResults.length > 5 && (
                                <div style={{ 
                                    padding: '8px 12px', 
                                    color: 'var(--subtext-color)', 
                                    fontSize: '12px', 
                                    backgroundColor: 'var(--background-color)'
                                }}>
                                    Showing {Math.min(searchResults.length, 25)} of {searchResults.length} results
                                </div>
                            )}
                            {searchResults.slice(0, 25).map((item) => (
                                <div
                                    key={item.id}
                                    className="search-select-option contact-search-option"
                                    onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent input blur
                                        handleSelectItem(item);
                                    }}
                                    style={{ transition: "background-color 0.3s" }}
                                >
                                    {/* Contact Avatar/Initials */}
                                    {item.profile_picture ? (
                                        <img 
                                            className='contact-initials'
                                            src={`https://api.casedb.co/${item.profile_picture}`} 
                                            alt="Profile" 
                                        />
                                    ) : (
                                        <div className={getContactInitialsClassName(item.id)}>
                                            <span>
                                                {users 
                                                    ? (() => {
                                                        const nameParts = item.name?.trim().split(" ");
                                                        return `${nameParts[0]?.charAt(0)}${nameParts[1]?.charAt(0) || ""}`;
                                                    })()
                                                    : lead
                                                        ? item.contact_name?.charAt(0) || 'L'
                                                        : !Number(item?.is_company) 
                                                            ? `${item?.full_name?.trim().charAt(0)}${item?.last_name ? item?.last_name?.trim().charAt(0) : ""}`
                                                            : item.company_name?.trim().charAt(0)
                                                }
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Contact Info */}
                                    <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                                        <div style={{
                                            fontWeight: '500',
                                            color: 'var(--text-color)',
                                            fontSize: '14px',
                                            whiteSpace: 'wrap',
                                            lineHeight: 1.2
                                        }}>
                                            {users 
                                                ? item.name
                                                : lead 
                                                    ? item.contact_name
                                                    : !Number(item?.is_company) 
                                                        ? `${item.prefix ? `${item.prefix} ` : ''}${item.full_name}${item.suffix ? ` ${item.suffix}` : ''}`
                                                        : item.company_name
                                            }
                                        </div>
                                        
                                        {/* Contact details - email and phone only */}
                                        <div style={{
                                            color: 'var(--subtext-color)',
                                            fontSize: '12px',
                                            whiteSpace: 'wrap',
                                            lineHeight: 1.2
                                        }}>
                                            {users
                                                ? `@${item.user || item.id}`
                                                : lead
                                                    ? `Status: ${item.status_name}`
                                                    : (() => {
                                                        const email = item.emails && item.emails.length > 0 ? item.emails[0].email : null;
                                                        const phone = item.phones && item.phones.length > 0 ? item.phones[0].number : null;
                                                        
                                                        const parts = [];
                                                        if (email) parts.push(email);
                                                        if (phone) parts.push(formatPhoneNumber(phone));
                                                        
                                                        return parts.length > 0 ? parts.join(' • ') : '';
                                                    })()
                                            }
                                        </div>
                                    </div>
                                    
                                    {/* Contact type and score on the right */}
                                    {!users && !lead && (item.contact_type || item.search_score) && (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            justifyContent: item.contact_type ? 'space-between' : 'flex-end',
                                            height: '100%',
                                            minHeight: '40px',
                                            maxWidth: '65px',
                                            flexShrink: 0
                                        }}>
                                            {item.contact_type && (
                                                <span className='subtext' style={{ textWrap: 'wrap', textAlign: 'right' }}>
                                                    {item.contact_type}
                                                </span>
                                            )}
                                            {item.search_score && (
                                                <div style={{
                                                    color: 'var(--subtext-color)',
                                                    fontSize: '10px',
                                                    opacity: 0.6
                                                }}>
                                                    {item.search_score.toFixed(1)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                            {searchResults.length === 0 && (
                                <div style={{
                                    padding: '16px',
                                    color: 'var(--subtext-color)',
                                    textAlign: 'center',
                                    fontSize: '14px'
                                }}>
                                    No contacts found
                                </div>
                            )}
                        </div>,
                        document.body
                    );
                    })()}
                </div>
            ) : (
                <div className='contact-shortform'>
                    {users ? (
                        <>
                            <span className={getContactInitialsClassName(displayContact?.id)}>
                                {(() => {
                                    const nameParts = displayContact?.name?.trim().split(" ");
                                    return `${nameParts[0]?.charAt(0)}${nameParts[1]?.charAt(0) || ""}${nameParts[2]?.charAt(0) || ""}`;
                                })()}
                            </span>
                            <div className='contact-display-shortform'>
                                <span 
                                    className={`subtext${onEditContact && !users ? ' clickable-contact-name' : ''}`}
                                    onClick={onEditContact ? () => onEditContact(displayContact) : undefined}
                                    style={onEditContact ? { cursor: 'pointer' , textWrap: 'wrap' } : { textWrap: 'wrap', whiteSpace: 'wrap', width: '100%' }}
                                >
                                    {displayContact?.name}
                                </span>
                                <span className='subtext'>{`@${displayContact?.user}` || displayContact?.id}</span>
                                {!disabled && <div onClick={handleXClick} className='exit'>
                                    <X size={20} />
                                </div>}
                            </div>
                        </>
                    ) : (
                        <>
                            {displayContact?.profile_picture ? (
                                <img src={`https://api.casedb.co/${displayContact?.profile_picture}`} alt="Profile" className='contact-initials'/>
                            ) : (
                                <span className={getContactInitialsClassName(displayContact?.id)}>
                                    {!Number(displayContact?.is_company) 
                                        ? `${displayContact?.full_name?.trim().charAt(0)}${displayContact?.last_name ? displayContact?.last_name?.trim().charAt(0) : ""}`
                                        : displayContact.company_name?.trim().charAt(0)
                                    }
                                </span>
                            )}
                            <div className='contact-display'>
                                <span 
                                    className={`subtext${onEditContact ? ' clickable-contact-name' : ''}`}
                                    onClick={onEditContact ? () => {onEditContact(displayContact); console.log("yes")} : undefined}
                                    style={onEditContact ? { cursor: 'pointer' , textWrap: 'wrap' } : { textWrap: 'wrap', whiteSpace: 'wrap', width: '100%' }}
                                >
                                    {!Number(displayContact?.is_company) ? `${displayContact.prefix ? `${displayContact.prefix} ` : ''}${displayContact?.full_name}${displayContact.suffix ? ` ${displayContact.suffix}` : ''}` : displayContact.company_name}
                                </span>
                                <div className='subtext contact-divider'>
                                    <span>{formatPhoneNumber(displayContact?.phones?.[0]?.number)}</span>
                                    <span>{displayContact?.emails?.[0]?.email}</span>
                                </div>
                                {displayContact?.job_title && <span className='subtext'>{displayContact.job_title}</span>}
                                {!disabled && <div onClick={handleXClick} className='exit'>
                                    <X size={20} />
                                </div>}
                            </div>
                        </>
                    )}
                </div>
            )}
            {showActionMenu && createPortal(
                <div
                    ref={actionMenuRef}
                    className="contact-action-menu"
                    style={{
                        position: 'absolute',
                        top: actionMenuPosition.top,
                        left: actionMenuPosition.left,
                        background: 'var(--secondary-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 100,
                        minWidth: '160px',
                        overflow: 'hidden'
                    }}
                >
                    <div
                        className="contact-action-item"
                        onClick={handleSelectNewContact}
                        style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: 'var(--text-color)',
                            borderBottom: '1px solid var(--border-color)'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--border-color)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        Select New Contact
                    </div>
                    <div
                        className="contact-action-item"
                        onClick={handleRemoveContact}
                        style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: 'var(--priority-color)',
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--border-color)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        Remove Contact
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export const ContactList = ({ onChange, value = [], onCreateNewContact, onEditContact }) => {
    const [contacts, setContacts] = useState([]);

    console.log(value);

    useEffect(() => {
        const normalizeIds = (input) => {
            if (Array.isArray(input)) {
                return input.map(id => parseInt(id)).filter(id => !isNaN(id) && id > 0);
            }
            if (typeof input === 'string' && input.trim()) {
                try {
                    const parsed = JSON.parse(input);
                    return Array.isArray(parsed)
                        ? parsed.map(id => parseInt(id)).filter(id => !isNaN(id) && id > 0)
                        : [];
                } catch {
                    return input.split(',')
                        .map(id => parseInt(id.trim()))
                        .filter(id => !isNaN(id) && id > 0);
                }
            }
            return [];
        };

        const idsEqual = (a, b) => {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i += 1) {
                if (String(a[i]) !== String(b[i])) return false;
            }
            return true;
        };

        // Ensure value is always an array of valid contact IDs
        const contactIds = normalizeIds(value);
        const currentIds = contacts
            .map(c => Number(c.selectedContact?.id))
            .filter(id => !isNaN(id) && id > 0);

        if (idsEqual(contactIds, currentIds)) {
            if (contactIds.length === 0) {
                if (contacts.length === 1 && !contacts[0].selectedContact) {
                    return;
                }
                if (contacts.length === 0) {
                    setContacts([{ id: Date.now(), selectedContact: null }]);
                }
            }
            return;
        }

        if (contactIds.length > 0) {
            const fetchInitialContacts = async () => {
                const contactData = await Promise.all(
                    contactIds.map(async (id) => {
                        try {
                            const res = await fetch(`https://api.casedb.co/contacts.php?id=${id}`, {
                                credentials: 'include'
                            });
                            const data = await res.json();
                            return data.success
                                ? { id: `contact-${id}`, selectedContact: data.contacts }
                                : null;
                        } catch (error) {
                            console.error(`Failed to fetch contact ${id}:`, error);
                            return null;
                        }
                    })
                );
                const validContacts = contactData.filter((c) => c !== null);
                setContacts(validContacts.length > 0 ? validContacts : [{ id: Date.now(), selectedContact: null }]);
            };
            fetchInitialContacts();
        } else {
            if (!(contacts.length === 1 && !contacts[0].selectedContact)) {
                setContacts([{ id: Date.now(), selectedContact: null }]);
            }
        }
    }, [value, contacts]);

    const handleContactSelect = (index, contact) => {
        const updated = [...contacts];
        updated[index].selectedContact = contact;
        setContacts(updated);

        const selectedIds = updated
            .map(c => Number(c.selectedContact?.id))
            .filter(id => !isNaN(id) && id !== 0 && id !== null && id !== undefined);

        onChange?.(selectedIds);
    };

    const addContactField = () => {
        setContacts(prev => [...prev, { id: Date.now(), selectedContact: null }]);
    };

    const removeContactField = (id) => {
        const updated = contacts.filter(c => c.id !== id);
        setContacts(updated);

        const selectedIds = updated
            .map(c => Number(c.selectedContact?.id))
            .filter(id => !isNaN(id) && id !== 0 && id !== null && id !== undefined);

        onChange?.(selectedIds);
    };

    // Check if there are any unassigned contact inputs
    const hasUnassignedContact = contacts.some(contact => !contact.selectedContact);

    return (
        <>
            {contacts.map((contactItem, index) => (
                <div key={contactItem.id} className="contact-entry">
                    <Contact
                        selectedContact={contactItem.selectedContact?.id}
                        setSelectedContact={(contact) => handleContactSelect(index, contact)}
                        onCreateNewContact={onCreateNewContact}
                        onEditContact={onEditContact}
                        showDropdownMenu={true}
                        onRemoveContact={contacts.length > 1 ? () => removeContactField(contactItem.id) : undefined}
                    />
                </div>
            ))}
            {!hasUnassignedContact && (
                <div className='action-tiny alt' onClick={addContactField}>Add Contact</div>
            )}
        </>
    );
};

export const DocGen = ({ value, onChange, options, case_id, lead_id, field_id, section_id = null, formData = {}, newData = {}, fieldUpdates = {}, fields = [], groupId = null, onBeforeGenerate = null, onBeforeReview = null }) => {
    const generationReview = useGenerationReview();
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [message, setMessage] = useState(null);
    const [generatedFile, setGeneratedFile] = useState(null);

    useEffect(() => {
        if (!value) {
            setGeneratedFile(null);
            return;
        }

        if (value && typeof value === 'object' && value.id) {
            setGeneratedFile(value);
            return;
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (parsed && typeof parsed === 'object' && parsed.id) {
                    setGeneratedFile(parsed);
                    return;
                }
            } catch (e) {
                // Not JSON, ignore
            }
        }

        setGeneratedFile(null);
    }, [value]);

    const downloadFile = async (file, filename) => {
        try {
            const fileId = Number(file?.id || file?.file_id || 0);
            const fileKey = String(file?.file_path || file?.key || file?.path || '').trim();

            if (!fileId && !fileKey) {
                throw new Error('Missing file identifier');
            }

            const downloadName = filename || file?.filename || file?.file_name || file?.name || 'download';
            const fileQuery = fileId > 0
                ? `id=${encodeURIComponent(fileId)}`
                : `key=${encodeURIComponent(fileKey)}`;

            const response = await fetch(
                `https://api.casedb.co/get-signed-url.php?${fileQuery}&cases=true&download=true&download_name=${encodeURIComponent(downloadName)}`,
                { credentials: 'include' }
            );
            const payload = await response.json();

            if (!response.ok || !payload?.success || !payload?.url) {
                throw new Error(payload?.message || 'Failed to get download URL');
            }

            const link = document.createElement('a');
            link.href = payload.url;
            link.setAttribute('download', payload.filename || downloadName);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download file');
        }
    };

    const generateAndUploadDocument = async (templateOverride = null) => {
        const templateName = typeof templateOverride === 'string' ? templateOverride : selectedTemplate;
        if (!templateName || !options?.key || !field_id) {
            console.error('Missing required parameters for document generation');
            return;
        }

        setMessage(null);
        setIsGenerating(true);
        let resolvedGroupId = groupId;
        const templateType = getGenerationTemplateType(templateName);
        const templateSupportsReview = supportsDocGenReview(templateName);
        const reviewIsEnabled = () => (
            typeof generationReview?.isEnabled === 'function'
                ? generationReview.isEnabled()
                : !!generationReview?.enabled
        );
        const initiallyReviewing = templateSupportsReview && reviewIsEnabled();
        
        try {
            const preparationHandler = initiallyReviewing ? onBeforeReview : onBeforeGenerate;
            if (typeof preparationHandler === 'function') {
                const preparation = await preparationHandler();
                if (preparation && preparation.success === false) {
                    throw new Error(preparation.message || preparation.error || 'Failed to save current add item data before document generation');
                }
                resolvedGroupId = preparation?.groupId ?? preparation?.group_id ?? resolvedGroupId;
            }

            // The account preference can change while an add-item row is
            // being prepared. If review was switched off during that wait,
            // perform the normal save preparation before direct generation.
            const reviewSupported = templateSupportsReview && reviewIsEnabled();
            if (initiallyReviewing && !reviewSupported && typeof onBeforeGenerate === 'function') {
                const directPreparation = await onBeforeGenerate();
                if (directPreparation && directPreparation.success === false) {
                    throw new Error(directPreparation.message || directPreparation.error || 'Failed to save current add item data before document generation');
                }
                if (directPreparation && directPreparation.groupId !== undefined && directPreparation.groupId !== null) {
                    resolvedGroupId = directPreparation.groupId;
                }
            }

            // Combine saved values, current form data, and unsaved payload values for placeholder replacement
            const combinedData = { ...fieldUpdates, ...formData, ...(newData.field_values || {}) };
            
            // Create field mapping for docgen placeholders
            const fieldMapping = {};
            fields.forEach(field => {
                if (field.docgen_name && combinedData[field.id] !== undefined) {
                    fieldMapping[field.docgen_name] = combinedData[field.id];
                }
            });

            const basePayload = {
                key: options.key,
                template: templateName,
                case_id,
                lead_id,
                field_id,
                group_id: resolvedGroupId,
                unsaved_data: fieldMapping
            };

            if (reviewSupported) {
                await generationReview.startReview({
                    engine: 'docgen',
                    endpoint: 'https://api.casedb.co/docgen-upload.php',
                    templateName,
                    templateType,
                    previewPayload: { ...basePayload, unsaved_data: {}, action: 'preflight' },
                    finalizePayload: { ...basePayload, unsaved_data: {}, action: 'finalize' },
                    directPayload: {
                        key: options.key,
                        template: templateName,
                        case_id,
                        field_id,
                        group_id: resolvedGroupId,
                        unsaved_data: {}
                    },
                    source: {
                        sectionId: Number(section_id || 0) || null,
                        fieldId: Number(field_id),
                        groupId: resolvedGroupId ?? null
                    },
                    onGenerated: (result) => {
                        if (result.file && typeof result.file === 'object') {
                            onChange?.(result.file, { groupId: result.group_id ?? resolvedGroupId, fileId: result.file_id });
                            setGeneratedFile(result.file);
                        } else if (result.file_id) {
                            onChange?.(result.file_id, { groupId: result.group_id ?? resolvedGroupId, fileId: result.file_id });
                        }
                        setSelectedTemplate('');
                    }
                });
                return;
            }

            const response = await fetch('https://api.casedb.co/docgen-upload.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    key: options.key,
                    template: templateName,
                    case_id: case_id,
                    lead_id: lead_id,
                    field_id: field_id,
                    group_id: resolvedGroupId,
                    unsaved_data: fieldMapping, // Pass the unsaved field data for placeholder replacement
                    debug: true
                })
            });

            const result = await response.json();
            
            if (result.success) {
                if (result.file && typeof result.file === 'object') {
                    onChange?.(result.file, { groupId: result.group_id ?? resolvedGroupId, fileId: result.file_id });
                    setGeneratedFile(result.file);
                } else {
                    onChange?.(result.file_id, { groupId: result.group_id ?? resolvedGroupId, fileId: result.file_id });
                }
                console.log('Document generated and uploaded successfully:', result);

                setMessage({ type: 'success', message: `Document "${result.filename}" generated and saved successfully!` });

                // Clear the selected template since we now have a generated document
                setSelectedTemplate("");
            } else {
                console.error('Document generation failed:', result.message);
                setMessage({ type: 'error', message: `Failed to generate document: ${result.message}` });
            }
        } catch (error) {
            console.error('Error generating document:', error);
            if (!error?.generationReviewHandled) {
                setMessage({ type: 'error', message: error.message || 'Error generating document. Please try again.' });
            }
        } finally {
            setIsGenerating(false);
        }
    };

    if (generatedFile && typeof generatedFile === 'object' && generatedFile.id) {
        const fileSize = generatedFile.size ? `${(generatedFile.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size';
        const filename = generatedFile.filename || generatedFile.file_name || 'Document';
        const [, , timeFormatted, , shortFormatted] = formatLastModified(generatedFile.uploaded_date);
        const displayDate = shortFormatted ? `${shortFormatted} ${timeFormatted}` : new Date().toLocaleDateString();

        return (
            <>
                <div className='doc-gen'>
                    <div className="generated-document">
                        <div className='docgen-icon' style={{ marginTop: '2px' }}>
                            <RiAiGenerate size={18} style={{ flexShrink: 0 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'center', alignItems: 'flex-start', flex: 1, minWidth: 0, height: '36px' }}>
                            <div
                                className="small-text" 
                                style={{ fontWeight: '500', whiteSpace: 'normal', overflow: 'hidden', cursor: 'pointer', color: 'var(--fill)', textDecoration: 'underline' }} 
                                title={filename}
                                onClick={() => window.open(`/file/${generatedFile.id}`, '_blank')}
                            >
                                {filename}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-color-secondary)' }}>
                                {fileSize} • {displayDate}
                            </div>
                        </div>
                        <button
                            className='action alt micro'
                            onClick={() => downloadFile(generatedFile, filename)}
                            title="Download file"
                            style={{ flexShrink: 0 }}
                            disabled={!generatedFile.id && !generatedFile.file_path}
                        >
                            <Download size={16} />
                        </button>
                        <button
                            className='action alt micro'
                            onClick={() => {
                                setGeneratedFile(null);
                                onChange?.(null);
                                setSelectedTemplate("");
                            }}
                            title="Clear and generate new document"
                            style={{ flexShrink: 0 }}
                        >
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </div>
                {message && (<Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />)}
            </>
        );
    }

    if (value && typeof value === 'string' && value.trim() !== '') {
        return (
            <div className='doc-gen'>
                {message && (
                    <Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />
                )}
                <div className="generated-document">
                    <span className="docgen-name small-text">{value}</span>
                    <button
                        className='action alt micro'
                        onClick={() => onChange?.(null)}
                        title="Clear and generate new document"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        );
    }

    // Otherwise, show the template selector
    return (
        <div className='doc-gen'>
            {message && (
                <Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />
            )}
            <SearchSelect
                value={selectedTemplate}
                disabled={isGenerating}
                onChange={(val) => {
                    setSelectedTemplate(val);
                }}
                options={options?.files || []}
                placeholder="Select a template..."
            />
            <button 
                className='action alt micro' 
                disabled={!selectedTemplate || isGenerating}
                onClick={() => generateAndUploadDocument()}
            >
                <RiAiGenerate size={18} title="Generate Document" disabled={isGenerating} />
            </button>
        </div>
    );
};

export const Deadline = ({ value, title, onChange, vital = false }) => {
    const parsedValue = {
        due: value?.due || '',
        done: value?.done || ''
    };
    const hasDoneDate = !!parsedValue.done;

    const handleDateChange = (key) => (newVal) => {
        onChange({ [key]: newVal });
    };

    return (
        <div className='form-group nm deadline-group'>
            {!vital && <label className="subtext deadline-title">{title}</label>}
            <div className='deadline'>
                <div className="deadline-date">
                    {hasDoneDate && (
                        <label>Due</label>
                    )}
                    <DateInput value={parsedValue.due} onChange={handleDateChange("due")} />
                </div>
                {hasDoneDate && (
                    <div className="deadline-date">
                        <label>Done</label>
                        <DateInput value={parsedValue.done} onChange={handleDateChange("done")} checkbox />
                    </div>
                )}
            </div>
        </div>
    );
};

export const DateRange = ({ title, value = null, onChange = null }) => {
    const [localValue, setLocalValue] = useState({ start: '', end: '' });
    const controlled = value !== null && value !== undefined;
    const dateRange = controlled ? value : localValue;

    const updateDateRange = (field, nextValue) => {
        const nextRange = {
            start: String(dateRange?.start || ''),
            end: String(dateRange?.end || ''),
            [field]: nextValue,
        };
        if (!controlled) setLocalValue(nextRange);
        if (typeof onChange === 'function') onChange(nextRange);
    };

    return (
        <div className='date-range'>
            <label className='subtext'>{title}</label>
            <div className='date-range-inputs'>
                <div className='data-presets form-box alt'><Calendar size={18} /></div>
                <input
                    type='date'
                    aria-label={`${title} start`}
                    value={dateRange?.start || ''}
                    onChange={(event) => updateDateRange('start', event.target.value)}
                />
                <input
                    type='date'
                    aria-label={`${title} end`}
                    value={dateRange?.end || ''}
                    onChange={(event) => updateDateRange('end', event.target.value)}
                />
            </div>
        </div>
    );
};

export const SaveButton = ({ dataChanged, name = "Save", saveFields }) => {
    return (
        <button className='action' onClick={saveFields} title={dataChanged ? "Save changes" : "No changes to save"}>
            {name}
        </button>
    );
};

export const TableOfContents = ({ subheaders = [], dataChanged, cancel = false, saveFields, onCancel, sectionName, headerActions = null }) => {
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [activeHeader, setActiveHeader] = useState('');

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-88px 0px -60% 0px',
            threshold: 0.25
        };

        const observer = new IntersectionObserver((entries) => {
            const visibleEntry = entries.find(entry => entry.isIntersecting);
            if (visibleEntry) {
                setActiveHeader(visibleEntry.target.id);
            }
        }, observerOptions);

        subheaders.forEach(header => {
            // Convert header name to URL-safe ID (same as Subheader component)
            const headerId = header.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
            const el = document.getElementById(headerId);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [subheaders]);

    return (
        <div className='table-of-contents'>
            <div className='toc-header'>
                <h3>{subheaders.length > 0 ? 'Table of Contents' : sectionName}</h3>
                <div className='toc-header-actions'>
                    {headerActions}
                    {cancel && 
                        <button className='action alt' onClick={onCancel}>Cancel</button>
                    }
                    <SaveButton dataChanged={dataChanged} name={cancel ? "Create" : "Save"} saveFields={saveFields} />
                </div>
            </div>
            {screenWidth > 768 ? (
                subheaders.map((header, index) => {
                    // Convert header name to URL-safe ID (same as Subheader component)
                    const headerId = header.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
                    return (
                        <a
                            key={`${header.name}-${index}`}
                            href={`#${headerId}`}
                            className={`subtext ${activeHeader === headerId ? 'active' : ''}`}
                            title={`Scroll to ${header.name}`}
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(headerId)?.scrollIntoView({ behavior: 'smooth' });
                            }}
                        >
                            {header.name}
                        </a>
                    );
                })
            ) : (
                <select
                    className='default-select'
                    onChange={(e) => {
                        const selectedHeader = subheaders.find(h => h.name === e.target.value);
                        if (selectedHeader) {
                            // Convert header name to URL-safe ID (same as Subheader component)
                            const headerId = selectedHeader.name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
                            document.getElementById(headerId)?.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                    value={activeHeader}
                >
                    <option value=''>Select a section</option>
                    {subheaders.map((header, index) => (
                        <option key={`${header.name}-${index}`} value={header.name}>
                            {header.name}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

export const DataTable = ({ fields, data, onRowClick, paginated, sortConfig, onSort, onRowContextMenu }) => {
    const [contactMap, setContactMap] = useState({});
    const [localSortConfig, setLocalSortConfig] = useState({ key: null, direction: 'asc' });
    const tableRef = useRef(null);

    // Use external sort config if provided (for backend sorting), otherwise use local config
    const activeSortConfig = sortConfig || localSortConfig;

    // Reorder fields to put title_key = 1 field first
    const reorderedFields = React.useMemo(() => {
        const filteredFields = fields.filter(f => f.field_id !== 14 && f.field_id !== 13 && !f.name.includes("Add"));
        const titleField = filteredFields.find(f => f.title_key === 1);
        const otherFields = filteredFields.filter(f => f.title_key !== 1);
        
        return titleField ? [titleField, ...otherFields] : filteredFields;
    }, [fields]);

    // Function to sync sticky column heights
    const syncStickyColumnHeights = () => {
        if (!tableRef.current) return;
        
        const rows = tableRef.current.querySelectorAll('tbody tr');
        
        rows.forEach(row => {
            const stickyCell = row.querySelector('.sticky-column');
            
            if (stickyCell) {
                // Reset height to auto to get natural height
                stickyCell.style.height = 'auto';
                // Get the row's actual height
                const rowHeight = row.offsetHeight;
                // Set the sticky cell to match
                stickyCell.style.height = `${rowHeight}px`;
            }
        });
    };

    useEffect(() => {
        const contactIds = new Set();
        fields.forEach(field => {
            if (field.field_id === 8) {
                data.forEach(row => {
                    if (row[field.id]) contactIds.add(row[field.id]);
                });
            }
        });
    
        if (contactIds?.size > 0) {
            fetch(`https://api.casedb.co/contacts.php?ids=${Array.from(contactIds).join(',')}`, {
                credentials: 'include'
            })
                .then(res => res.json())
                .then(result => {
                    setContactMap(result?.contacts?.reduce((map, contact) => {
                        map[contact.id] = Number(contact.is_company) === 1 ? contact.company_name ?? contact.full_name ?? `Contact ${contact.id}` : contact.full_name ?? `Contact ${contact.id}`;
                        return map;
                    }, {}));
                });
        }
    }, [fields, data]);

        const getUploadDisplayName = (item) => {
            if (item === null || item === undefined || item === '') return '';

            if (typeof item === 'string') {
                try {
                    const parsed = JSON.parse(item);
                    if (parsed && typeof parsed === 'object') {
                        return formatUploadDisplayValue(parsed);
                    }
                } catch {}

                const bracketMatch = item.match(/^\[\d+\]\s*-\s*(.+)$/);
                return bracketMatch ? bracketMatch[1] : item;
            }

            if (typeof item === 'object') {
                return item.file_name || item.filename || item.name || item.fileName || item.title || item.original_name || '';
            }

            return String(item);
        };

        const formatUploadDisplayValue = (value) => {
            if (value === null || value === undefined || value === '') return '';

            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed) || (parsed && typeof parsed === 'object')) {
                        return formatUploadDisplayValue(parsed);
                    }
                } catch {}
            }

            const items = Array.isArray(value) ? value : [value];
            const names = items.map(getUploadDisplayName).filter(Boolean);
            return names.join(', ');
        };

    const formatMultiSelectDisplayValue = (field, rawValue) => {
        let options = field.options;
        if (typeof options === 'string') {
            try { options = JSON.parse(options); } catch { options = []; }
        }
        if (!Array.isArray(options)) options = [];

        let values = rawValue;
        if (typeof values === 'string') {
            try { values = JSON.parse(values); } catch { /* Legacy label text. */ }
        }
        if (!Array.isArray(values)) values = [values];

        const labelText = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'object') {
                // Never pass option objects through as React children.
                const label = value.label ?? value.name ?? value.text ?? value.value;
                return typeof label === 'string' || typeof label === 'number' ? String(label) : '';
            }
            return String(value);
        };

        return values.map(value => {
            if (value === null || value === undefined || value === '') return '';
            // MultiSelect persists zero-based option indexes, including index 0.
            const token = typeof value === 'number' || typeof value === 'string' ? String(value).trim() : '';
            if (/^\d+$/.test(token)) {
                const index = Number(token);
                if (Number.isSafeInteger(index) && index < options.length) {
                    const label = labelText(options[index]);
                    if (label !== '') return label;
                }
            }
            // Keep legacy labels / unknown selections visible, not silently lost.
            return labelText(value);
        }).filter(label => label.trim() !== '').join(', ');
    };

    const displayValue = (field, rawValue) => {
        const isContactField = field.field_id === 8;

        if (isContactField) {
            return contactMap?.[rawValue] || rawValue;
        }

        // Handle different field types based on field_id
        switch (field.field_id) {
            case 3: // Amount - Currency (money)
                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    const num = parseFloat(rawValue);
                    if (!isNaN(num)) {
                        return num.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                        });
                    }
                }
                return rawValue;

            case 4: // Amount - Percent (percent)
                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    const num = parseFloat(rawValue);
                    if (!isNaN(num)) {
                        return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
                    }
                }
                return rawValue;

            case 5: // Amount - Number (number)
                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    const num = parseFloat(rawValue);
                    if (!isNaN(num)) {
                        return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
                    }
                }
                return rawValue;

            case 6: // Date
                if (rawValue && /^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
                    try {
                        // Parse date without timezone conversion by treating as local date
                        const [year, month, day] = rawValue.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        });
                    } catch {
                        return rawValue;
                    }
                }
                return rawValue;

            case 7: // Time
                if (rawValue && /^\d{2}:\d{2}/.test(rawValue)) {
                    try {
                        const [hours, minutes] = rawValue.split(':');
                        const time = new Date();
                        time.setHours(parseInt(hours), parseInt(minutes));
                        return time.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                        });
                    } catch {
                        return rawValue;
                    }
                }
                return rawValue;

            case 12: // Yes/No (boolean)
                if (rawValue === '0' || rawValue === 0 || rawValue === false || rawValue === 'false') {
                    return 'Yes';
                } else if (rawValue === '1' || rawValue === 1 || rawValue === true || rawValue === 'true') {
                    return 'No';
                } else {
                    return 'Unknown';
                }

            case 25: // Rating
                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    const rating = parseInt(rawValue);
                    if (!isNaN(rating) && rating >= 1 && rating <= 5) {
                        return '★'.repeat(rating) + '☆'.repeat(5 - rating);
                    }
                }
                return rawValue;

            case 26: // Multi Value
                if (Array.isArray(rawValue)) {
                    return `${rawValue.length} entr${rawValue.length === 1 ? 'y' : 'ies'}`;
                }
                if (typeof rawValue === 'string') {
                    try {
                        const parsed = JSON.parse(rawValue);
                        if (Array.isArray(parsed)) {
                            return `${parsed.length} entr${parsed.length === 1 ? 'y' : 'ies'}`;
                        }
                    } catch {
                        return rawValue;
                    }
                }
                return rawValue;

            case 16: // Documents/Files
                return formatUploadDisplayValue(rawValue) || rawValue;
            case 15: // File upload
                return formatUploadDisplayValue(rawValue) || rawValue;
            
            case 17:
                if (isDateCalculationField(field)) {
                    const config = parseCalculationOptions(field.options);
                    const dateConfig = config?.date || config?.date_calculation || config?.dateCalculation || config?.fallback || config;
                    const displayFormat = dateConfig?.display_format || dateConfig?.displayFormat || dateConfig?.format || 'MM/DD/YYYY';
                    return formatCalculationDateForDisplay(rawValue, displayFormat);
                }

                if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    const num = parseFloat(rawValue);
                    if (!isNaN(num)) {
                        return num.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                        });
                    }
                }
                return rawValue;

            default:
                // Handle dropdown/multiselect options for other field types
                try {
                    const opts = JSON.parse(field.options || "[]");
                    if (opts && (typeof rawValue === 'number' || !isNaN(rawValue))) {
                        return opts[Number(rawValue)] ?? rawValue;
                    }
                } catch {}
                
                // Ensure we never return an object that React can't render
                if (rawValue && typeof rawValue === 'object') {
                    // If it's a file object with name, return the name
                    if (rawValue.file_name || rawValue.filename || rawValue.name) {
                        return rawValue.file_name || rawValue.filename || rawValue.name;
                    }
                    // Otherwise, try to stringify it or return empty string
                    try {
                        return JSON.stringify(rawValue);
                    } catch {
                        return '[Object]';
                    }
                }
                
                return rawValue;
        }
    };

    const getRawSortValue = (field, rawValue) => {
        if (field.field_id === 8) {
            return contactMap?.[rawValue] || rawValue;
        }

        // For numeric field types, ensure we sort by numeric value
        if ([3, 4, 5, 25].includes(field.field_id)) { // currency, percent, number, rating
            const num = parseFloat(rawValue);
            return !isNaN(num) ? num : 0;
        }

        // For date fields, sort by timestamp
        if (field.field_id === 6 && rawValue && /^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
            return new Date(rawValue).getTime();
        }

        // For time fields, convert to minutes for sorting
        if (field.field_id === 7 && rawValue && /^\d{2}:\d{2}/.test(rawValue)) {
            const [hours, minutes] = rawValue.split(':');
            return parseInt(hours) * 60 + parseInt(minutes);
        }

        // For boolean fields, convert to numeric for sorting (0 = Yes, 1 = No)
        if (field.field_id === 12) {
            if (rawValue === '0' || rawValue === 0 || rawValue === false || rawValue === 'false') {
                return 0; // Yes
            } else if (rawValue === '1' || rawValue === 1 || rawValue === true || rawValue === 'true') {
                return 1; // No
            }
            return 2; // Unknown values sort last
        }

        try {
            const parsed = JSON.parse(rawValue);
            if (parsed.due) {
                return new Date(parsed.due).getTime();
            }
            if (Array.isArray(parsed)) {
                return parsed.length;
            }
        } catch {
            // Not JSON, use raw value
        }

        if (!isNaN(rawValue) && rawValue !== '') {
            return Number(rawValue);
        }

        if (typeof rawValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
            return new Date(rawValue).getTime();
        }

        return rawValue || '';
    };

    const handleSort = (fieldId) => {
        let direction = 'asc';
        if (activeSortConfig.key === fieldId && activeSortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        if (onSort) {
            // Call parent's sort handler for backend sorting
            onSort(fieldId, direction);
        } else {
            // Use local sorting for backward compatibility
            setLocalSortConfig({ key: fieldId, direction });
        }
    };

    const sortedData = React.useMemo(() => {
        // If using external sort (backend sorting), don't sort here
        if (sortConfig) return data;
        
        // Otherwise use local sorting
        if (!localSortConfig.key) return data;

        const sortField = fields.find(f => f.id === localSortConfig.key);
        if (!sortField) return data;

        return [...data].sort((a, b) => {
            const aValue = getRawSortValue(sortField, a[sortField.id]);
            const bValue = getRawSortValue(sortField, b[sortField.id]);

            let comparison = 0;
            if (aValue < bValue) {
                comparison = -1;
            } else if (aValue > bValue) {
                comparison = 1;
            }

            return localSortConfig.direction === 'desc' ? comparison * -1 : comparison;
        });
    }, [data, localSortConfig, sortConfig, fields, contactMap]);

    // Sync heights after render and when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            syncStickyColumnHeights();
        }, 50); // Small delay to ensure rendering is complete
        
        return () => clearTimeout(timer);
    }, [sortedData, contactMap]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            syncStickyColumnHeights();
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!data || data.length === 0) return <p className="subtext">No entries yet.</p>;

    return (
        <table className="data-table default" ref={tableRef}>
            <thead>
                <tr>
                    {reorderedFields.map((f, index) => (
                        <th 
                            key={f.id} 
                            title={f.name}
                            className={`sortable ${index === 0 ? 'sticky-column' : ''}`}
                            onClick={() => handleSort(f.id)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            {f.name}
                            {activeSortConfig.key === f.id && (
                                <span style={{ marginLeft: '5px' }}>
                                    {activeSortConfig.direction === 'asc' ? '↑' : '↓'}
                                </span>
                            )}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {sortedData.map((group, index) => (
                    <tr
                        key={`group-${index}`}
                        className='data-cell'
                        onClick={() => onRowClick && onRowClick(group)}
                        onContextMenu={(e) => {
                            if (onRowContextMenu) {
                                e.preventDefault();
                                e.stopPropagation();
                                onRowContextMenu(e, group);
                            }
                        }}
                    >
                        {reorderedFields.map((f, colIndex) => {
                            return (
                                <td key={`group-${index}-field-${f.id}`} className={colIndex === 0 ? 'sticky-column' : ''}>
                    {(() => {
                        // Resolve selections before generic numeric coercion or
                        // JSON rendering, which cannot interpret option indexes.
                        if (Number(f.field_id) === 11) {
                            const labels = formatMultiSelectDisplayValue(f, group[f.id]);
                            return labels ? (
                                <span title={labels}>
                                    {labels.length > 30 ? `${labels.slice(0, 30)}...` : labels}
                                </span>
                            ) : '';
                        }

                        // Handle object format for documents (field_id === 16) before processing
                        if (f.field_id === 16 && group[f.id] && typeof group[f.id] === 'object' && group[f.id].name) {
                            const fileName = group[f.id].name;
                            return fileName.length > 30 ? `${fileName.slice(0, 30)}...` : fileName;
                        }

                        const raw = displayValue(f, group[f.id]);

                        try {
                            const parsed = JSON.parse(raw);                            if (parsed.due || parsed.done) {
                                const due = parsed.due ? `${parsed.due}` : null;
                                const done = parsed.done ? <Check size={18} color={'var(--fill)'} style={{ marginLeft: due ? '5px' : '' }} /> : null;
                                return (
                                    <>
                                        {formatDate(due)[0]}
                                        {done}
                                    </>
                                );
                            }

                            if (f.field_id === 9 && Array.isArray(parsed) && parsed.length > 0) {
                                return parsed.length > 1 ? `${parsed.length} contacts` : `1 contact`;
                            } else if ((f.field_id === 15 || f.field_id === 16) && Array.isArray(parsed)) {
                                const fileNames = formatUploadDisplayValue(parsed);
                                return fileNames.length > 30 ? `${fileNames.slice(0, 30)}...` : fileNames;
                            }

                            if (Array.isArray(parsed)) {
                                return parsed.join(', ');
                            }
                        } catch {
                            
                        }

                        // Final safety check - ensure we never return an object that React can't render
                        if (raw && typeof raw === 'object') {
                            if (f.field_id === 15 || f.field_id === 16) {
                                const fileNames = formatUploadDisplayValue(raw);
                                return fileNames.length > 30 ? `${fileNames.slice(0, 30)}...` : fileNames;
                            }
                            if (raw.name) {
                                return raw.name.length > 30 ? `${raw.name.slice(0, 30)}...` : raw.name;
                            }
                            return '[Object]';
                        }

                        return raw?.length > 30 ? `${raw?.slice(0,30)}...` : raw ?? '';
                                    })()}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export const InteractiveInput = ({
    value,
    options,
    onChange,
    placeholder = "Type here..."
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const wrapperRef = useRef(null);

    const selectedValues = value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);

    const availableOptions = options.filter(opt => !selectedValues.includes(opt));

    const handleOptionClick = (option) => {
        const updated = value.length > 0 ? [...selectedValues, option].join(', ') : option;
        onChange(updated);
    };

    return (
        <div className="interactive-input" ref={wrapperRef}>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                onFocus={() => setIsFocused(true)}
                onBlur={(e) => {
                    setTimeout(() => {
                        if (!wrapperRef?.current?.contains(document.activeElement)) {
                            setIsFocused(false);
                        }
                    }, 100);
                }}
                autoComplete="off"
            />
            {isFocused && availableOptions.length > 0 && (
                <div className="interactive-dropdown">
                    {availableOptions.map((option, index) => (
                        <div
                            key={index}
                            className="interactive-option"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleOptionClick(option);
                            }}
                        >
                            {option}<span><Plus size={12} /></span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const normalizeActivityAssigneeIds = (assignees) => Array.from(new Set(
    (Array.isArray(assignees) ? assignees : [])
        .map(value => Number(value))
        .filter(value => Number.isInteger(value) && value > 0)
));

export const AddActivity = ({ users, fetchFeed, lead_id = null, case_id = null, onClick, addActivity, setAddActivity, contacts, lockedType = null, locked = false, initialDraft = null, onActivityCreated = () => {}, onCancel = () => {}, onRequestCreateContact = () => {}, createdContact = null }) => {
    const [activeDraft, setActiveDraft] = useState(null);
    const effectiveLockedType = activeDraft?.lockedType ?? lockedType;
    const resolveActivityType = (typeIndex) => {
        if (typeIndex === 1) return "tasks";
        if (typeIndex === 2) return "calls";
        if (typeIndex === 3) return "faxes";
        if (typeIndex === 4) return "texts";
        if (typeIndex === 5) return "esignatures";
        return "notes";
    };

    const buildActivityData = (typeIndex = (effectiveLockedType !== null ? effectiveLockedType : 0), overrides = {}) => ({
        subject: "",
        content: "",
        attachments: [],
        type: resolveActivityType(typeIndex),
        tags: [],
        start_time: null,
        end_time: null,
        post_date: null,
        date: null,
        case_id: case_id,
        lead_id: lead_id,
        contact: { id: null },
        mentions: [],
        fax_number: "",
        fax_file: null,
        sms_number: "",
        esignature_document: null,
        esignature_signers: [],
        tempSignerName: "",
        tempSignerEmail: "",
        tempSignerPhone: "",
        task: null,
        assignees: [],
        lockSubject: false,
        settlementTriggerRoute: false,
        completeSettlementPhaseAfterNote: false,
        settlementPhaseId: null,
        disengagementTriggerRoute: false,
        completeDisengagementPhaseAfterNote: false,
        disengagementPhaseId: null,
        disengagementDefaultContent: '',
        ...overrides,
        type: overrides.type || resolveActivityType(typeIndex),
        case_id: overrides.case_id ?? case_id,
        lead_id: overrides.lead_id ?? lead_id,
        attachments: Array.isArray(overrides.attachments) ? overrides.attachments : [],
        tags: Array.isArray(overrides.tags) ? overrides.tags : (overrides.tags ?? []),
        mentions: Array.isArray(overrides.mentions) ? overrides.mentions : [],
        contact: overrides.contact ?? { id: null },
        esignature_signers: Array.isArray(overrides.esignature_signers) ? overrides.esignature_signers : [],
        assignees: normalizeActivityAssigneeIds(overrides.assignees)
    });

    const [activeActivityType, setActiveActivityType] = useState(lockedType !== null ? lockedType : 0);
    const [attachFile, setAttachFile] = useState(false);
    const [postDate, setPostDate] = useState(false);
    const [showTags, setShowTags] = useState(false);
    const [showSMSContacts, setShowSMSContacts] = useState(false);
    const [syncItem, setSyncItem] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [smsPendingConfirmation, setSmsPendingConfirmation] = useState(false);
    const [showSettlementGuidance, setShowSettlementGuidance] = useState(false);
    const [showDisengagementGuidance, setShowDisengagementGuidance] = useState(false);
    const [activityData, setActivityData] = useState(() => buildActivityData());
    const appliedDraftIdRef = useRef(null);
    const appliedCreatedContactIdRef = useRef(null);
    const smsSendLockRef = useRef(false);
    const smsRequestIdRef = useRef(null);

    useEffect(() => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
    }, [activeActivityType, case_id, lead_id]);

    const resetComposer = useCallback(() => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
        const nextTypeIndex = lockedType !== null ? lockedType : 0;
        setAddActivity(false);
        setActiveDraft(null);
        setAttachFile(false);
        setPostDate(false);
        setShowTags(false);
        setShowSMSContacts(false);
        setSyncItem(false);
        setShowSettlementGuidance(false);
        setShowDisengagementGuidance(false);
        setActivityData(buildActivityData(nextTypeIndex));
        setActiveActivityType(nextTypeIndex);
    }, [buildActivityData, lockedType, setAddActivity]);

    const handleComposerCancel = () => {
        const settlementCancelled = !!(activeDraft?.settlementTriggerRoute || activityData.settlementTriggerRoute);
        const disengagementCancelled = !!(activeDraft?.disengagementTriggerRoute || activityData.disengagementTriggerRoute);
        resetComposer();
        if (settlementCancelled) {
            onCancel?.({ settlement_cancelled: true });
        } else if (disengagementCancelled) {
            onCancel?.({ disengagement_cancelled: true });
        } else {
            onCancel?.({ cancelled: true });
        }
    };

    const userMap = users?.reduce((acc, user) => {
        acc[user.id] = user.name;
        return acc;
    }, {});
    const validActivityAssignees = normalizeActivityAssigneeIds(activityData.assignees);

    // Helper function to get the complete fax number with +1 default
    const getCompleteFaxNumber = () => {
        const number = activityData.fax_number.trim();
        if (!number) return "";
        
        // If the number already starts with + and has more than 10 digits, let backend handle it
        if (number.startsWith('+') && number.length > 11) { // +1 + 10 digits = 12 chars minimum for international
            return number;
        }
        
        // If the number starts with +1, use as-is
        if (number.startsWith('+1')) {
            return number;
        }
        
        // If the number starts with 1 and has 11 digits total, add +
        if (number.startsWith('1') && number.length === 11) {
            return '+' + number;
        }
        
        // Otherwise, default to +1 + number
        return '+1' + number;
    };
      
    const handleInputChange = (key) => (value) => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
        setActivityData((prev) => {
            const updated = { 
                ...prev, 
                [key]: value, 
                type: resolveActivityType(activeActivityType)
            };
            return updated;
        });
    };

    const handleMentionsChange = (mentions) => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
        setActivityData(prev => ({ ...prev, mentions }));
    };

    const handleTagsChange = (tags) => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
        setActivityData(prev => ({ ...prev, tags }));
    };

    useEffect(() => {
        setActivityData(prev => ({
            ...prev,
            case_id,
            lead_id
        }));
    }, [case_id, lead_id]);

    useEffect(() => {
        if (!initialDraft?.id || appliedDraftIdRef.current === initialDraft.id) {
            return;
        }

        const nextTypeIndex = effectiveLockedType !== null
            ? effectiveLockedType
            : initialDraft.type === 'tasks'
                ? 1
                : initialDraft.type === 'calls'
                    ? 2
                    : initialDraft.type === 'faxes'
                        ? 3
                        : initialDraft.type === 'texts'
                            ? 4
                            : initialDraft.type === 'esignatures'
                                ? 5
                                : 0;

        appliedDraftIdRef.current = initialDraft.id;
        setActiveDraft(initialDraft);
        setAddActivity(true);
        setAttachFile(false);
        setPostDate(false);
        setShowTags(false);
        setShowSMSContacts(false);
        setSyncItem(false);
        setActiveActivityType(nextTypeIndex);
        setActivityData(buildActivityData(nextTypeIndex, initialDraft));
        setShowSettlementGuidance(!!initialDraft.settlementTriggerRoute);
        setShowDisengagementGuidance(!!initialDraft.disengagementTriggerRoute);
    }, [buildActivityData, effectiveLockedType, initialDraft, setAddActivity]);

    useEffect(() => {
        if (!createdContact?.id || appliedCreatedContactIdRef.current === createdContact.id) {
            return;
        }

        appliedCreatedContactIdRef.current = createdContact.id;
        setActivityData((prev) => ({
            ...prev,
            contact: createdContact
        }));
    }, [createdContact]);

    const subjectLocked = locked || !!(activeDraft && activityData.lockSubject);
    const typeSelectionLocked = effectiveLockedType !== null || !!(activeDraft && (activityData.settlementTriggerRoute || activityData.disengagementTriggerRoute));

    const handleSettlementSkip = async () => {
        if (isSubmitting) return;

        if (!activityData.case_id) {
            console.error('Missing case ID for settlement skip flow');
            alert('Unable to set settlement without a case ID.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('https://api.casedb.co/phases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: activityData.case_id,
                    phase_id: activityData.settlementPhaseId || 19,
                    is_subphase: false,
                    verified: true,
                    create_feed_entry: true
                })
            });

            const data = await response.json();

            if (data.success) {
                resetComposer();
                fetchFeed();
                onActivityCreated?.({
                    ...data,
                    settlement_phase_updated: true,
                    settlement_note_skipped: true
                });
            } else {
                console.error('Error setting settlement without note:', data);
                alert(data.message || 'Failed to set settlement without note.');
            }
        } catch (error) {
            console.error('Error setting settlement without note:', error);
            alert('Failed to set settlement without note.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDisengagementSkip = async () => {
        if (isSubmitting) return;

        if (!activityData.case_id) {
            console.error('Missing case ID for disengagement skip flow');
            alert('Unable to complete disengagement without a case ID.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('https://api.casedb.co/phases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    case_id: activityData.case_id,
                    phase_id: activityData.disengagementPhaseId || 3,
                    is_subphase: false,
                    verified: true,
                    create_feed_entry: true,
                    disengagement_subject: activityData.subject || 'Pending disengagement',
                    disengagement_content: activityData.disengagementDefaultContent || activityData.content || '@ksatriano Pending disengagement.'
                })
            });

            const data = await response.json();

            if (data.success) {
                resetComposer();
                fetchFeed();
                onActivityCreated?.({
                    ...data,
                    disengagement_phase_updated: true,
                    disengagement_note_skipped: true
                });
            } else {
                console.error('Error completing disengagement with default entry:', data);
                alert(data.message || 'Failed to complete disengagement with default entry.');
            }
        } catch (error) {
            console.error('Error completing disengagement with default entry:', error);
            alert('Failed to complete disengagement with default entry.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        const isSmsSubmission = activityData.type === 'texts';
        if (isSmsSubmission && (smsSendLockRef.current || smsPendingConfirmation)) return;
        if (isSubmitting) return; // Prevent multiple submissions

        if (isSmsSubmission) smsSendLockRef.current = true;
        
        setIsSubmitting(true);
        try {
            // Handle SMS sending separately
            if (activityData.type === 'texts') {
                const formData = new FormData();
                formData.append('phone_number', activityData.sms_number);
                formData.append('message', activityData.content);
                const requestId = smsRequestIdRef.current || createCommunicationRequestId();
                smsRequestIdRef.current = requestId;
                formData.append('request_id', requestId);
                
                // Add case/lead IDs and optional subject/content for feed_updates
                if (activityData.case_id) {
                    formData.append('case_id', activityData.case_id);
                }
                if (activityData.lead_id) {
                    formData.append('lead_id', activityData.lead_id);
                }
                if (activityData.subject) {
                    formData.append('subject', activityData.subject);
                }
                if (activityData.content) {
                    formData.append('content', activityData.content);
                }
                
                // Convert attachments to base64 for SMS (to avoid temp file issues)
                if (Array.isArray(activityData.attachments) && activityData.attachments.length > 0) {
                    const fileDataArray = [];
                    const fileNameArray = [];
                    
                    for (const file of activityData.attachments) {
                        if (file && typeof file.name === "string" && typeof file.size === "number") {
                            try {
                                const base64Data = await new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                        const base64 = reader.result.split(',')[1]; // Remove data:...;base64, prefix
                                        resolve(base64);
                                    };
                                    reader.onerror = reject;
                                    reader.readAsDataURL(file);
                                });
                                
                                fileDataArray.push(base64Data);
                                fileNameArray.push(file.name);
                            } catch (error) {
                                console.error('Error converting file to base64:', error);
                            }
                        }
                    }
                    
                    if (fileDataArray.length > 0) {
                        fileDataArray.forEach((fileData, index) => {
                            formData.append(`file_data[${index}]`, fileData);
                            formData.append(`file_name[${index}]`, fileNameArray[index]);
                        });
                    }
                }
                
                // Add tags and mentions for feed_updates
                formData.append('tags', JSON.stringify(
                    typeof activityData?.tags === 'string' 
                        ? activityData.tags.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                        : activityData?.tags || []
                ));
                formData.append('mentions', JSON.stringify(activityData.mentions || []));

                const response = await fetch('https://api.casedb.co/fax-fixed.php', {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                });

                const result = await response.json();
                if (isSmsConfirmationPending(result)) {
                    setSmsPendingConfirmation(true);
                    if (result?.feed_update_id) {
                        fetchFeed();
                    }
                } else if (response.ok && result.success) {
                    // SMS sent successfully and recorded in feed_updates by fax.php
                    smsRequestIdRef.current = null;
                    setSmsPendingConfirmation(false);
                    resetComposer();
                    fetchFeed();
                } else {
                    if (result?.retry_with_new_request_id) {
                        smsRequestIdRef.current = null;
                        setSmsPendingConfirmation(false);
                    }
                    if (result?.feed_update_id) {
                        fetchFeed();
                    }
                    console.error("Error sending SMS:", result.error || result);
                    alert(`Failed to send SMS: ${result.error || 'Unknown error'}`);
                }
                return;
            }

            // Handle fax sending separately
            if (activityData.type === 'faxes') {
                // Client-side validation & diagnostics before sending
                if (!activityData.fax_number || !getCompleteFaxNumber()) {
                    alert('Please enter a fax number.');
                    return;
                }
                if (!activityData.fax_file) {
                    alert('Please select a file to fax.');
                    return;
                }
                const selectedFaxFileId = activityData.fax_file?.id || activityData.fax_file?.file_id || null;
                if (!selectedFaxFileId) {
                    alert('Please select a saved document to fax.');
                    return;
                }

                const faxFormData = new FormData();
                const completeFaxNumber = getCompleteFaxNumber();
                faxFormData.append('fax_number', completeFaxNumber);
                faxFormData.append('file_id', selectedFaxFileId);
                faxFormData.append('original_name', activityData.fax_file?.name || activityData.fax_file?.file_name || activityData.fax_file?.filename || 'fax_document.pdf');
                if (activityData.fax_file?.size || activityData.fax_file?.file_size) {
                    faxFormData.append('file_size', activityData.fax_file.size || activityData.fax_file.file_size);
                }
                
                // Add case/lead IDs and optional subject/content for feed_updates
                if (case_id) {
                    faxFormData.append('case_id', case_id);
                }
                if (lead_id) {
                    faxFormData.append('lead_id', lead_id);
                }
                if (activityData.subject) {
                    faxFormData.append('subject', activityData.subject);
                }
                if (activityData.content) {
                    faxFormData.append('content', activityData.content);
                }
                
                // Add tags and mentions for feed_updates
                faxFormData.append('tags', JSON.stringify(
                    typeof activityData?.tags === 'string' 
                        ? activityData.tags.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                        : activityData?.tags || []
                ));
                faxFormData.append('mentions', JSON.stringify(activityData.mentions || []));
                
                const response = await fetch('https://api.casedb.co/fax-fixed.php', {
                    method: 'POST',
                    credentials: 'include',
                    body: faxFormData,
                });

                const result = await response.json();
                if (response.ok && result.success) {
                    // Fax sent successfully and recorded in feed_updates by fax.php
                    resetComposer();
                    fetchFeed();
                } else {
                    if (result?.feed_update_id) {
                        fetchFeed();
                    }
                    console.error("Error sending fax:", result.error || result, result);
                    let extra = '';
                    if (result?.code === 'FILE_NOT_FOUND') {
                        extra = '\nThe browser did not deliver the uploaded file (tmp missing). Try re-selecting the file or using a smaller file. If this persists, check server upload_max_filesize & post_max_size.';
                    } else if (result?.code === 'UPLOAD_ERROR') {
                        extra = '\nUpload error at PHP level: ' + (result.php_error ?? 'unknown');
                    }
                    alert(`Failed to send fax: ${result.error || 'Unknown error'}${extra}`);
                }
                return;
            }

            // Handle eSignature requests separately
            if (activityData.type === 'esignatures') {
                // Validate required fields
                if (!activityData.esignature_document) {
                    alert('Please select a document to be signed');
                    return;
                }

                if (!activityData.esignature_signers || activityData.esignature_signers.length === 0) {
                    alert('Please add at least one signer for the document.');
                    return;
                }

                const formData = new FormData();
                formData.append('document', activityData.esignature_document);
                formData.append('request_title', activityData.subject || 'Signature Request');
                formData.append('description', activityData.content || '');

                formData.append('signers', JSON.stringify(activityData.esignature_signers));
                
                // Add case/lead IDs
                if (activityData.case_id) {
                    formData.append('case_id', activityData.case_id);
                }
                if (activityData.lead_id) {
                    formData.append('lead_id', activityData.lead_id);
                }
                
                // Add tags and mentions for feed_updates
                formData.append('tags', JSON.stringify(
                    typeof activityData?.tags === 'string' 
                        ? activityData.tags.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                        : activityData?.tags || []
                ));
                formData.append('mentions', JSON.stringify(activityData.mentions || []));

                const response = await fetch('https://api.casedb.co/esignature.php', {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                });

                const result = await response.json();
                if (response.ok && result.success) {
                    // eSignature request created successfully
                    resetComposer();
                    fetchFeed();
                    
                    // If the backend returns a redirect URL for configuring signers, open it
                    if (result.redirect_url) {
                        window.open(result.redirect_url, '_blank');
                    }
                } else {
                    alert(`Failed to create eSignature request: ${result.error || 'Unknown error'}`);
                }
                return;
            }

            // Handle eSignature requests separately
            if (activityData.type === 'esignatures') {
                if (!activityData.esignature_document) {
                    alert('Please select a document to request signatures for.');
                    return;
                }

                if (!activityData.esignature_signers || activityData.esignature_signers.length === 0) {
                    alert('Please add at least one signer for the document.');
                    return;
                }

                const formData = new FormData();
                formData.append('case_id', activityData.case_id);
                formData.append('lead_id', activityData.lead_id);
                formData.append('request_title', activityData.subject || 'Signature Request');
                formData.append('description', activityData.content);
                formData.append('document', activityData.esignature_document);
                
                formData.append('signers', JSON.stringify(activityData.esignature_signers));
                
                // Add tags and mentions
                formData.append('tags', JSON.stringify(
                    typeof activityData?.tags === 'string' 
                        ? activityData.tags.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                        : activityData?.tags || []
                ));
                formData.append('mentions', JSON.stringify(activityData.mentions || []));

                const response = await fetch('https://api.casedb.co/esignature.php', {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                });

                const result = await response.json();
                if (response.ok && result.success) {
                    // eSignature request created successfully
                    resetComposer();
                    fetchFeed();
                    
                    // Open the configuration page in a new window
                    if (result.redirect_url) {
                        window.open(result.redirect_url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
                    }
                    
                    alert(result.message || 'eSignature request created successfully!');
                } else {
                    console.error("Error creating eSignature request:", result.error || result);
                    alert(`Failed to create eSignature request: ${result.error || 'Unknown error'}`);
                }
                return;
            }

            // Validate task assignment
            if (activityData.type === 'tasks' && validActivityAssignees.length === 0) {
                alert('Please assign the task to at least one user');
                return;
            }

            const formData = new FormData();
            formData.append("case_id", activityData.case_id);
            formData.append("lead_id", activityData.lead_id);
            formData.append("type", activityData.type);
            formData.append("subject", activityData.subject);
            formData.append("content", activityData.content);
            if (activityData.completeSettlementPhaseAfterNote) {
                formData.append("complete_settlement_phase_after_note", "1");
            }
            if (activityData.settlementPhaseId) {
                formData.append("settlement_phase_id", activityData.settlementPhaseId);
            }
            if (activityData.completeDisengagementPhaseAfterNote) {
                formData.append("complete_disengagement_phase_after_note", "1");
            }
            if (activityData.disengagementPhaseId) {
                formData.append("disengagement_phase_id", activityData.disengagementPhaseId);
            }
            if (syncItem) {
                formData.append("sync_item", "1");
            }
            formData.append(
                "tags",
                JSON.stringify(
                    typeof activityData?.tags === 'string' 
                        ? activityData.tags.split(/[\s,]+/)?.map(tag => tag.trim())?.filter(tag => tag.length > 0)
                        : activityData?.tags || []
                )
            );
            formData.append("mentions", JSON.stringify(activityData.mentions || []));
            if (activityData.type === 'tasks' && validActivityAssignees.length > 0) {
                // Send first assignee as task for backward compatibility
                formData.append("task", validActivityAssignees[0]);
                // Send full assignees array
                formData.append("assignees", JSON.stringify(validActivityAssignees));
                formData.append("due_date", activityData.due_date || "");
                formData.append("post_date", activityData.post_date || "");
            }
    
            if (activityData.type === 'calls') {
                formData.append("date", activityData.date || "");
                formData.append("start_time", activityData.start_time || "");
                formData.append("end_time", activityData.end_time || "");
                if (activityData.contact?.id) {
                    formData.append("contact_id", activityData.contact.id);
                }
            }

            if (Array.isArray(activityData.attachments)) {
                activityData.attachments.forEach((file) => {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        formData.append("attachment[]", file);
                    }
                });
            }            
    
            const response = await fetch("https://api.casedb.co/activity_feed.php", {
                method: "POST",
                credentials: 'include',
                body: formData,
            });
    
            const data = await response.json();
            if (data.success) {
                resetComposer();
                fetchFeed();
                onActivityCreated?.(data);
            } else {
                console.error("Error adding activity:", data);
            }
        } catch (error) {
            console.error("Error adding activity:", error);
        } finally {
            if (isSmsSubmission) smsSendLockRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {showSettlementGuidance && activityData.settlementTriggerRoute && (
                <Alert
                    message="You can skip the note or send a note with additional info to set the phase to Settlement."
                    type="info"
                    onClose={() => setShowSettlementGuidance(false)}
                />
            )}
            {showDisengagementGuidance && activityData.disengagementTriggerRoute && (
                <Alert
                    message="You can edit and send this pending disengagement note, or send the default entry."
                    type="info"
                    onClose={() => setShowDisengagementGuidance(false)}
                />
            )}
            {!addActivity ? 
                <div className='form-group activity'>
                    <input
                        type='text'
                        onClick={onClick}
                        placeholder="Add new activity..."
                    />
                </div>
            : (
                <div className='add-activity' data-activity-composer="true">
                    {!typeSelectionLocked && (
                        <div className='activity-type-container'>
                            <div className='activity-type' onClick={() => setActiveActivityType(0)} style={{ backgroundColor: "var(--fill)"}}>
                                <StickyNote size={20} color='var(--secondary-color)'/> {activeActivityType === 0 ? "Add a note" : ""}
                            </div>
                            <div className='activity-type' onClick={() => setActiveActivityType(1)} style={{ backgroundColor: "var(--fill-3)"}}>
                                <CheckSquare size={20} color='var(--secondary-color)'/> {activeActivityType === 1 ? "Assign a task" : ""}
                            </div>
                            <div className='activity-type' onClick={() => setActiveActivityType(2)} style={{ backgroundColor: "var(--fill-4)"}}>
                                <Phone size={20} color='var(--secondary-color)'/> {activeActivityType === 2 ? "Log a phone call" : ""}
                            </div>
                            <div className='activity-type' onClick={() => setActiveActivityType(3)} style={{ backgroundColor: "var(--card-color)", color: "var(--text-color)" }}>
                                <Printer size={20} color='var(--text-color)'/> {activeActivityType === 3 ? "Send a fax" : ""}
                            </div>
                            <div className='activity-type' onClick={() => setActiveActivityType(4)} style={{ backgroundColor: "var(--secondary-color)", color: "var(--text-color)" }}>
                                <MessageSquare size={20} color='var(--text-color)'/> {activeActivityType === 4 ? "Send SMS" : ""}
                            </div>
                            <div className='activity-type' onClick={() => setActiveActivityType(5)} style={{ backgroundColor: "var(--background-color)", color: "var(--text-color)" }}>
                                <FileSignature size={20} color='var(--text-color)'/> {activeActivityType === 5 ? "Request eSignature" : ""}
                            </div>
                        </div>
                    )}
                    <div className='form-group activity' style={{ width: '100%', maxWidth: '100%' }}>
                        <label className='activity-type-label subtext'>
                            Subject{activeActivityType !== 3 && activeActivityType !== 4 && activeActivityType !== 5 && <span className='required'>*</span>}
                        </label>
                        <input
                            type='text'
                            onClick={onClick}
                            value={activityData.subject}
                            placeholder={activeActivityType === 3 ? "Optional - will auto-generate if empty" : 
                                        activeActivityType === 4 ? "Optional - SMS subject for activity feed" :
                                        activeActivityType === 5 ? "Optional - eSignature request title" :
                                        "Add a subject"}
                            onChange={(e) => handleInputChange("subject")(e.target.value)}
                            disabled={subjectLocked}
                            style={subjectLocked ? { backgroundColor: 'var(--border-color)', cursor: 'not-allowed' } : {}}
                        />
                    </div>
                    <div className='form-group activity' style={{ width: '100%', maxWidth: '100%' }}>
                        <label className='activity-type-label subtext'>
                            {activeActivityType === 4 ? "Message" : 
                             activeActivityType === 5 ? "Description/Instructions" : 
                             "Content"}{activeActivityType !== 3 && activeActivityType !== 5 && <span className='required'>*</span>}
                        </label>
                        <MentionsTextarea
                            value={activityData.content}
                            onChange={handleInputChange("content")}
                            users={users}
                            onMentionsChange={handleMentionsChange}
                            disabled={locked}
                        />
                    </div>
                    {showTags && (
                        <div className='form-group activity'>
                            <label className='activity-type-label subtext'>Tags - Separate multiple tags with commas</label>
                            <input
                                type='text'
                                placeholder="Enter tags separated by commas (e.g., urgent, follow-up, client)"
                                value={activityData.tags}
                                onChange={(e) => handleTagsChange(e.target.value)}
                            />
                        </div>
                    )}
                    {attachFile && <MultiFile
                        value={activityData.attachments}
                        onChange={(val) => handleInputChange("attachments")(val)}
                        lead_id={case_id}
                        sectionName="Activity Feed"
                    />}
                    {activeActivityType === 1 && (
                        <div className="activity-task-info">
                            <div className='form-group task-assign activity share'>
                                <label className='activity-type-label subtext'>Assign to<span className='required'>*</span></label>
                                <SearchSelect
                                    value={null}
                                    onChange={(val) => {
                                        const userId = Number(val);
                                        if (!Number.isInteger(userId) || userId <= 0) return;
                                        if (!validActivityAssignees.includes(userId)) {
                                            const newAssignees = [...validActivityAssignees, userId];
                                            handleInputChange('assignees')(newAssignees);
                                            // Set task to first assignee for backward compatibility
                                            if (!activityData.task) {
                                                handleInputChange('task')(userId);
                                            }
                                        }
                                    }}
                                    options={userMap}
                                    placeholder={validActivityAssignees.length > 0 ? "Add another assignee..." : "Select assignees..."}
                                />
                                {validActivityAssignees.length > 0 && (
                                    <div className='activity-selected-users' style={{ marginTop: '2px', gap: '5px', flexWrap: 'wrap' }}>
                                        {validActivityAssignees.map((assigneeId) => (
                                            <div className="assignees-list" key={assigneeId} style={{ backgroundColor: 'var(--background-color)', padding: '2px 4px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                                                <span>{userMap[assigneeId]}</span>
                                                <X
                                                    size={14}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const newAssignees = validActivityAssignees.filter(id => id !== assigneeId);
                                                        handleInputChange('assignees')(newAssignees);
                                                        // Update task to first assignee or null
                                                        handleInputChange('task')(newAssignees.length > 0 ? newAssignees[0] : null);
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className='form-group task-assign activity share'>
                                <label className='activity-type-label subtext'>Due date</label>
                                <DateInput
                                    value={activityData.due_date}
                                    onChange={(val) => handleInputChange("due_date")(val)}
                                />
                            </div>
                            {postDate && <div className='form-group task-assign activity share'>
                                <label className='activity-type-label subtext'>Start date</label>
                                <DateInput
                                    value={activityData.post_date}
                                    onChange={(val) => handleInputChange("post_date")(val)}
                                />
                            </div>}
                        </div>
                    )}
                    {activeActivityType === 2 && (
                        <div className='activity-call-info'>
                            <div className='form-group call-log activity'>
                                <label className="activity-type-label subtext">Date</label>
                                <DateInput
                                    value={activityData.date}
                                    onChange={(val) => handleInputChange("date")(val)}
                                />
                            </div>
                            <div className='form-group call-log activity'>
                                <label className="activity-type-label subtext">Start Time</label>
                                <TimeInput
                                    value={activityData.start_time}
                                    onChange={(val) => handleInputChange("start_time")(val)}
                                />
                            </div>
                            <div className='form-group call-log activity'>
                                <label className="activity-type-label subtext">End Time</label>
                                <TimeInput
                                    value={activityData.end_time}
                                    onChange={(val) => handleInputChange("end_time")(val)}
                                />
                            </div>
                            <div className='form-group activity half'>
                                <label className="activity-type-label subtext">Contact</label>
                                <Contact
                                    selectedContact={activityData.contact.id}
                                    setSelectedContact={(contact) => handleInputChange("contact")(contact)}
                                    onCreateNewContact={onRequestCreateContact}
                                />
                            </div>
                        </div>
                    )}
                    {activeActivityType === 3 && (
                        <div className='activity-fax-info'>
                            <div className='form-group fax-send activity'>
                                <label className="activity-type-label subtext">Fax Number<span className='required'>*</span></label>
                                <input
                                    type="tel"
                                    placeholder="7132213838 or +447132213838"
                                    value={activityData.fax_number}
                                    onChange={(e) => handleInputChange("fax_number")(e.target.value)}
                                />
                            </div>
                            <div className='form-group fax-send activity'>
                                <label className="activity-type-label subtext">File to Fax - Supported formats: PDF, TIFF, TXT, DOC, DOCX, JPG, PNG<span className='required'>*</span></label>
                                <DocumentSearch
                                    value={activityData.fax_file}
                                    onChange={(file) => handleInputChange("fax_file")(file)}
                                    case_id={case_id}
                                    lead_id={lead_id}
                                    caseName="Case"
                                    sectionName="Faxes"
                                />
                            </div>
                        </div>
                    )}
                    {activeActivityType === 4 && (
                        <div className='activity-sms-info' style={{ width: '100%'}}>
                            <div className='form-group half sms-send activity'>
                                <label className="activity-type-label subtext">Phone Number<span className='required'>*</span></label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="tel"
                                        style={{ width: '100%' }}
                                        placeholder="7132213838 or +17132213838"
                                        value={activityData.sms_number}
                                        onChange={(e) => handleInputChange("sms_number")(e.target.value)}
                                        onFocus={() => setShowSMSContacts(true)}
                                    />
                                    {/*showSMSContacts && contacts && contacts.length > 0 && (
                                        <div className="sms-contacts-dropdown" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            background: 'var(--card-color)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            zIndex: 1000,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}>
                                            <div style={{
                                                padding: '8px 12px',
                                                borderBottom: '1px solid var(--border-color)',
                                                fontSize: '12px',
                                                color: 'var(--subtext-color)',
                                                fontWeight: 'bold'
                                            }}>
                                                Recommended Contacts:
                                            </div>
                                            {contacts.filter(contact => contact.phone).map(contact => (
                                                <div 
                                                    key={contact.id}
                                                    onClick={() => {
                                                        handleInputChange("sms_number")(contact.phone);
                                                        setShowSMSContacts(false);
                                                    }}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid var(--border-color)',
                                                        fontSize: '14px'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--fill-2)'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                >
                                                    <div style={{ fontWeight: 'bold' }}>{contact.name}</div>
                                                    <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{contact.phone}</div>
                                                </div>
                                            ))}
                                            <div 
                                                onClick={() => setShowSMSContacts(false)}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'center',
                                                    color: 'var(--text-muted)'
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--fill-2)'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                            >
                                                Close
                                            </div>
                                        </div>
                                    )*/}
                                </div>
                            </div>
                        </div>
                    )}
                    {activeActivityType === 5 && (
                        <div className='activity-esignature-info'>
                            <div className='form-group esignature-send activity'>
                                <label className="activity-type-label subtext">Document to Sign - Supported formats: PDF, DOC, DOCX<span className='required'>*</span></label>
                                <FileUpload
                                    value={activityData.esignature_document}
                                    onChange={(file) => handleInputChange("esignature_document")(file)}
                                />
                            </div>
                            <div className='form-group esignature-send activity'>
                                <label className="activity-type-label subtext">Signers - Add people who need to sign this document<span className='required'>*</span></label>
                                
                                <div className="signer-add-form" style={{
                                    background: 'var(--background-color)', 
                                    padding: '12px', 
                                    borderRadius: '6px', 
                                    marginBottom: '8px',
                                    border: '1px solid var(--border-color)'
                                }}>
                                    <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                                        <input
                                            type="text"
                                            placeholder="Signer name"
                                            value={activityData.tempSignerName || ''}
                                            onChange={(e) => handleInputChange("tempSignerName")(e.target.value)}
                                            style={{flex: 1}}
                                        />
                                        <input
                                            type="email"
                                            placeholder="Email address"
                                            value={activityData.tempSignerEmail || ''}
                                            onChange={(e) => handleInputChange("tempSignerEmail")(e.target.value)}
                                            style={{flex: 1}}
                                        />
                                    </div>
                                    <div style={{display: 'flex', gap: '8px'}}>
                                        <input
                                            type="tel"
                                            placeholder="Phone number (optional)"
                                            value={activityData.tempSignerPhone || ''}
                                            onChange={(e) => handleInputChange("tempSignerPhone")(e.target.value)}
                                            style={{flex: 1}}
                                        />
                                        <button 
                                            type="button"
                                            className="action alt"
                                            style={{padding: '8px 16px', fontSize: '14px'}}
                                            onClick={() => {                                                
                                                if (!activityData.tempSignerName || !activityData.tempSignerEmail) {
                                                    alert('Please enter both name and email for the signer');
                                                    return;
                                                }
                                                
                                                const newSigner = {
                                                    name: activityData.tempSignerName,
                                                    email: activityData.tempSignerEmail,
                                                    phone: activityData.tempSignerPhone || ''
                                                };
                                                                                                
                                                const currentSigners = activityData.esignature_signers || [];
                                                const updatedSigners = [...currentSigners, newSigner];
                                                                                                
                                                handleInputChange("esignature_signers")(updatedSigners);
                                                handleInputChange("tempSignerName")('');
                                                handleInputChange("tempSignerEmail")('');
                                                handleInputChange("tempSignerPhone")('');
                                            }}
                                        >
                                            Add Signer
                                        </button>
                                    </div>
                                </div>

                                {/* Display Added Signers */}
                                {activityData.esignature_signers?.length > 0 && (
                                    <div className="signers-list">
                                        <div className="subtext" style={{marginBottom: '2.5px'}}>
                                            Added Signers ({activityData.esignature_signers.length}):
                                        </div>
                                        {activityData.esignature_signers.map((signer, index) => (
                                            <div key={index} className="signer-item" style={{
                                                padding: '8px 12px',
                                                background: 'var(--card-color)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                marginBottom: '4px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }}>
                                                <div>
                                                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{signer.name}</div>
                                                    <div style={{fontSize: '12px', color: 'var(--text-muted)'}}>{signer.email}</div>
                                                    {signer.phone && (
                                                        <div style={{fontSize: '12px', color: 'var(--text-muted)'}}>{signer.phone}</div>
                                                    )}
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        const newSigners = activityData.esignature_signers.filter((_, i) => i !== index);
                                                        handleInputChange("esignature_signers")(newSigners);
                                                    }}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: 'var(--danger-color)',
                                                        cursor: 'pointer',
                                                        padding: '4px 0'
                                                    }}
                                                    title="Remove signer"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {(!activityData.esignature_signers || activityData.esignature_signers.length === 0) && (
                                    <div className="no-signers-message" style={{
                                        padding: '16px',
                                        textAlign: 'center',
                                        color: 'var(--text-muted)',
                                        fontSize: '14px',
                                        fontStyle: 'italic'
                                    }}>
                                        No signers added yet. Add at least one signer to continue.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {activeActivityType === 4 && smsPendingConfirmation && (
                        <div
                            className="small-text"
                            role="status"
                            style={{
                                margin: '0 16px 12px',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                backgroundColor: 'var(--background-color)'
                            }}
                        >
                            {SMS_CONFIRMATION_PENDING_MESSAGE}
                        </div>
                    )}
                    <div className='activity-actions'>
                        <div className='activity-additional'>
                            <div className='activity-additional-item subtext' onClick={() => setShowTags(!showTags)}>
                                <Hash size={14}/>
                                <span>{!showTags ? 'Add' : 'Remove'} tags</span>
                            </div>
                            {activeActivityType !== 3 && activeActivityType !== 5 && (
                                <div className='activity-additional-item subtext' onClick={() => setAttachFile(!attachFile)}>
                                    <Paperclip size={14}/>
                                    <span>{!attachFile ? 'Attach a' : 'Remove'} file</span>
                                </div>
                            )}
                            {activeActivityType === 1 && (
                                <div className='activity-additional-item subtext' onClick={() => setPostDate(!postDate)}>
                                    <CalendarClock size={14}/>
                                    <span>{!postDate ? 'Set a' : 'Remove'} start date</span>
                                </div>
                            )}
                            <div className='activity-additional-item subtext' onClick={() => setSyncItem(!syncItem)}>
                                <CloudCheck size={14}/>
                                <span>{syncItem ? 'Remove' : 'Sync'} this item</span>
                            </div>
                        </div>
                        <div className='activity-primary-actions'>
                            {activityData.settlementTriggerRoute && (
                                <button
                                    type='button'
                                    className='action quaternary settlement-skip-button'
                                    style={{ backgroundColor: 'var(--background-color)' }}
                                    onClick={handleSettlementSkip}
                                    disabled={isSubmitting}
                                >
                                    Set Settlement Without Note
                                </button>
                            )}
                            {activityData.disengagementTriggerRoute && (
                                <button
                                    type='button'
                                    className='action quaternary settlement-skip-button'
                                    style={{ backgroundColor: 'var(--background-color)' }}
                                    onClick={handleDisengagementSkip}
                                    disabled={isSubmitting}
                                >
                                    Send Default Entry
                                </button>
                            )}
                            <div className={`activity-add${isSubmitting || locked || smsPendingConfirmation ? ' disabled' : ''}`} onClick={isSubmitting || locked || smsPendingConfirmation ? undefined : handleSubmit}>
                                <span className='activity-add-text'>
                                    {isSubmitting ? "Submitting..." : 
                                     smsPendingConfirmation ? "Checking delivery..." :
                                     locked ? "Select a case or lead first" :
                                     activeActivityType === 0 ? "Add Note" : 
                                     activeActivityType === 1 ? "Create Task" : 
                                     activeActivityType === 2 ? "Log Call" : 
                                     activeActivityType === 3 ? "Send Fax" : 
                                     activeActivityType === 4 ? "Send SMS" : 
                                     "Create Signature Request"}
                                </span>
                                <SendHorizonal size={20}/>
                            </div>
                        </div>
                    </div>
                    <X size={20} className='exit' onClick={handleComposerCancel} />
                </div>
            )}
        </>
    );
}

export const Notification = ({ count, type }) => {
    return (
        <>
            {count > 0 && <div className={`notification ${type}`}>
                {count}
            </div>}
        </>
    )
};

export const File = ({ file, onClick, menuOptions = [] }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const containerRef = useRef(null);
    const hasMenu = Array.isArray(menuOptions) && menuOptions.length > 0;

    useEffect(() => {
        if (!menuOpen) return;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen]);

    const handleDefaultClick = (e) => {
        if (menuOpen) {
            setMenuOpen(false);
        }
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <div
            ref={containerRef}
            className={`file small-text ${menuOpen ? 'opened' : ''}`}
            onClick={handleDefaultClick}
            title={file?.name}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '6px',
                position: 'relative'
            }}
        >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{file?.name}</span>
            {hasMenu && (
                <button
                    type='button'
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(prev => !prev);
                    }}
                    className='file-actions-button'
                >
                    <ChevronDown size={14} />
                </button>
            )}
            {hasMenu && menuOpen && (
                <div className='file-actions-menu-dropdown'>
                    {menuOptions.map((option, idx) => (
                        <div
                            key={idx}
                            type='button'
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpen(false);
                                option?.onClick?.(e);
                            }}
                            disabled={option?.disabled}
                            className={`file-actions-menu-dropdown-item${option?.disabled ? ' disabled' : ''}`}
                        >
                            {option?.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const MiniNav = ({ options = [], selectedOption, setSelectedOption, notifications = [], icons = [] }) => {
    return (
        <>
            <div className='mini-nav' style={{ height: `${options.length * 32 + 2}px` }}>
                {options.map((option, index) => {
                    const IconComponent = icons[index];
                    return (
                        <div 
                            key={index} 
                            className={`option${selectedOption === index ? ' active' : ''}`} 
                            onClick={() => setSelectedOption(index)}
                        >
                            {IconComponent && <IconComponent size={16} style={{ marginRight: '6px' }} />}
                            {option}
                            {notifications[index] > 0 && <div className="pulsing-wrapper"><Dot className='pulsing' /></div>}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export const BankAccountLink = ({ value, onChange }) => {
    return (
        <div className='bank-account'>
        <Instructions
            instructions={
                <>
                    No accounts available.
                    <br />
                    <br />
                    Request feature completion by contacting:{' '}
                    <a href="mailto:tnolet@dalyblack.com">tnolet@dalyblack.com</a>
                </>
            }
        />
        </div>
    )
};

export const MentionsTextarea = ({ value, onChange, users = [], onMentionsChange, disabled = false}) => {
    const [mentionsList, setMentionsList] = useState([]);
    const [showMentions, setShowMentions] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [mentionStartPos, setMentionStartPos] = useState(null);
    const [mentions, setMentions] = useState(new Set());
    const [prevContent, setPrevContent] = useState('');
    const textareaRef = useRef(null);
    const mentionsRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (mentionsRef.current && !mentionsRef.current.contains(e.target)) {
                setShowMentions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        const currentPosition = e.target.selectionStart;
        setCursorPosition(currentPosition);
        
        // Check for removed mentions by comparing with previous content
        if (prevContent !== newValue) {
            const currentMentions = new Set(mentions);
            const remainingMentions = new Set();
            let mentionsChanged = false;
            
            // First, check if any @username patterns exist in the current text
            const mentionRegex = /@([a-zA-Z0-9_]+)\b/g;
            let match;
            
            // Find all current mentions in the new text
            while ((match = mentionRegex.exec(newValue)) !== null) {
                const username = match[1];
                const mentionedUser = users?.find(u => u?.user === username);
                
                if (mentionedUser) {
                    remainingMentions.add(mentionedUser.id);
                }
            }
            
            // If the size of the Sets differ, mentions have been added or removed
            if (currentMentions.size !== remainingMentions.size) {
                mentionsChanged = true;
            } else {
                // Check if the content of the Sets differ
                for (const id of currentMentions) {
                    if (!remainingMentions.has(id)) {
                        mentionsChanged = true;
                        break;
                    }
                }
            }
            
            // Update mentions if they've changed
            if (mentionsChanged) {
                setMentions(remainingMentions);
                onMentionsChange([...remainingMentions]);
            }
            
            // Update prev content for next comparison
            setPrevContent(newValue);
        }
        
        onChange(newValue);

        // Check if we're in a potential mention context
        const textBeforeCursor = newValue.substring(0, currentPosition);
        const atSymbolIndex = textBeforeCursor.lastIndexOf('@');
        
        if (atSymbolIndex >= 0 && (atSymbolIndex === 0 || /\s/.test(textBeforeCursor.charAt(atSymbolIndex - 1)))) {
            const query = textBeforeCursor.substring(atSymbolIndex + 1);
            setMentionStartPos(atSymbolIndex);
            
            // Filter users based on query
            const filteredUsers = users
                .filter(user => user.user.toLowerCase().includes(query.toLowerCase()))
                .sort((a, b) => a.user.localeCompare(b.user));
            
            if (filteredUsers.length > 0) {
                setMentionsList(filteredUsers);
                setShowMentions(true);
                positionMentionsDropdown();
            } else {
                setShowMentions(false);
            }
        } else {
            setShowMentions(false);
        }
    };

    // Track initial content on mount
    useEffect(() => {
        setPrevContent(value || '');
    }, []);

    const positionMentionsDropdown = () => {
        if (!textareaRef.current) return;
        
        const textareaRect = textareaRef.current.getBoundingClientRect();
        const lineHeight = parseInt(getComputedStyle(textareaRef.current).lineHeight);
        const textBeforeCursor = textareaRef.current.value.substring(0, cursorPosition);
        const lines = textBeforeCursor.split('\n');
        const currentLineIndex = lines.length - 1;
        
        const top = textareaRect.top + (currentLineIndex * lineHeight) + lineHeight + window.scrollY;
        const left = 0;
        
        setDropdownPosition({ top, left });
    };

    const handleMentionClick = (user) => {
        if (!textareaRef.current) return;

        const currentValue = textareaRef.current.value || value || '';
        const currentCursor = textareaRef.current.selectionStart ?? cursorPosition;
        const atIndex = mentionStartPos !== null
            ? mentionStartPos
            : currentValue.lastIndexOf('@', Math.max(currentCursor - 1, 0));

        if (atIndex < 0) return;

        const beforeMention = currentValue.substring(0, atIndex);
        const mentionTail = currentValue.substring(atIndex + 1);
        const tailMatch = mentionTail.match(/^([a-zA-Z0-9_]+)/);
        const tailLength = tailMatch ? tailMatch[1].length : 0;
        const replaceEnd = atIndex + 1 + tailLength;
        const afterMention = currentValue.substring(replaceEnd);
        const newValue = `${beforeMention}@${user.user} ${afterMention}`;

        onChange(newValue);
        setPrevContent(newValue);
        setShowMentions(false);
        setMentionStartPos(null);


        // Add user ID to mentions set
        const newMentions = new Set(mentions);
        newMentions.add(user.id);
        setMentions(newMentions);
        onMentionsChange([...newMentions]);

        // Reset cursor position
        setTimeout(() => {
            const newPosition = beforeMention.length + user.user.length + 2; // +2 for @ and space
            textareaRef.current.selectionStart = newPosition;
            textareaRef.current.selectionEnd = newPosition;
            textareaRef.current.focus();
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (showMentions && mentionsList.length > 0) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape' || e.key === 'Tab') {
                e.preventDefault();
                
                if (e.key === 'Escape') {
                    setShowMentions(false);
                } else if (e.key === 'Enter' || e.key === 'Tab') {
                    handleMentionClick(mentionsList[0]);
                }
            }
        }
    };

    // Make sure to handle the case where the content is completely cleared
    useEffect(() => {
        // If the textarea is emptied, clear all mentions
        if (value === '' && mentions.size > 0) {
            setMentions(new Set());
            onMentionsChange([]);
        }
    }, [value, mentions, onMentionsChange]);

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Add a note"
                style={{ width: '100%', height: '100%' }}
                disabled={disabled}
            />
            {showMentions && mentionsList.length > 0 && (
                <div 
                    ref={mentionsRef}
                    style={{
                        position: 'absolute',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        backgroundColor: 'var(--background-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        maxWidth: '400px'
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    {mentionsList.map(user => (
                        <div 
                            key={user.id}
                            className="mention-item small-text"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleMentionClick(user);
                            }}
                        >
                            @{user.user} <span className='subtext'>({user.name})</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const DataBox = ({ label, route, values, onClick }) => {
    const navigate = useNavigate();

    return (
        <div className='databox-count'>
            <span className='databox-label' onClick={() => navigate(`/${route}`)}>{label}</span>
            <div className='databox-data'>
                {values?.map((value, index) => (
                    <div className='databox-value' key={index} onClick={() => onClick && onClick(value)}>
                        <div className='databox-value-label small-text'>{value.label}</div>
                        <div className={`databox-value-data ${value.route ? 'clickable' : ''}`} onClick={() => navigate(`/${value.route}`)}>{value.data}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export const SMSCreation = ({ case_id, lead_id, contacts = [], onClose, onSent }) => {
    
    // Helper function to handle API responses and session expiration
    const handleApiResponse = async (response) => {
        if (response.status === 401) {
            const result = await response.json();
            if (result.code === 'SESSION_EXPIRED') {
                alert('Your session has expired. The page will refresh to log you back in.');
                window.location.reload();
                return null;
            }
        }
        return response;
    };

    const [smsData, setSmsData] = useState({
        phone_number: '',
        message: '',
        subject: '',
        tags: '[]',
        mentions: '[]',
        attachments: []
    });
    const [sending, setSending] = useState(false);
    const [smsPendingConfirmation, setSmsPendingConfirmation] = useState(false);
    const [recommendedNumbers, setRecommendedNumbers] = useState([]);
    const smsSendLockRef = useRef(false);
    const smsRequestIdRef = useRef(null);

    useEffect(() => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
    }, [smsData.phone_number, smsData.message, smsData.subject, smsData.attachments, case_id, lead_id]);

    useEffect(() => {
        // Extract phone numbers from contacts
        if (contacts && contacts.length > 0) {
            const numbers = contacts
                .map(contact => {
                    const phoneDetail = contact?.details?.find(detail => detail?.type === 'phone');
                    if (phoneDetail) {
                        try {
                            const phoneData = JSON.parse(phoneDetail.value);
                            if (phoneData.number) {
                                const contactName = contact.is_company 
                                    ? contact.company_name 
                                    : `${contact.prefix || ''} ${contact.full_name} ${contact.suffix || ''}`.trim();
                                return {
                                    number: phoneData.number,
                                    name: contactName,
                                    role: contact.field || 'Contact'
                                };
                            }
                        } catch (e) {
                            console.error('Error parsing phone data:', e);
                        }
                    }
                    return null;
                })
                .filter(Boolean);
            setRecommendedNumbers(numbers);
        }
    }, [contacts]);

    const handleSend = async () => {
        if (smsSendLockRef.current || smsPendingConfirmation) return;
        if (!smsData.phone_number || !smsData.message.trim()) {
            alert('Please enter both phone number and message');
            return;
        }

        smsSendLockRef.current = true;
        setSending(true);
        try {
            const formData = new FormData();
            formData.append('phone_number', smsData.phone_number);
            formData.append('message', smsData.message);
            const requestId = smsRequestIdRef.current || createCommunicationRequestId();
            smsRequestIdRef.current = requestId;
            formData.append('request_id', requestId);
            
            if (case_id) formData.append('case_id', case_id);
            if (lead_id) formData.append('lead_id', lead_id);
            if (smsData.subject) formData.append('subject', smsData.subject);
            
            // Convert attachments to base64 for SMS (to avoid temp file issues)
            if (Array.isArray(smsData.attachments) && smsData.attachments.length > 0) {
                const fileDataArray = [];
                const fileNameArray = [];
                
                for (const file of smsData.attachments) {
                    if (file && typeof file.name === "string" && typeof file.size === "number") {
                        try {
                            const base64Data = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const base64 = reader.result.split(',')[1]; // Remove data:...;base64, prefix
                                    resolve(base64);
                                };
                                reader.onerror = reject;
                                reader.readAsDataURL(file);
                            });
                            
                            fileDataArray.push(base64Data);
                            fileNameArray.push(file.name);
                        } catch (error) {
                            console.error('Error converting file to base64:', error);
                        }
                    }
                }
                
                if (fileDataArray.length > 0) {
                    fileDataArray.forEach((fileData, index) => {
                        formData.append(`file_data[${index}]`, fileData);
                        formData.append(`file_name[${index}]`, fileNameArray[index]);
                    });
                }
            }
            
            formData.append('tags', smsData.tags);
            formData.append('mentions', smsData.mentions);

            const response = await fetch('https://api.casedb.co/fax-fixed.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const handledResponse = await handleApiResponse(response);
            if (!handledResponse) return; // Session expired, page will refresh

            const result = await handledResponse.json();

            if (isSmsConfirmationPending(result)) {
                setSmsPendingConfirmation(true);
            } else if (result.success) {
                smsRequestIdRef.current = null;
                setSmsPendingConfirmation(false);
                if (onSent) onSent();
                onClose();
            } else {
                if (result?.retry_with_new_request_id) {
                    smsRequestIdRef.current = null;
                    setSmsPendingConfirmation(false);
                }
                alert(`SMS failed: ${result.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('SMS send error:', error);
            alert('Failed to send SMS. Please try again.');
        } finally {
            smsSendLockRef.current = false;
            setSending(false);
        }
    };

    return (
        <Modal
            title="Send SMS"
            instructions="Send a text message to a contact"
            onClose={onClose}
            footer={
                <div className='modal-footer-actions'>
                    <button 
                        onClick={handleSend} 
                        className="action" 
                        disabled={sending || smsPendingConfirmation || !smsData.phone_number || !smsData.message.trim()}
                    >
                        {sending ? 'Sending...' : smsPendingConfirmation ? 'Checking delivery...' : 'Send SMS'}
                    </button>
                    <button onClick={onClose} className="action alt">Cancel</button>
                </div>
            }
        >
            <div className='modal-content-wrapper'>
                {smsPendingConfirmation && (
                    <div
                        className="small-text"
                        role="status"
                        style={{
                            padding: '10px 12px',
                            borderRadius: '6px',
                            backgroundColor: 'var(--background-color)'
                        }}
                    >
                        {SMS_CONFIRMATION_PENDING_MESSAGE}
                    </div>
                )}
                <div className='form-group full'>
                    <label className="subtext">Phone Number</label>
                    <input
                        type="tel"
                        value={smsData.phone_number}
                        onChange={(e) => setSmsData(prev => ({ ...prev, phone_number: e.target.value }))}
                        placeholder="Enter phone number"
                    />
                    
                    {recommendedNumbers.length > 0 && (
                        <div className="recommended-numbers">
                            <label className="subtext" style={{ marginTop: '10px', marginBottom: '5px' }}>
                                Recommended Recipients:
                            </label>
                            <div className="recommended-numbers-list">
                                {recommendedNumbers.map((rec, index) => (
                                    <div 
                                        key={index} 
                                        className="recommended-number-item"
                                        onClick={() => setSmsData(prev => ({ ...prev, phone_number: rec.number }))}
                                        style={{
                                            padding: '8px 12px',
                                            margin: '4px 0',
                                            backgroundColor: 'var(--secondary-color)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.3s'
                                        }}
                                    >
                                        <div style={{ fontWeight: '500' }}>{rec.name}</div>
                                        <div className="subtext">{rec.number} • {rec.role}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className='form-group full'>
                    <label className="subtext">Subject (Optional)</label>
                    <input
                        type="text"
                        value={smsData.subject}
                        onChange={(e) => setSmsData(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="SMS subject for activity feed"
                    />
                </div>

                <div className='form-group full'>
                    <label className="subtext">Message</label>
                    <textarea
                        value={smsData.message}
                        onChange={(e) => setSmsData(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Enter your message"
                        rows={4}
                        style={{ resize: 'vertical', minHeight: '100px' }}
                    />
                    <div className="character-count subtext" style={{ textAlign: 'right', marginTop: '4px' }}>
                        {smsData.message.length}/160 characters
                    </div>
                </div>

                <div className='form-group full'>
                    <label className="subtext">Attachments (Optional)</label>
                    <input
                        type="file"
                        multiple
                        onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setSmsData(prev => ({ ...prev, attachments: files }));
                        }}
                        accept="*/*"
                    />
                    {smsData.attachments && smsData.attachments.length > 0 && (
                        <div className="attachment-list" style={{ marginTop: '8px' }}>
                            {smsData.attachments.map((file, index) => (
                                <div key={index} className="attachment-item subtext">
                                    {file.name} ({Math.round(file.size / 1024)}KB)
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="subtext" style={{ fontSize: '12px', marginTop: '4px' }}>
                        Note: Small attachments (&lt;1MB) will be sent via MMS. Larger files will be saved with the SMS activity.
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export const DocumentSearch = ({ value, onChange, case_id, lead_id, caseName, sectionName = "", fieldId, sectionId = null }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const canUploadDocument = !!case_id;

    useEffect(() => {
        if (value) {
            if (typeof value === 'object') {
                const normalizedDoc = {
                    id: value.id || value.file_id || value.fileId || null,
                    name: value.name || value.file_name || value.filename || value.fileName || value.title || '',
                    file_path: value.file_path || value.path || null,
                    file_url: value.file_url || value.url || null,
                    size: value.size || value.file_size || null,
                    uploaded_date: value.uploaded_date || value.creation_date || value.updated_at || null
                };

                if (normalizedDoc.id || normalizedDoc.name) {
                    // Handle object format from backend (when loading existing data)
                    setSelectedDocument(normalizedDoc);
                    setSearchTerm(normalizedDoc.name);
                    return;
                }
            } else if (typeof value === 'string') {
                // Handle legacy string format (for backwards compatibility)
                setSelectedDocument({ id: value, name: value });
                setSearchTerm(value);
            } else if (typeof value === 'number') {
                // Handle numeric ID - don't clear existing selection if IDs match
                if (selectedDocument && selectedDocument.id == value) {
                    // Keep the existing selection and display - IDs match
                    return;
                } else {
                    // Clear selection for unrecognized numeric ID
                    setSelectedDocument(null);
                    setSearchTerm('');
                }
            }
        } else {
            // Clear selection when value is null/undefined
            setSelectedDocument(null);
            setSearchTerm('');
        }
    }, [value]);

    const searchDocuments = async (term) => {
        if (!term.trim() || (!case_id && !lead_id)) {
            setDocuments([]);
            return;
        }
        
        setLoading(true);
        try {
            const params = new URLSearchParams({ search: term });
            if (case_id) params.set('case_id', case_id);
            if (lead_id) params.set('lead_id', lead_id);
            const response = await fetch(`https://api.casedb.co/document-search.php?${params.toString()}`);
            const data = await response.json();
            
            if (data.success && data.files) {
                setDocuments(data.files);
            } else {
                setDocuments([]);
            }
        } catch (error) {
            console.error('Error searching documents:', error);
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm.trim().length > 2) {
                searchDocuments(searchTerm);
            } else {
                setDocuments([]);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, case_id]);

    const handleDocumentSelect = (doc) => {
        // Create a normalized document object for internal use
        const normalizedDoc = {
            id: doc.id || doc.file_id || null,
            name: doc.file_name || doc.name || 'Document',
            file_path: doc.file_path || doc.path || null,
            file_url: doc.file_url || doc.url || null,
            size: doc.file_size || doc.size || null,
            uploaded_date: doc.uploaded_date || doc.creation_date || doc.updated_at || null
        };
        setSelectedDocument(normalizedDoc);
        setSearchTerm(normalizedDoc.name);
        setIsFocused(false);
        // Pass the full object to maintain consistency - backend will extract ID when saving
        onChange(normalizedDoc);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (!value.trim()) {
            setSelectedDocument(null);
            onChange(null);
        }
    };

    const uploadDocumentFile = async (fileToUpload) => {
        if (!fileToUpload) return;

        setUploading(true);
        try {
            const base64Content = await readFileAsBase64(fileToUpload);
            const folderPath = `{{Name}}/${sectionName}`;

            const uploadData = new FormData();
            uploadData.append("case_id", case_id);
            uploadData.append("lead_id", lead_id);
            uploadData.append("folder_path", folderPath);
            uploadData.append("file_name", fileToUpload.name);
            uploadData.append("file_content", base64Content);
            uploadData.append("field_id", fieldId);
            if (sectionId) {
                uploadData.append("section_id", sectionId);
            }

            const response = await fetch("https://api.casedb.co/upload.php", {
                method: "POST",
                body: uploadData,
                credentials: "include"
            });

            const result = await response.json();
            console.log('DocumentSearch upload response:', result);

            if (result.success && result.uploaded_files && result.uploaded_files.length > 0) {
                // Get the file_id from the first uploaded file
                const uploadedFile = result.uploaded_files[0];
                const fileId = uploadedFile.file_id || uploadedFile.id;

                console.log('DocumentSearch extracted file_id:', fileId, 'type:', typeof fileId);

                // Ensure fileId is numeric
                const numericFileId = parseInt(fileId);
                if (isNaN(numericFileId)) {
                    console.error('DocumentSearch: file_id is not numeric:', fileId);
                    return;
                }

                const newDoc = {
                    id: numericFileId,
                    name: uploadedFile.file_name || fileToUpload.name,
                    file_path: uploadedFile.file_path || uploadedFile.path || null,
                    file_url: uploadedFile.file_url || uploadedFile.url || null,
                    size: uploadedFile.file_size || uploadedFile.size || fileToUpload.size || null,
                    uploaded_date: uploadedFile.uploaded_date || uploadedFile.creation_date || uploadedFile.updated_at || null
                };
                setSelectedDocument(newDoc);
                setSearchTerm(newDoc.name);
                onChange(newDoc);

                setUploadFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            } else {
                console.error('DocumentSearch upload failed:', result);
            }
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleFileUpload = async () => {
        if (!uploadFile) return;
        await uploadDocumentFile(uploadFile);
    };

    const handleFileUploadSelection = async (file) => {
        if (!file) {
            setSelectedDocument(null);
            setSearchTerm('');
            onChange(null);
            return;
        }
        await uploadDocumentFile(file);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    return (
        !selectedDocument ? (
            <div className="document-search-simple" ref={dropdownRef}>
                {!uploadFile && (
                    <>
                        <div className="search-input-container">
                            <input
                                type="text"
                                placeholder="Search documents or enter file name..."
                                value={searchTerm}
                                onChange={handleInputChange}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => {
                                    // Use setTimeout to allow document selection to work
                                    setTimeout(() => setIsFocused(false), 200);
                                }}
                                className="document-search-input"
                            />
                            
                            {canUploadDocument && (
                                <>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={(e) => setUploadFile(e.target.files[0])}
                                        style={{ display: 'none' }}
                                    />
                                    
                                    <button 
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="form-box alt"
                                        title="Upload new file"
                                    >
                                        <PlusIcon size={16} />
                                    </button>
                                </>
                            )}
                        </div>

                        {isFocused && documents.length > 0 && (
                            <div className="search-file-dropdown">
                                {loading && (
                                    <div className="dropdown-item loading">
                                        Searching...
                                    </div>
                                )}
                                {!loading && documents.map((doc) => (
                                    <div 
                                        key={doc.id}
                                        className="dropdown-item"
                                        onClick={() => handleDocumentSelect(doc)}
                                    >
                                        <div className="doc-name">{doc.file_name}</div>
                                        <div className="doc-path subtext">{doc.file_path ? String(doc.file_path.slice(Math.max(doc.file_path.indexOf('{{Name}}'), 0))).replaceAll('{{Name}}', caseName || 'Case') : ''}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {uploadFile && (
                    <div className="upload-preview">
                        <span className="upload-file-name"><FileIcon size={18} /> {uploadFile.name}</span>
                        <div className="upload-actions">
                            <button 
                                type="button"
                                onClick={handleFileUpload}
                                disabled={uploading}
                                className="upload-confirm-btn"
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </button>
                            <button 
                                type="button"
                                onClick={() => {
                                    setUploadFile(null);
                                    if (fileInputRef.current) {
                                        fileInputRef.current.value = "";
                                    }
                                }}
                                className="upload-cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <FileUpload value={selectedDocument} onChange={handleFileUploadSelection} />
        )
    );
};

// Import ESignatureManager for the quick access component
const ESignatureManager = React.lazy(() => import('./ESignatureManager'));

export const ESignatureQuickAccess = ({ case_id, lead_id, user_id, onNavigate }) => {
    const [showManager, setShowManager] = useState(false);

    const handleNavigate = () => {
        if (onNavigate) {
            onNavigate('eSignatures');
        } else {
            setShowManager(true);
        }
    };

    if (showManager) {
        return (
            <div className="esignature-overlay" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    background: 'var(--card-color)',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                }}>
                    <React.Suspense fallback={<div>Loading...</div>}>
                        <ESignatureManager 
                            case_id={case_id} 
                            lead_id={lead_id} 
                            user_id={user_id} 
                            onClose={() => setShowManager(false)}
                        />
                    </React.Suspense>
                </div>
            </div>
        );
    }

    return (
        <button 
            className="action secondary"
            onClick={handleNavigate}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                margin: '8px 0'
            }}
        >
            <FileSignature size={16} />
            <span>eSignature Requests</span>
        </button>
    );
};

export const VerifyData = ({ onClick, decline }) => {
    return (
        <div className='verify-data-wrap'>
            <div className='verify-confirm-hint'>
                <span className='verify-confirm-text'>Confirm here</span>
                <span className='verify-confirm-arrow'>↓</span>
            </div>
            <div className='verify-data'>
                {decline && <span className='verify-decline small-text' onClick={decline}><X size={16} /> Decline</span>}
                <span className={`verify-verify small-text${decline ? ' has-decline' : ''}`} onClick={onClick}><Check size={16} /> Confirm</span>
            </div>
        </div>
    );
};
